/**
 * The client: real Hafnarfjörður, streamed from the server, walked around.
 *
 * Click to look, WASD to move, F to toggle flying. The HUD says which street
 * you are on and how high you are, because the fastest way to check whether the
 * terrain is right is to stand somewhere you know and read the numbers.
 */

import { MeshLambertNodeMaterial, WebGPURenderer, type Node } from 'three/webgpu';
import {
  and,
  attribute,
  equal,
  float,
  greaterThan,
  int,
  mix,
  or,
  positionView,
  select,
  smoothstep,
  texture,
  uniform,
  uv,
  vec3,
} from 'three/tsl';
import {
  ACESFilmicToneMapping,
  AgXToneMapping,
  BoxGeometry,
  BufferAttribute,
  DoubleSide,
  BufferGeometry,
  Color,
  DirectionalLight,
  EdgesGeometry,
  Fog,
  HemisphereLight,
  LinearFilter,
  NearestFilter,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  NeutralToneMapping,
  NoToneMapping,
  PerspectiveCamera,
  Scene,
  Vector3,
  type DataArrayTexture,
} from 'three';
import {
  BLOCK_AIR,
  CHUNK_SIZE,
  CUTTABLE_FIELDS,
  LJAR_ITEM,
  MAX_REACH,
  blockById,
  blockIdForKey,
  blockHeight,
  stateIdForKey,
  chunkKey,
  WORLD_MAX_Y,
  WORLD_MIN_Y,
  chunkOrigin,
  formatWorldClock,
  formatWorldDate,
  isSolid,
  isTargetable,
  northingToZ,
  raycastVoxels,
  snapGauge,
  worstSurcharge,
  shelterAt,
  sheltersFromWeather,
  warmsFromWeather,
  underARoof,
  type ShelterRung,
  headBlock,
  describeSky,
  t,
  tAnyKey,
  worldBounds,
  worldToChunk,
  zToNorthing,
  type BlockChange,
  type ChunkCoord,
  type DecodedChunkFrame,
  sunPosition,
  moonPosition,
  type MoonPosition,
  type VoxelHit,
  MAX_LIGHT,
  findEmitters,
  type Emitter,
  type WorldPosition,
  TORCH_KEY,
  GATE_BLOCK,
  feedGrade,
  HOUSES,
  snapToNeighbour,
  type BurFacing,
  STATION_ALIAS,
  stationFor,
  burDoorFor,
  isPitBlock,
  PIT_DOOR,
  isFluid,
  burFacingToward,
  isBur,
  isRumPart,
  isLoomPart,
  rumHeadFor,
  LOOM_BLOCK,
  loomAnchorFor,
  loomPlotFor,
  rumCentre,
  burFrame,
  RUM_BLOCK,
  isOperated,
  torchFacingFrom,
  torchWallInset,
  stateKey,
  type WeatherSnapshot,
  type WorldTime,
  RECIPES,
  BLOCK,
  BLOCKS,
  blockAppearance,
  isPickedByHand,
  SPEAR_ITEM,
  spearTargetFrom,
  KNIFE_ITEM,
  knifeTargetFrom,
  lookTargetFrom,
  LEAD_ITEM,
  LEAD_REACH,
  ROD_ITEM,
  carryTargetFrom,
  isCarriable,
  type CreaturesMessage,
} from '@lofi/shared';
import { meshChunk, type ChunkGeometry } from './mesher.ts';
import { noticeMs, rettirStep } from './notices.ts';
import { Shoal, fishDaylight } from './fishmesh.ts';
import { Flock, sheepDaylight } from './sheepmesh.ts';
import { Foxes } from './foxmesh.ts';
import { buildBlockTextures } from './textures.ts';
import { rendererInfo } from './gpuinfo.ts';
import {
  connect,
  flatRequested,
  forgetSignInAttempt,
  rememberSignInAttempt,
  serverUrl,
  signInAttempted,
  storedGameSession,
  takePlayToken,
  withFlatParam,
  type Connection,
} from './net.ts';
import { Controls } from './controls.ts';
import { Breaking } from './breaking.ts';
import { crackGeometry, crackVertexCount } from './cracks.ts';
import { Hand } from './hand.ts';
import { preloadHeldPhotos } from './helditem.ts';
import type { SlotContent } from './hud-parts.ts';
import {
  DevTools,
  insideWorld,
  type GraphicsSettings,
  type LightSettings,
  type SearchHit,
  type SearchKind,
} from './devtools.ts';
import { GameMenu } from './gamemenu.ts';
import {
  DEFAULT_GRAPHICS,
  WATER_OPACITY_MAX,
  WATER_OPACITY_MIN,
  graphicsStore,
  lightStore,
  menuStore,
  viewRadiusStore,
} from './settings.ts';
import { Hud, type NearbyRoad } from './hud.ts';
import type { AimTone } from './hud-scene.ts';
import { animalAimLine } from './aimline.ts';
import { PackPanel } from './pack.ts';
import { HearthPanel } from './hearth-panel.ts';
import { ChestPanel } from './chest-panel.ts';
import { BUG_SHOT_TYPE, itemIconUri, signInAction } from '@lofi/shared';
import { TopBar, TOP_BAR_HEIGHT } from './topbar.ts';
import { ClientWorldClock } from './worldclock.ts';
import { ClientVitals } from './clientvitals.ts';
import { ClientWeather } from './clientweather.ts';
import { skyLook, DEFAULT_SKY_WEATHER, type SkyLook, type SkyWeather } from './sky.ts';
import { SkyObjects } from './skyobjects.ts';
import { isTypingTarget } from './keys.ts';
import {
  itemById,
  itemIdForKey,
  edibleFor,
  isWornOut,
  toolOf,
  TUNING,
  type EdibleDef,
  type Held,
  type Stack,
} from '@lofi/shared';
import { chooseLanguage } from './language.ts';
import { OPERATED_BLOCKS, interactionFor } from './interact.ts';
import { PROTOCOL_VERSION } from '@lofi/shared';
import { OtherPlayers } from './players.ts';
import { BugBox, watchErrors } from './bugbox.ts';
import { GiveBox } from './givebox.ts';

/**
 * 🪵 **Every block you take with an EMPTY HAND.**
 *
 * 🔴 **Built from the DATA, not written out here** — which is the whole point.
 * The berry bush comes from `tuning.json` as it always did, and everything else
 * comes from the `pickedByHand` flag in `block-behaviour.json`.
 *
 * ⚠️ **The hand-written version of this is what broke driftwood.** `interact.ts`
 * had warned that the day a second such block existed the list would be
 * forgotten, and it was: the server knew how to pick driftwood up and **the
 * client never asked**, so the click did nothing at all. 🎯 haugur, minutes
 * after the first heap landed: *"i left click it with empty hand nothing
 * happens."*
 */
const HARVESTABLE: ReadonlySet<string> = new Set(
  [
    TUNING.berries.pickedFromBlock,
    ...BLOCKS.filter((b) => isPickedByHand(b.id)).map((b) => b.key),
  ].filter((k): k is string => k !== undefined),
);
/** 🌾 What a hand that is not holding a ljár cuts — nothing (D131). */
const NOTHING_CUT: ReadonlySet<string> = new Set();
import { slotContentFor } from './hud-cluster.ts';
import {
  HEARTH_FLAME_BASE,
  HEARTH_FLAME_HEIGHT,
  HEARTH_FLAME_WIDTH,
  buildFlameMaterial,
  buildFlameTexture,
  buildFlames,
  flameFrame,
  type FlameSpec,
} from './flame.ts';
import { Floats, type FloatView } from './floats.ts';
import { Ropes, type RopeEnds } from './ropes.ts';
import { buildTorchMaterial, buildTorchMesh, pegsIn, torchFlames, torchesIn } from './torchmesh.ts';
import { gatesIn } from './gatemesh.ts';
import { FlockWarning } from './flockwarn.ts';
import { buildDriftMaterial, buildDriftMesh, driftwoodIn } from './driftmesh.ts';
import { BurModel, buildBurGroup, burDaylight, storehousesIn, type BurGroup } from './burmesh.ts';
import { RumModel, bedsIn, buildRumGroup } from './rummesh.ts';
import { LoomModels, buildLoomGroup, loomsIn, stonesIn } from './loommesh.ts';
import { pitBreakCache, pitMenuTarget } from './pitaim.ts';
import {
  PitHouseModels,
  buildPitHouseGroup,
  disposePitHouseGroup,
  pitHousesIn,
} from './pithousemesh.ts';
import { BurShadow } from './burshadow.ts';
import {
  buildSmokeMaterial,
  buildSmokeMesh,
  buildSmokeTexture,
  primeSmoke,
  smokeTime,
  type SmokeSource,
} from './smoke.ts';
import {
  buildRainMaterial,
  buildRainMesh,
  buildRainTexture,
  rainStrength,
  rainTime,
} from './rain.ts';
import { flameFrameAt, rippleFrameAt, rippleLayerBase, rippleStillLayers } from '@lofi/shared';
import {
  CY_ABOVE,
  CY_BELOW,
  chunkIntersects,
  chunksWanted,
  clampViewRadius,
  shouldUnload,
  verticalSpan,
} from './streaming.ts';
/* 🌊 T61 — whether the eye is under water, and what that looks like. */
import {
  DRY,
  UNDERWATER_FOG_FAR,
  UNDERWATER_FOG_NEAR,
  fogColourAt,
  lightScaleAt,
  submersionAt,
  type Submersion,
} from './underwater.ts';
import { ChunkWaits, FrameTimes } from './perf.ts';
import { stamped } from './assets.ts';

/**
 * 🔴 **Before anything draws.** Every sentence below this line comes out of the
 * language file, so the language has to be chosen before the first one is asked
 * for (T124). There is only English today, so today it always chooses English —
 * which is the point: the path runs every time, rather than being written now
 * and exercised for the first time in a year.
 */
chooseLanguage();

/**
 * 🔴 **The vertical band of chunks lives in `streaming.ts` now.**
 *
 * ⚠️ **It was two constants here, and nothing could test them.** T199's own
 * lesson was that the arithmetic belongs in `streaming.ts` — and the band's
 * numbers stayed behind in `main.ts`, so **`verticalSpan` was tested against
 * literals rather than against what the game actually asks for.** Changing them
 * would have failed no test at all.
 *
 * 🎯 **Found when haugur flew over Gullfoss and saw sky through the riverbed.**
 * See `CY_BELOW` there for the whole story.
 */

/** The lowest and highest chunk layers the world itself has. */
const CY_WORLD_MIN = Math.floor(WORLD_MIN_Y / CHUNK_SIZE);
const CY_WORLD_MAX = Math.floor(WORLD_MAX_Y / CHUNK_SIZE);

/**
 * ⚠️ Render distance is adjustable at runtime, deliberately.
 *
 * The brief's performance targets (§8.7) were set against an RTX 3080 that
 * turned out to be an RTX 3050 Ti, and have never been measured on the real
 * machine. Rather than bake in a guessed constant, let it be moved with `-` and
 * `=` and watch the FPS readout. The answer then comes from the hardware
 * instead of from arithmetic.
 */
let viewRadius = viewRadiusStore.get();

/** Chunks asked for but not yet arrived. Caps how much the server is given. */
const MAX_IN_FLIGHT = 64;
/** The server caps a request at 64 chunks; well under it leaves room to move. */
const REQUEST_BATCH = 16;

const hud = new Hud();

/**
 * 🎒 **The pack, and the E key that opens it** (T152).
 *
 * 🔴 **Built here rather than inside `Hud`, because it is not a readout.** The
 * HUD is a thing you glance at while the world moves; the pack is a thing you
 * stop and look at — it takes the mouse, it stops the keyboard driving the
 * player, and it is the one screen in this game you can change something with.
 *
 * ⚠️ **The move is only ever asked for.** Nothing here moves an item; the
 * server answers with the whole inventory and the panel redraws from that, which
 * is the same single source of truth the number keys have used since protocol 9.
 */
const pack = new PackPanel(
  (from, to, count) => {
    connection?.sendMoveItem(from, to, count);
  },
  /**
   * 🗑️ Throwing something away (T154).
   *
   * 🔴 **Said out loud, because it cannot be undone.** Everything else the pack
   * does is visible in the pack itself and reversible by doing it again; this
   * destroys something, and *"what did I just throw away"* is a question a
   * player will ask the moment after they stop being sure.
   *
   * ⚠️ **Announced when the request is SENT, not when it is confirmed**, which
   * is a small honest simplification: the server can refuse a discard, but only
   * from a client working off a pack it no longer has — and this client drew
   * that stack a moment ago. If it is ever refused, the pack redraws with the
   * stack still in it and the line was briefly wrong.
   */
  (from, count, itemKey) => {
    connection?.sendDiscardItem(from, count);
    const name = itemById(itemIdForKey(itemKey) ?? -1)?.nameIs ?? itemKey;
    say(t('pack.discarded', { count: String(count), item: name }));
  },
  /**
   * 🔨 Making something (T130 slice 3, D95).
   *
   * ⚠️ **Nothing is said here and nothing is drawn.** The server answers with a
   * new inventory, and the pack redraws from that — so what appears is what was
   * really made. A refusal comes back on `edit_rejected` with a reason, which
   * the readout already shows.
   */
  (recipe, at) => {
    connection?.sendCraft(recipe, at);
  },
  /* 🖱️ Whichever way this screen opened — `E`, or right-clicking a bench —
     the mouse comes back to the player. See {@link handMouseTo}. */
  () => {
    handMouseTo();
  },
  /* 🔨 And ask what the bench can see — T172. ⚠️ Called by the panel from
     `openAtStation`, so BOTH ways of walking up to a bench ask; a second way
     in that forgot what the first one does is a fault recorded four times. */
  () => {
    connection?.sendAskNearbyMaterials();
  },
);

/**
 * 🔥 **The hearth, open** — T128, D96, protocol 16.
 *
 * 🔴 **Its own screen, not a page of the pack**, because haugur ruled on it:
 * *"if we put all the menus as tabs on the same menu, there is no need for
 * players to walk to hearth or smíðabekkur."*
 *
 * ⚠️ **It opens when the SERVER says so**, never on the click. The click asks;
 * `hearth_contents` arriving is what opens it — so the screen cannot appear
 * empty, and cannot appear at all for a hearth the server refused.
 */
const hearthPanel = new HearthPanel(
  (at, from, to, count) => {
    /* 🔴 Both ends in your own pack is the PACK's move — see the note on the
       chest panel below, T247. The hearth refuses it for the same stated
       reason and with the same silence. */
    if (from.where === 'player' && to.where === 'player') {
      connection?.sendMoveItem(from.slot, to.slot, count);
      return;
    }
    connection?.sendMoveHearthItem(at, from, to, count);
  },
  /**
   * 🔥 The sentences under the fire's row — what it is doing, and 🏠 whether
   * it is your home.
   *
   * ⚠️ **Written here, not by the server** — protocol 8's rule: the server does
   * not know what language this player reads, so it sends `lit`, a number of
   * game hours and `home`, and this turns them into words.
   *
   * 🏠 **Three things a hearth can be to you, and each gets a sentence** —
   * D108's one gap, closed. Your home says so. A fire that is not yours says
   * how to make it yours, and the how depends on whether it burns: lighting
   * claims it, and so does feeding one already alight (`isBurning`, not
   * `kind === 'fed'`, on the server).
   */
  (lit, hoursLeft, home) => ({
    fire: lit ? t('hearth.burning', { hours: String(Math.max(1, Math.round(hoursLeft))) }) : null,
    home: home ? t('hearth.home') : lit ? t('hearth.notHomeLit') : t('hearth.notHomeOut'),
  }),
);

/**
 * 📦 **The chest, open** — T135, D106, protocol 20.
 *
 * ⚠️ **It opens when the SERVER says so**, never on the click — the hearth’s
 * rule and its reason: the click only asks, and `chest_contents` arriving is
 * what opens it. So the screen cannot appear empty while the real one is still
 * being read, and cannot appear at all for a chest the server refused.
 *
 * 🔴 **Far fewer arguments than the hearth’s**, and that is the shape of the
 * difference between the two containers rather than an oversight. A hearth
 * needs a fallback for what a slot holds and a sentence about the fire; **a
 * chest slot holds whatever the server says it holds and the chest says
 * nothing** — it does not burn, cook, or run down.
 */
const chestPanel = new ChestPanel(
  (at, from, to, count) => {
    /**
     * 🔴 **A drag with BOTH ends in the player's own pack is the PACK's move, and
     * sending it as a container move is what broke it** — T247, 2026-09-22.
     *
     * 🎯 **haugur, in play:** *"could put it on hand but not into my own
     * invetory"*, then *"cant move coocked fish from on hand to personal
     * either"*. ⚠️ **It was read as a fish problem for half an hour and it is not
     * about fish at all** — with a búr or a hearth open, **nothing** could be
     * moved between your own slots.
     *
     * ✅ **The SERVER was right to refuse it.** `moveChestItem` says so in as many
     * words — *"pack-to-pack belongs to moveStack"* — and two functions that both
     * half-answer one question is how they drift. 🔴 **The client was asking the
     * wrong one**, and the refusal is silent: the server sends both halves back
     * unchanged, so the stack simply snaps home with nothing said and **nothing
     * logged.** 📏 **That silence is how it hid: the server's log held no
     * `move_item` at all, which reads exactly like a click that never happened.**
     */
    if (from.where === 'player' && to.where === 'player') {
      connection?.sendMoveItem(from.slot, to.slot, count);
      return;
    }
    connection?.sendMoveChestItem(at, from, to, count);
  },
  /**
   * 🥫🔥 **A move involving a búr's FIRE goes as the hearth's message** —
   * D121, protocol 33.
   *
   * 🔴 **Not a new message.** A búr's fire is a hearth, and the server reads
   * the block at the named position to decide which rules govern it — so a
   * client cannot ask to be judged by the hearth's rules instead of the búr's,
   * **which is what keeps charcoal out of a búr (D71).**
   *
   * ⚠️ **The same pack-to-pack guard as above**, and for the same reason: with
   * the Fire tab open you can still drag inside your own pack, and that is
   * `move_item`'s job. **T247 is what happens when it is not.**
   */
  (at, from, to, count) => {
    if (from.where === 'player' && to.where === 'player') {
      connection?.sendMoveItem(from.slot, to.slot, count);
      return;
    }
    connection?.sendMoveHearthItem(at, from, to, count);
  },
  /**
   * 🥫🔥 **The búr's store and its own fire** — T222c, protocol 34.
   *
   * 🔴 **The pack is at neither end**, which is true of no other move on that
   * screen and is the whole reason this is its own message.
   */
  (at, from, to, count) => {
    connection?.sendMoveBurItem(at, from, to, count);
  },
);

/**
 * 🖱️ **Give the mouse to a panel, or give it back to the world.**
 *
 * 🔴 **ONE function, because the copies did not stay in step.** While you play,
 * the pointer is locked: no cursor, and the mouse turns your head. Every screen
 * has to release it on the way in and ask for it back on the way out — and
 * `setPackOpen`, `setHearthOpen` and `setChestOpen` each did their own version
 * of that, **while right-clicking a smíðabekkur went through none of them.**
 *
 * 🎯 **haugur, 2026-09-02, having hit it:** *"when i open smíðabekkur or even
 * press e to open a menu i have to press escape after the menu opens to
 * interact with it"* — and *"maybe we need to make it so that after you have
 * opened a menu, any menu, the mouse is automatically broken free."*
 *
 * ⚠️ **It also took the WHEEL away.** `controls.ts` steps the on-hand row while
 * the pointer is locked, so the recipe list could not be scrolled until Escape
 * had been pressed — which is why the chest recipe stayed invisible even after
 * the list learned to scroll.
 *
 * ⚠️ **`requestPointerLock` needs a user gesture**, which a keypress is. When
 * the browser declines — asked too soon after the last release — the player's
 * own click on the world asks again, the path `controls.ts` has always used.
 * **That is why the rejection is not reported.**
 */
function handMouseTo(): void {
  /* 🔴 **Derived, never passed in.** Opening a hearth or a chest CLOSES the
     pack first, so a version told *"the pack just closed"* would ask for the
     pointer back a moment before the new screen released it again — and which
     of the two won would depend on a promise resolving. **The mouse belongs to
     the world exactly when no panel is on screen**, so that is the question
     this asks. */
  /* 🎛️ **The game menu is in this list, and that is the whole of its mouse
     plumbing** (T187). It is a full-screen door, so it needs the cursor more
     than any other panel — and the comment above is the reason it is added
     here rather than doing its own version three lines away. */
  const anyOpen = pack.isOpen || hearthPanel.isOpen || chestPanel.isOpen || gameMenu.isOpen;
  controls?.setPaused(anyOpen);
  if (anyOpen) {
    document.exitPointerLock();
  } else {
    void document.querySelector('canvas')?.requestPointerLock();
  }
}

/** Open or close the chest, and hand the mouse over or take it back. */
function setChestOpen(open: boolean): void {
  if (open === chestPanel.isOpen) return;
  chestPanel.setOpen(open);
  handMouseTo();
  chestBeat(open);
}

/**
 * 🥫🔥 **While a BÚR is open, keep asking what is in it** — 2026-09-28.
 *
 * 🔴 **The hearth has done this since T222, and the búr never did** — so a búr
 * cooking a fish sat perfectly still. 🎯 haugur: *"i have to go out and into
 * the menu again for things to refresh."* ⚠️ **Only a container with a fire is
 * asked** — a chest changes only when somebody moves something, and polling
 * one would be quiet waste. The server answers with `chest_contents`.
 */
let chestPoll: ReturnType<typeof setInterval> | null = null;
function chestBeat(open: boolean): void {
  if (chestPoll !== null) {
    clearInterval(chestPoll);
    chestPoll = null;
  }
  if (!open) return;
  chestPoll = setInterval(() => {
    const at = chestPanel.openAt;
    /* 🧵 And a vefstofa while it spins — D127. */
    if (at === null || !chestPanel.polls) return;
    connection?.sendAskHearthContents(at);
  }, 2000);
}

/**
 * Open or close the hearth, and hand the mouse over or take it back.
 *
 * 🔴 **The same pointer-lock dance the pack does**, and for the same reason:
 * with the mouse captured there is no cursor to click a slot with.
 */
function setHearthOpen(open: boolean): void {
  if (open === hearthPanel.isOpen) return;
  hearthPanel.setOpen(open);
  handMouseTo();
  hearthBeat(open);
}

/**
 * 🍲 **While the hearth is open, keep asking what is in it** — T222, protocol
 * 32.
 *
 * 🔴 **The fire is the first container in this game that changes while you are
 * looking at it.** A chest changes only when somebody moves something, and a
 * hearth used to make one charcoal every **five real minutes** — so nobody ever
 * stood and watched one. **Cooking a fish takes about nineteen seconds**, so
 * now they will, and without this the screen would sit perfectly still while the
 * fire worked. ⚠️ **That reads as a fire that does not work**, and it is a bug
 * report rather than a mechanic.
 *
 * 📏 **Two seconds** — about a tenth of a cook, so a fish appears within a
 * moment of being done, and it is one small message every two seconds per open
 * screen rather than a broadcast to everybody.
 *
 * ⚠️ **Stopped the moment the screen closes**, or a player who walked away
 * would go on polling a fire behind them for the rest of the session. **The
 * server would silently refuse every one** — it checks the reach — which is
 * exactly the kind of quiet waste nothing ever notices.
 */
let hearthPoll: ReturnType<typeof setInterval> | null = null;
function hearthBeat(open: boolean): void {
  if (hearthPoll !== null) {
    clearInterval(hearthPoll);
    hearthPoll = null;
  }
  if (!open) return;
  hearthPoll = setInterval(() => {
    const at = hearthPanel.openAt;
    /* ⚠️ `openAt` is null between the screen opening and the first contents
       arriving. Nothing to ask about yet, and the contents are on their way. */
    if (at === null) return;
    connection?.sendAskHearthContents(at);
  }, 2000);
}

/**
 * Open or close the pack, and hand the mouse over or take it back.
 *
 * 🔴 **The pointer lock is the whole of the plumbing.** With the mouse captured
 * there is no cursor to click a slot with, so opening must release it — and
 * closing must take it back, or the player is left standing in a world they
 * cannot turn around in, which reads exactly like the game having frozen.
 *
 * ⚠️ **`requestPointerLock` needs a user gesture**, which a keypress is. Closing
 * from anywhere else would have to leave the lock to the player's own click on
 * the canvas — the path that already exists in `controls.ts`.
 */
function setPackOpen(open: boolean): void {
  if (open === pack.isOpen) return;
  /* 🖱️ `pack.setOpen` calls back into `handMouseTo`, so the mouse is dealt
     with here too — and, unlike before, it is dealt with the same way when the
     bench opens this screen without coming through this function. */
  pack.setOpen(open);
}

/**
 * The status bar along the top (2026-08-11).
 *
 * 🔴 **Built before the renderer**, because it sets `--topbar-h` and the canvas
 * is sized from it. Reversing the two gives one frame at the wrong height.
 */
const topBar = new TopBar();

/**
 * How tall the 3D picture is: the window, less the bar.
 *
 * 🔴 **The bar's space is not the game window** — haugur's requirement, and the
 * reason a future button on the bar cannot be mistaken for a click on the world.
 */
const viewHeight = (): number => Math.max(1, window.innerHeight - TOP_BAR_HEIGHT);

/**
 * The world clock, kept in step with the server's (T103).
 *
 * ⚠️ **Not rebuilt when the connection is** — switching to the flat debug view
 * closes the socket and opens a new one, and the date must not blink to a dash
 * and back while that happens. What day it is has nothing to do with which
 * socket is carrying it.
 */
const worldClock = new ClientWorldClock();

/**
 * 🍖 The two bars, carried forward between the server's corrections (T129).
 *
 * ⚠️ **Not rebuilt when the connection is**, for the same reason the clock is
 * not: how hungry you are belongs to the player, not to a socket, and switching
 * to the flat debug view must not make the bars blink.
 */
const clientVitals = new ClientVitals();

/**
 * The weather where we are standing, eased between the server's readings
 * (T103 piece ④).
 *
 * ⚠️ **Not rebuilt when the connection is**, for the same reason as the clock:
 * the flat-view switch closes the socket and opens a new one, and the sky must
 * not flash back to default grey and then to the real weather while that
 * happens.
 */
const worldWeather = new ClientWeather();

/** How far the sun must sink before it counts as night, for the stars. */
function smoothstepValue(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** A unit vector from a height and a bearing. **North is −z** (`world.ts`). */
function skyDirection(elevationDeg: number, azimuthDeg: number): Vector3 {
  const e = (elevationDeg * Math.PI) / 180;
  const a = (azimuthDeg * Math.PI) / 180;
  const horizontal = Math.cos(e);
  return new Vector3(Math.sin(a) * horizontal, Math.sin(e), -Math.cos(a) * horizontal);
}

/**
 * 🎬 **The posed winter night** (haugur's preset, 2026-08-11).
 *
 * ⚠️ **The moon here is NOT the world's moon.** On any given night the real one
 * may be below the horizon or nearly new, and a preset that sometimes shows
 * nothing is not a preset. **Asked directly, haugur chose to force a good one.**
 *
 * A waxing gibbous, high in the south-east: about 82% lit, so the phase is
 * unmistakable and the light is generous.
 */
const POSED_MOON = {
  elevation: 42,
  azimuth: 205,
  phase: 0.36,
  illumination: (1 - Math.cos(2 * Math.PI * 0.36)) / 2,
};

/**
 * 🌌 **The posed aurora** (T114, haugur's ask, 2026-08-12).
 *
 * 🎯 *"make sure to create the aurora test feature in Dev tools, so i can go
 * there click it and see best case scenarion for auroras."*
 *
 * ⚠️ **Kp 7, not Kp 9.** Nine is the top of the scale and it has happened four
 * times in thirty years of the archive — a "best case" that nobody will ever
 * meet in the game is a lie about what the feature looks like. **Seven is a
 * genuinely exceptional night that the drawn years actually contain**, and it
 * still fills the sky and shows the purple fringe.
 */
const POSED_AURORA_KP = 7;

/** Everything the sky and the light need, once the panel has had its say. */
interface SkyScene {
  readonly sunElevation: number;
  /** The geomagnetic index this frame should draw with (T114). */
  readonly kp: number;
  readonly sunDirection: { readonly x: number; readonly y: number; readonly z: number };
  readonly weather: SkyWeather;
  readonly moon: {
    readonly elevation: number;
    readonly illumination: number;
    readonly phase: number;
    readonly direction: { readonly x: number; readonly y: number; readonly z: number };
  };
}

/**
 * Decide what sky to draw: the world's, the sliders', or a preset's.
 *
 * 🔴 **One place decides, and both the light and the drawn objects read it.**
 * They used to decide separately, and the result was a panel whose switch moved
 * the light while the sun stayed where the clock put it.
 *
 * **The order of precedence is: preset, then the sun switch, then the clock.**
 */
function resolveScene(
  light: LightSettings,
  time: WorldTime,
  moon: MoonPosition,
  measured: SkyWeather,
  measuredKp: number,
): SkyScene {
  const realMoon = { ...moon, direction: moon.direction };

  if (light.preset === 'aurora') {
    return {
      /* Well past nautical twilight: `darknessFromSun` is fully open at −12°. */
      sunElevation: -25,
      sunDirection: skyDirection(-25, 350),
      kp: POSED_AURORA_KP,
      weather: { cloud: 0, fog: 0, rain: 0 },
      /* 🔴 **The moon is put DOWN, and that is the whole point of the preset.**
         A gibbous moon washes out an aurora exactly as it washes out the stars,
         and the real sky obliges only about half the time. haugur asked to see
         the best case; the best case is a moonless one. */
      moon: { ...realMoon, elevation: -40, illumination: 0 },
    };
  }

  if (light.preset === 'day') {
    return {
      sunElevation: 35,
      /* Irrelevant in daylight — `darknessFromSun` is 0 — but a scene has to
         carry a value, and the honest one is what the world actually has. */
      kp: measuredKp,
      sunDirection: skyDirection(35, 170),
      /* A perfect day is a clear one, by definition. */
      weather: { cloud: 0, fog: 0, rain: 0 },
      /* ⚠️ Put the moon below the horizon rather than leaving the real one up:
         a daytime moon is real, but it is not what "perfect day" means. */
      moon: { ...realMoon, elevation: -30, illumination: 0 },
    };
  }

  if (light.preset === 'night') {
    return {
      /* Well past civil twilight — properly dark, so the stars are all out. */
      sunElevation: -25,
      sunDirection: skyDirection(-25, 350),
      kp: measuredKp,
      weather: { cloud: 0, fog: 0, rain: 0 },
      moon: {
        elevation: POSED_MOON.elevation,
        illumination: POSED_MOON.illumination,
        phase: POSED_MOON.phase,
        direction: skyDirection(POSED_MOON.elevation, POSED_MOON.azimuth),
      },
    };
  }

  const weather: SkyWeather = light.clearSky ? { cloud: 0, fog: 0, rain: 0 } : measured;

  if (light.followClock) {
    const fromClock = sunPosition(time);
    return {
      sunElevation: fromClock.elevation,
      sunDirection: fromClock.direction,
      kp: measuredKp,
      weather,
      moon: realMoon,
    };
  }

  /* The sliders drive it. `Sun height` is a time-of-day dial down to −30°. */
  return {
    sunElevation: light.elevation,
    sunDirection: skyDirection(light.elevation, light.azimuth),
    kp: measuredKp,
    weather,
    moon: realMoon,
  };
}

/**
 * The Dev Tools window — Shift+P (T73).
 *
 * Declared here, before the render loop, and given handlers that call
 * functions defined further down. That is safe because nothing in it runs
 * until a key is pressed or a button is clicked, by which time the module has
 * finished evaluating.
 */
const devTools = new DevTools({
  onToggleFlat: () => setFlat(!flatWorld),
  onTeleport: (x, northing, label) => void teleportTo(x, northing, label),
  search: searchIceland,
});

/**
 * 🎛️ **The game menu — Escape** (T187).
 *
 * 🎯 **haugur, 2026-09-11:** *"when a player presses escape in the game i want
 * a menu popping up."*
 *
 * ⚠️ **It teleports through the SAME function Dev Tools does.** Two callers,
 * one `teleportTo` — see `gamemenu.ts` for why a second copy of anything here
 * would be the fault this repo has recorded five times.
 */
/* 🐑⚠️ T286 — "a short note in hud will not cut it": the arrow and the login warning. */
const flockWarning = new FlockWarning((x, z, label) => gameMenu.showOnMap(x, z, label));

const gameMenu = new GameMenu({
  onTeleport: (x, northing, label) => void teleportTo(x, northing, label),
  /* 🖱️ The menu is a panel like any other as far as the mouse is concerned. */
  onOpenChange: () => handMouseTo(),
  /**
   * 🗺️ What the World map needs (T189).
   *
   * ⚠️ **Everything here speaks ISN93 northings**, like `/api/terrain`,
   * `/api/roads` and the teleport box — the conversion to world z happens at
   * this boundary and nowhere else.
   */
  map: {
    httpBase: () => httpBase(),
    onTeleport: (x, northing, label) => void teleportTo(x, northing, label),
    playerPosition: () => {
      const at = controls?.state.position;
      return at === undefined ? null : { x: at.x, y: zToNorthing(at.z) };
    },
    /* 🧍 The same list the nameplates are drawn from, so the map and the
       world cannot disagree about who is where. */
    otherPlayers: () =>
      otherPlayers.nameplates().map((p) => ({ x: p.x, y: zToNorthing(p.z), name: p.name })),
    /* 🏠 T201. ⚠️ Read on every draw rather than handed over once, so a
       hearth lit while the menu is open moves the button with it. */
    homePosition: () => home,
  },
});
document.body.appendChild(gameMenu.root);

/**
 * ⎋ **Escape means BACK, and this is the only place that decides what back is**
 * (T187).
 *
 * 🎯 **haugur, 2026-09-11:** *"when a player presses escape in the game i want
 * a menu popping up."*
 *
 * 🔴 **One listener, one ladder, and the order is the design** — it is
 * Minecraft's: close the thing that is open, and if nothing is open, open the
 * menu. **So one press never does two things.** A player with a chest open who
 * wants out presses Escape twice and passes through the menu on the way.
 *
 * ## ⚠️ Why this is its own listener rather than a branch in the big one
 *
 * **The big key handler returns early on `isTypingTarget`** — correctly, since
 * `g` is a letter in *Urðarstígur* and flattening the world mid-search would
 * be a memorable way to lose your place. But **Escape is never typing**, and
 * behind that guard it could not close the menu from inside the menu's own
 * coordinate box. 🔴 **That is exactly the trap `devtools.ts` names at its
 * top**, and it is why that window has a listener of its own too.
 *
 * ## ⚠️ `defaultPrevented` is how the other two windows keep their Escape
 *
 * Dev Tools and the bug box each close themselves on Escape from their own
 * listeners, **both registered before this one**, and both now call
 * `preventDefault` when they do. Without that check a single press would close
 * Dev Tools *and* open the menu in the same frame — which on screen reads as
 * Dev Tools having **turned into** the menu. 🔴 **The bug box matters more than
 * the tidiness:** F8 is the most valuable instrument this project owns, and a
 * report being written is the worst possible moment to cover the screen.
 */
document.addEventListener('keydown', (e) => {
  if (e.repeat || e.code !== 'Escape' || e.defaultPrevented) return;

  if (gameMenu.isOpen) {
    gameMenu.setOpen(false);
    return;
  }
  if (hearthPanel.isOpen) {
    setHearthOpen(false);
    return;
  }
  if (chestPanel.isOpen) {
    setChestOpen(false);
    return;
  }
  if (pack.isOpen) {
    setPackOpen(false);
    return;
  }
  gameMenu.setOpen(true);
});

/* 🔴 The two store subscriptions live further down, beside the startup call
   they replaced — `camera` and the lights do not exist yet on this line, and a
   subscription fires immediately by design. */

const scene = new Scene();

/**
 * ⚠️ The sky and the fog have to be the same colour, or the horizon reads as a
 * cut-out (T84).
 *
 * `scene.background` was never set at all, so the sky rendered **black** while
 * the fog faded distant terrain toward a pale blue-grey. Two unrelated colours
 * meeting along a hard line is exactly what a cut-out looks like, and it is why
 * every screenshot of the world has one.
 *
 * Held in a variable because the Dev Tools colour picker moves it and because
 * the fog is rebuilt whenever the render distance changes.
 */
const skyColour = new Color(0x9fb4c7);
scene.fog = new Fog(skyColour, CHUNK_SIZE * 3, CHUNK_SIZE * viewRadius * 2.2);

/**
 * 🌟 The sky and everything in it — the gradient dome, the stars, the sun and
 * the moon (T116, **D59**).
 *
 * ⚠️ **Built before `setSky` is first called**, because the gradient now lives on
 * this object's uniforms rather than on a texture. ⚠️ **Everything it draws is
 * dimmed or hidden by cloud** — the same oktas that colour the sky.
 */
/**
 * 🌊 **The water you see above you when the sky is hidden** — T61.
 *
 * 🔴 **Nothing is behind the sky dome.** `scene.background` is never set in
 * ordinary play, and `skyobjects.ts` records that the sky rendered **black**
 * the one time it was relied on. **So hiding the sky without this is a black
 * sky, not a wet one.**
 */
const submergedSky = new Color();

/**
 * 🔴 **Whether {@link skyObjects} exists yet.**
 *
 * ⚠️ `applySubmersion` is a hoisted function declaration and is called during
 * start-up — from `applySceneLight` — **before `skyObjects` is constructed**.
 * Touching a `const` before its line has run is a `ReferenceError`, not an
 * `undefined`, so the sea would take the whole client down on the first frame.
 */
let skyReady = false;

const skyObjects = new SkyObjects(scene);
/* 🌊 T61 — from here on `applySubmersion` may hide the sky. See `skyReady`. */
skyReady = true;

/**
 * The visibility the weather last asked for, so a rebuilt `Fog` can inherit it.
 *
 * ⚠️ Declared up here with the rest of the scene's state rather than beside
 * `setFogDistance`, because `applyLight` runs during startup and a `let` further
 * down the module would still be in its temporal dead zone.
 */
let lastViewFactor = 1;

/* ──────────────────────────────────────────────────────────────────────────
   🌊 UNDER WATER — T61
   ────────────────────────────────────────────────────────────────────────── */

/**
 * 🌊 **What the fog and the lights would be if you were in AIR.**
 *
 * 🔴 **Held rather than written straight to the scene**, because being under
 * water changes both, and the frame that puts you back on the beach has to know
 * what to put back. `applySceneLight` fills these in; **`applySubmersion` is the
 * only thing that writes `scene.fog` or a light intensity.**
 */
let airFogColour = '#ffffff';
let airSun = 0;
let airSky = 0;
let airMoon = 0;

/** Where the eye is, as of the last frame. */
let submersion: Submersion = DRY;

/**
 * 🌊 **Put the sea — or the air — on the scene. The ONLY writer of both.**
 *
 * 🔴 **Called from three places and it must stay that way:** whenever the light
 * is recomputed, whenever the view distance changes, and whenever the eye moves
 * between water and air. ⚠️ **Do not set `scene.fog.color`, `sun.intensity`,
 * `skyLight.intensity` or `moonLight.intensity` anywhere else** — that is the
 * fault this function exists to prevent, and this repo has paid for it six
 * times.
 *
 * 📖 The numbers and the reasoning are in `underwater.ts` and in
 * `Development/Minecraft/20_Water_Seen_From_Inside.md`.
 */
function applySubmersion(): void {
  const deep = submersion.submerged ? submersion.depth : 0;

  /* 🌑 The world dims with depth. 🔴 **The FISH do not** — `fishbody.ts` lights
     them with baked arithmetic and deliberately not the scene's sun — which is
     what makes this safe to do at all. */
  const scale = submersion.submerged ? lightScaleAt(deep) : 1;
  sun.intensity = airSun * scale;
  skyLight.intensity = airSky * scale;
  moonLight.intensity = airMoon * scale;

  /**
   * 🌊 **The sky, and it is the half that actually matters** — T61.
   *
   * 🔴 **`scene.fog` cannot reach the sky, and that is why the sea looked like
   * nothing had happened.** A magenta fog forced to twenty metres turned every
   * scrap of terrain magenta **and left the sky bright blue** — and with your
   * eye 1.6 m above a flat seabed, ground sixty metres out sits **one and a
   * half degrees** below the horizon, so almost everything you can see is
   * either close ground or sky.
   *
   * ✅ **So under water the sky is hidden and the background becomes water.**
   */
  if (skyReady) {
    skyObjects.setVisible(!submersion.submerged);
    if (submersion.submerged) {
      submergedSky.set(fogColourAt(deep));
      scene.background = submergedSky;
    } else {
      /* ⚠️ Back to nothing, not to a colour — the dome is the sky again. */
      scene.background = null;
    }
  }

  if (!scene.fog || !(scene.fog instanceof Fog)) return;

  if (!submersion.submerged) {
    scene.fog.color.set(airFogColour);
    scene.fog.near = CHUNK_SIZE * 3 * lastViewFactor;
    scene.fog.far = CHUNK_SIZE * viewRadius * 2.2 * lastViewFactor;
    return;
  }

  /* 🔴 **The single biggest line in the ticket: under water the fog starts AT
     THE CAMERA.** In air it starts 96 m out. `Minecraft/20`: *"air fog and
     water fog are the same fog with different numbers."* */
  scene.fog.color.set(fogColourAt(deep));
  scene.fog.near = UNDERWATER_FOG_NEAR;
  /* ⚠️ Never further than you could see in air — a render distance pulled right
     down must not be overridden into drawing more than it has. */
  scene.fog.far = Math.min(UNDERWATER_FOG_FAR, CHUNK_SIZE * viewRadius * 2.2);
}

/**
 * Repaint the sky.
 *
 * 🔴 **Two uniforms on a dome, NOT a background texture — and that is the fix
 * for a bug that had been on screen since piece ④ shipped.**
 *
 * `scene.background` with an equirectangular texture is **converted to a cube
 * map once and cached by three, keyed on the texture, until the texture is
 * disposed.** Writing new pixels and setting `needsUpdate` changes nothing at
 * all. **The sky was frozen at the colour it held on the first frame** — through
 * every weather and every hour — while the fog and the ground light, which are
 * driven by `skyColour`, went on responding correctly. 🎯 **haugur, before being
 * told:** *"i kinda had the feeling i was always just seeing the same kind of
 * white sky."* He was right.
 *
 * ⚠️ **`skyColour` still carries the HORIZON stop** for the fog and the
 * `HemisphereLight` — Design §12.6 is explicit that neither may take the zenith.
 */
function setSky(zenith: string, horizon: string): void {
  skyColour.set(horizon);
  skyObjects.setGradient(zenith, horizon);
}

/* One definition of where the world is, shared with the server.
   🔴 **`worldBounds()` since T193 — all of Iceland.** This was
   `testAreaBounds()`, which is why the streaming ring stopped at a 1,400 m
   box. `TEST_AREA` still exists and still means *where we started*; it just
   stopped meaning *where the world ends*. */
const areaBounds = worldBounds();
const inWorldArea = (coord: ChunkCoord): boolean => chunkIntersects(coord, areaBounds, CHUNK_SIZE);

const camera = new PerspectiveCamera(75, window.innerWidth / viewHeight(), 0.1, 2000);

/* Position, intensity and tone curve all come from the Dev Tools panel — see
   `applyLight` below, and the reasoning in `devtools.ts`. */
const sun = new DirectionalLight(0xfff4e6, 2.2);

/**
 * ⚠️ A **hemisphere** light, not an ambient one — and the difference is the
 * whole reason walls were rendering black.
 *
 * `AmbientLight` adds the same flat amount to every surface regardless of which
 * way it faces. That is nothing like being outdoors: a wall facing away from
 * the sun is not dark, it is lit by *the entire sky*, which on an Icelandic
 * overcast day is the brightest thing there is.
 *
 * With the sun due south at 31°, the north face of anything got `NdotL = 0` and
 * fell back to flat ambient alone — which is why haugur's móberg letters came
 * out as black cut-outs against a bright sky (2026-08-07).
 *
 * `HemisphereLight` blends sky colour above to ground-bounce below across the
 * surface normal, so a vertical face picks up half the sky instead of nothing.
 * The sky colour is the *same* colour as `scene.background`, which means
 * changing the sky in the panel changes the light too — as it should.
 */
const skyLight = new HemisphereLight(0x9fb4c7, 0x3a342b, 1.4);

/**
 * 🌕 The moon's own light (T116, **D60**).
 *
 * ⚠️ **A second directional light, not a share of the sun's.** The moon is
 * somewhere else in the sky entirely — often the opposite side — and lighting a
 * moonlit night from the sun's direction would put every shadow the wrong way
 * round. That is `sun.ts`'s "north is −z" trap wearing a different hat.
 *
 * **Cooler than the sun and much dimmer.** Moonlight is reflected sunlight and
 * is physically the same colour, but it reads as blue because the eye's colour
 * vision gives up at that level — the Purkinje shift — and every painter since
 * has agreed with the eye rather than the spectrometer.
 */
const moonLight = new DirectionalLight(0xc9d6ea, 0);

scene.add(sun, skyLight, moonLight);

/**
 * The block tiles, built once at startup (T84).
 *
 * ⚠️ **A node material, not `MeshLambertMaterial`.** Sampling a *layer* of a
 * texture array per vertex is not something the classic material can express,
 * and a texture array is what avoids an atlas bleeding one block's colour into
 * its neighbour's at distance. `MeshLambertNodeMaterial` is the WebGPU
 * renderer's native path anyway — the classic material is converted to one
 * behind the scenes.
 *
 * The graph is: **tile colour × ambient occlusion × the block's own value
 * jitter**, with the last two fading out with distance — see `blockColorNode`.
 */
let blockTextures = buildBlockTextures();
const material = new MeshLambertNodeMaterial();

/**
 * 🌊 **The water's own material — you can see into it (T158, D115).**
 *
 * 🎯 **haugur opened this on 2026-08-25:** *"it needs to be seethrough for
 * fishing, maybe a little bluer."* The bluer half shipped that hour as a
 * texture; **this is the other half, and it is machinery rather than a
 * picture.** 🎯 He chose it again on 2026-09-16 over two cheaper tricks,
 * knowing it was the big one, because fish have to be visible *under* the
 * surface.
 *
 * ## 🔴 A SECOND material, and the world's own is untouched
 *
 * **The block material stays exactly as opaque as it has always been.** One
 * draw call has one material, so water needs its own — which is the pattern the
 * flame, the torch post, the driftwood and the smoke all already follow, each
 * for a reason this shares: *not opaque* is on every one of their lists.
 *
 * ⚠️ **This is the change class that blacked out dev AND staging** — a texture
 * on the held item, 2026-09-03. The cause then was a PLAIN material mutated
 * into a textured one while the renderer was running. ✅ **Nothing is mutated
 * here**: a second material is built at startup, beside the first, and the
 * first is never touched.
 *
 * ## 🔴 `depthWrite` stays ON, and that is what makes sorting a non-problem
 *
 * T158 called *"water seen through water"* the hard case, and it is — for the
 * usual setup, where transparent surfaces do not write depth and must be sorted
 * back to front. ✅ **Writing depth removes the case entirely:** the nearest
 * water surface wins the depth test and every surface behind it is rejected, so
 * **you see one pane of water over the seabed, never two panes multiplying into
 * murk.** The sea has one surface; that is what it should look like.
 *
 * ⚠️ **The cost is that water cannot be seen through water** — standing on the
 * seabed looking up through a wave at another wave shows one. **Nobody has ever
 * been able to do that here anyway** (T61: the client has no underwater at
 * all), and a flat sea has nothing behind its surface but its own bed.
 *
 * ⚠️ **NO `alphaTest` on this one.** The block material's 0.5 is a leaf
 * stencil; put it here and any water more see-through than half would be
 * **discarded entirely** — an invisible sea, not a clear one. 🔴 **The two
 * materials must not be made to look alike out of tidiness.**
 */
const waterMaterial = new MeshLambertNodeMaterial();
waterMaterial.transparent = true;
waterMaterial.depthWrite = true;

/**
 * 🪟 **The surface from below is NOT drawn, and that is a retreat rather than a
 * decision** — T61, T241.
 *
 * 🔴 **This material took three's default of front faces only until 2026-09-21,
 * when it was set to `DoubleSide` so you could see the surface overhead.
 * haugur played it within the hour and filed #701 — *"this is how it looks, sky
 * is black."*** 📷 **His picture is a brightly lit seabed under a BLACK
 * ceiling.**
 *
 * 🔴 **THE CAUSE: a Lambert back face is unlit when the light is above it.**
 * The surface's underside has its normal flipped downward, the sun is over the
 * water, and the face therefore receives nothing. **It did not draw water
 * overhead — it drew a hole.**
 *
 * ⚠️ **`scene.background` was NOT the fault** — proved by forcing it magenta and
 * photographing a magenta sky on dry land. **That test is why this was found in
 * one pass instead of three.**
 *
 * 👉 **So the surface goes to T241 where it always belonged.** Seen from below a
 * water surface is a **window and a mirror**, not a lit plane — Snell's circle,
 * the sky compressed into a bright disc overhead, everything outside it
 * reflecting the seabed back at you. **That needs the light to bend, which is
 * shader work.**
 *
 * 🔴 **AND THE LESSON IS THE DAY'S, FOR THE SECOND TIME: I NAMED THIS SEAM AND
 * THEN SHIPPED ACROSS IT ANYWAY.** The previous commit's own message said a
 * surface that reads properly is T241 — **and it shipped a half-measure into
 * T61 regardless, which made the game worse than having nothing there.**
 */

/**
 * 🌊 **How much of the water you do NOT see through**, 0.15…1.
 *
 * 🔴 **A uniform, and a slider, because nobody building this can see it.** That
 * is the T94 switches' reasoning applied to the one question only haugur can
 * answer — *how clear should the sea be?* A uniform needs no shader rebuild and
 * no deploy, so he can find the number on the machine that shows it.
 *
 * ⚠️ **The default is a starting point, not a measurement.** Clear enough to
 * read a fish under it, solid enough that the sea still reads as water rather
 * than as blue glass.
 */
const waterOpacity = uniform(DEFAULT_GRAPHICS.waterOpacity);

/**
 * 🌊 Drawn after everything opaque. Three's own default for a transparent
 * material is 0, the same as the ground's, and *the same number is not an
 * order.*
 */
const WATER_RENDER_ORDER = 1;

/**
 * 🐟 **And the fish are drawn BEFORE it** — see `fishmesh.ts`, which carries the
 * whole reasoning and the bug that shipped without it.
 *
 * 🔴 **These two numbers are one ordering, not two settings.** The water writes
 * depth, so anything under the surface that draws *after* it is rejected —
 * which is every fish in the sea. **Raising the fish above this number makes
 * them vanish, silently, with every test still passing.**
 */

/** 🌊 One water object per chunk that has any, keyed and disposed like the ground's. */
const waterMeshes = new Map<string, Mesh>();

/**
 * 🐟 **Every fish you can see, as one object** — added to the scene at startup,
 * empty, even though most of Iceland is dry.
 *
 * 🔴 **That is the smoke's lesson made a habit:** a material whose shader is
 * first compiled when somebody walks onto a beach is a shader nobody has ever
 * run, and it would fail in front of haugur rather than here.
 */
const fishSchool = new Shoal();
/* 🐑 The flock — T255. ⚠️ Its group is added to the scene beside the other
   players', because a sheep is a body in the world exactly as a person is. */
const flock = new Flock();
scene.add(flock.group);
/* 🦊 The foxes — T288, decision 134. Beside the flock, for the same reason. */
const foxes = new Foxes();
scene.add(foxes.group);
/** 🪢 Every lead and tether rope in view — D128. */
const ropes = new Ropes(scene);
/** 🎣 Every float in the water, and the last word from the server on them — D129. */
const floatMeshes = new Floats(scene);
let floatsNow: readonly FloatView[] = [];
/** 🪢 This connection's own id, from the welcome — so a client knows its own ropes. */
let myConnection: string | null = null;
/* 🐟 One mesh per species — see `Shoal`. Three draw calls, and NOT three times
   the fish: the server splits one budget of 60 between them. */
for (const mesh of fishSchool.meshes) scene.add(mesh);
/**
 * 🔬 **What the school last drew, where a probe can read it** — T220.
 *
 * 🔴 **This exists because TWO fixes were shipped on a guess and haugur found
 * both.** From outside, *no fish were ever built*, *fish were built and the
 * depth test ate them* and *fish were built too far away to see* look
 * identical — and every picture this workstation can take is of a fallback
 * renderer at 2 fps.
 *
 * ⚠️ **A number the page can be asked for settles in one run what a screenshot
 * argues about for an afternoon.**
 */
/**
 * 🔴 **A GETTER, AND IT HAS TO BE.** `Shoal.stats` gathers a fresh reading from
 * every species each time it is asked, so **assigning it once would have pinned
 * `window.__fish` to the numbers at startup** — zero fish, not ready — for the
 * life of the page.
 *
 * ⚠️ **`fishprobe.mjs` would then have reported an empty sea on every run, for
 * ever, and been right about the object it was reading.** 🔴 **That is the fish
 * session's own process failure wearing a new coat:** *a measurement nobody can
 * trust is worth less than none*, and a frozen instrument is indistinguishable
 * from a broken world.
 */
/** 🛏️ D133 — the same window for beds: loaded, placed, waiting, or why not. */
Object.defineProperty(window, '__beds', {
  get: () => rumModel.stats,
  configurable: true,
});

Object.defineProperty(window, '__fish', {
  get: () => fishSchool.stats,
  configurable: true,
});

/**
 * 🐑 **The same window for the flock** — T255, and for the same reason.
 *
 * 🔴 **The server had ten sheep standing on the ground and the client drew
 * NONE**, and a daylight screenshot from the middle of the flock could say only
 * *"no sheep"* — not whether the model failed, the cut failed, or they were
 * drawn 347 km away. ⚠️ **That is the settler's own recorded bug** and the
 * reason `__fish` exists at all: ***a measurement nobody can trust is worth
 * less than none.***
 */
Object.defineProperty(window, '__sheep', {
  get: () => flock.stats,
  configurable: true,
});
/** 🦊 And the foxes — what the model measured and what the first one is doing. */
Object.defineProperty(window, '__foxes', {
  get: () => foxes.stats,
  configurable: true,
});

/**
 * Turn one bucket of meshed faces into geometry the renderer can draw.
 *
 * ⚠️ **Shared by the ground and the water rather than written twice** (T158).
 * The two buckets carry identical attributes by construction — that is why
 * `ChunkGeometry` was split out — and two copies of this would be two places to
 * forget an attribute in.
 */
function geometryOf(mesh: ChunkGeometry): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(mesh.positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(mesh.normals, 3));
  /* ⚠️ Two attributes, not one grey `color` (T94). Ambient occlusion is shape
     and the jitter is variety; the shader fades them out separately, so
     multiplying them together here would throw away the difference. */
  geometry.setAttribute('ao', new BufferAttribute(mesh.ao, 1));
  geometry.setAttribute('jitter', new BufferAttribute(mesh.jitter, 1));
  /* 🔦 Torch and hearth light. ⚠️ A THIRD attribute rather than folded into
     'ao' — Minecraft/14 §7.3 warns about exactly that: they are three different
     things, and merging them throws away what tells them apart. */
  geometry.setAttribute('light', new BufferAttribute(mesh.light, 1));
  geometry.setAttribute('uv', new BufferAttribute(mesh.uvs, 2));
  /* Which tile each face draws. One float per vertex; the shader rounds it to
     an integer layer index. */
  geometry.setAttribute('layer', new BufferAttribute(mesh.layers, 1));
  geometry.setIndex(new BufferAttribute(mesh.indices, 1));
  return geometry;
}

/**
 * 🔥 **The fire.** One texture and one material for the whole world; one mesh
 * per chunk that has anything burning in it.
 *
 * ⚠️ **Kept beside the terrain meshes rather than inside them.** A flame is
 * transparent, unshaded, animated and not a cube — every one of those is a
 * reason it cannot ride in the world's merged opaque mesh.
 */
const flameTexture = buildFlameTexture();
const flameMaterial = buildFlameMaterial(flameTexture);
const flames = new Map<string, Mesh>();

/**
 * 🔦 **A chunk's torch POSTS.** Kept beside the flames and the terrain, with
 * the same key and the same lifetime, for the same reason: a post left behind
 * when its chunk is rebuilt would stand in mid-air.
 *
 * ⚠️ **Two objects per chunk, not one.** The post is opaque wood the sun
 * shades; the flame is unshaded, transparent and animated. **Every one of those
 * is a reason they cannot share a material**, which is the same argument
 * `flame.ts` makes for not riding in the world mesh.
 */
const torchPosts = new Map<string, Mesh>();
const torchMaterial = buildTorchMaterial();
/* 🪵 Driftwood wears the mean of the texture haugur chose, so the flat colour and
   the picture cannot drift apart. See buildDriftMaterial for why it is not the
   texture itself yet. */
/**
 * 🏠 **The storehouse model, loaded once** — D109. Every chunk with a door in
 * it gets a clone; a chunk built before the file arrives fills itself in when
 * it does (burmesh.ts). Served from the client's own assets.
 */
/* 🪧 The carved boards (D110) are loaded BY the model, in its own queue — a
   building placed before they decoded would otherwise stand with a bare gable
   for ever. */
const burModel = new BurModel(stamped('/models/bur.glb'));
const burGroups = new Map<string, BurGroup>();
/** 🛏️ D133 — the rúm, one model per bed head, per chunk. */
const rumModel = new RumModel(stamped('/models/rum.glb'));
const rumGroups = new Map<string, BurGroup>();
/** 🧵 D136 — the loom (his pick A) and the kljásteinn (his pick B), per chunk. */
const loomModels = new LoomModels(stamped('/models/loom.glb'), stamped('/models/kljasteinn.glb'));
const loomGroups = new Map<string, BurGroup>();
/** 🔬 What the looms did, for a probe — a loom nobody can see must say why. */
Object.defineProperty(window, '__looms', { get: () => loomModels.stats });
/** 🏠 D135 — the pit-house vefstofa: its walls, and the four models he picks. */
const pitModels = new PitHouseModels(
  (piece) => stamped(`/models/pit_${piece}.glb`),
  stamped('/models/pit_roof_top.png'),
);
const pitGroups = new Map<string, BurGroup>();

const driftMaterial = buildDriftMaterial(
  blockAppearance(BLOCK['rekavidur'] ?? 0)?.side ?? '#706A62',
);
const driftMeshes = new Map<string, Mesh>();

/**
 * 💨 **A chunk's SMOKE.** Third of the three objects a chunk's fires need,
 * beside the flames and the torch posts, with the same key and the same
 * lifetime.
 *
 * 🔴 **Its own material because smoke is NOT additive.** A flame is light and
 * adds to what is behind it; a puff of smoke is a thing in the way and has to
 * dull what it covers. Sharing the flame's material would make a column of
 * smoke brighten the night sky.
 */
/**
 * 🌧️ **The rain — ONE object, made once, never rebuilt.**
 *
 * 🔴 **It is in the scene even in July**, and that is the lesson of 2026-09-01
 * built in: a material's shader is not compiled until something using it is
 * drawn, so rain that only appeared in a downpour would be a shader nobody had
 * ever run — and it would fail the first time the weather turned, in front of
 * haugur, exactly as the smoke did. ✅ `rainStrength` is 0 when it is dry, so
 * every drop is discarded before it shades a pixel.
 */
const rainMaterial = buildRainMaterial(buildRainTexture());
const rainMesh = buildRainMesh(rainMaterial);
if (rainMesh) scene.add(rainMesh);

const smokeMeshes = new Map<string, Mesh>();
const smokeMaterial = buildSmokeMaterial(buildSmokeTexture());

/**
 * 🔴 **Compile the smoke shader NOW, not when somebody lights a fire.**
 *
 * 🎯 **haugur, 2026-09-01:** *"as soon as i place a log on the fire on dev, the
 * world stops moving."* The first version of this material was broken and
 * **nothing could see it** — a shader is not compiled until something using it
 * is drawn, so the gates, 305 tests and two headless boots all passed a shader
 * that had never run. **It waited for a player to light a fire.**
 *
 * ✅ One invisible puff, parked at the origin, drawn for ever. **If this shader
 * is wrong the world now fails on EVERY boot**, in front of `shoot.mjs`, rather
 * than in front of haugur.
 */
primeSmoke(scene, smokeMaterial);

/**
 * 🍃 **Where a hole in a leaf becomes a hole on the screen (T144).**
 *
 * A texel whose alpha is at or below this is **discarded** — the pixel is never
 * drawn and never written to the depth buffer. ✅ **Read in the installed three
 * r180 source rather than assumed:** `NodeMaterial.setupDiffuseColor` discards
 * where `alpha <= alphaTest`, and then — because `transparent` is `false` and
 * blending is normal — forces alpha back to 1 for everything that survives. **So
 * this is a stencil, not transparency.** Nothing has to be sorted, nothing
 * blends, and a crown cannot draw itself in the wrong order.
 *
 * ✅ **Safe on the one shared material**, and that is checked mechanically
 * rather than believed: `textures.test.ts` asserts every tile except the cutout
 * ones is fully opaque, so nothing else can lose a pixel to this.
 *
 * ⚠️ **0.5 is also what makes a distant crown close up, and that is wanted.**
 * Mipmaps average the alpha along with the colour, so at range the holes blur
 * toward "mostly leaf" and stop passing the test — a birch you can see from
 * across the valley stays a birch. 🔴 **The failure mode to watch for is the
 * opposite one**: raise this much above 0.5 and mid-distance crowns start
 * dissolving instead, which is the classic cutout-plus-mipmap artefact.
 */
const LEAF_ALPHA_TEST = 0.5;
material.alphaTest = LEAF_ALPHA_TEST;

/**
 * The switches T94 added, as uniforms rather than rebuilt shaders.
 *
 * ⚠️ **These exist because the T84 session diagnosed the crawl three times
 * from an armchair and was wrong every time.** Nobody working on this can see
 * the screen it happens on, so the only way to find out which of the four
 * things on top of each other is aliasing is to let haugur switch each one off
 * on the machine that shows it. A uniform costs nothing and needs no reload.
 *
 * `1` means "fully on", `0` means "gone".
 */
const textureStrength = uniform(1);
const aoStrength = uniform(1);
const jitterStrength = uniform(1);
/** 🔦 `1` lets torches and fires light the world; `0` puts them out. */
const lightStrength = uniform(1);
/** `1` fades per-block detail out with distance; `0` leaves it on everywhere. */
const detailFadeStrength = uniform(1);
/**
 * 🌊 **`1` lets the water move; `0` leaves it the still photograph** — T207.
 *
 * 🔴 **The way out if the movement reads badly on a machine nobody here can
 * see.** It is a uniform, so it needs no shader rebuild and no deploy, and `0`
 * restores the **exact** tile the sea has always drawn — the frames are a
 * separate bank of layers, not a change to the still one.
 */
const movingWaterStrength = uniform(1);
/**
 * 🌊 **Which frame of the water is showing**, driven from the render loop —
 * the same shape as `flameFrame`, and for the same reason.
 */
const waterFrame = uniform(0);

/**
 * Where per-block detail starts and finishes fading out, in metres.
 *
 * ⚠️ **Ambient occlusion and the value jitter are carried by the geometry, so
 * nothing filters them.** Mipmaps only ever touch the texture. Once a block is
 * small on screen, a per-block number is re-sampled from a different place
 * every frame the camera moves — which is what crawling *is*. Fading it out is
 * the only fix available, because there is no mip chain for a vertex attribute.
 *
 * The numbers are a starting point, not a measurement.
 */
const DETAIL_FADE_START = 60;
const DETAIL_FADE_END = 200;

/**
 * Point the block material at the texture, and at the tile's alpha.
 *
 * Rebuilt (rather than driven by a uniform) only when the *shape* of the graph
 * changes — the texture object itself, or forcing a mip level, which needs a
 * different sampling instruction.
 *
 * ⚠️ **Both nodes come from ONE sampler, and that is not tidiness.** Colour and
 * alpha must be the same read of the same texel; two samplers would be two
 * texture reads that a driver is free to resolve at different mip levels, and
 * the leaf's edge would stop lining up with the leaf.
 *
 * @param forcedMip a mip level to sample explicitly, or `null` for the ordinary
 *   derivative-based choice. **The decisive experiment for T94:** if pinning
 *   the smallest mip does not stop the crawl, the tile texture is not what is
 *   crawling.
 */
function applyBlockNodes(forcedMip: number | null): void {
  const { color, alpha, glow } = blockNodes(forcedMip);
  material.colorNode = color;
  /**
   * 🔦 **Torch and hearth light, ADDED rather than multiplied.**
   *
   * 🔴 **`emissiveNode` is the only safe place for it, and the reason is
   * blunt: this material is LAMBERT.** A diffuse colour is multiplied by the
   * light falling on it, so on a December midnight the sun contributes nothing
   * and no amount of colour would show. **A torch has to contribute light, not
   * colour.**
   *
   * ✅ **Emissive is added after the lighting**, so a surface with no torch near
   * it is bit-for-bit what it was before this existed — the attribute is 0 and
   * 0 adds nothing. **Nothing in Iceland can get darker because of this.**
   */
  emissiveOf(material).emissiveNode = glow;
  /* 🍃 Without this the material has no alpha to test — a vec3 `colorNode` is
     padded to alpha 1, so every leaf would stay a solid box no matter what the
     tile says. `opacityNode` is the node material's way in. */
  material.opacityNode = alpha;
  material.needsUpdate = true;

  /**
   * 🌊 **The water wears the SAME graph — T158.**
   *
   * 🔴 **Rebuilt from `blockNodes` rather than copied from the material above**,
   * so the sea keeps everything the world's own material gives it: its tile, the
   * movement frames (T207), the distance fade, the four diagnostic switches, and
   * a torch lighting the shallows. **A hand-written second graph would be a
   * second copy that drifts**, which this repo has paid for five times.
   *
   * ⚠️ **Only the opacity differs**, and it differs completely: the ground asks
   * the TILE how solid it is, and water is told by a uniform haugur can move.
   */
  const water = blockNodes(forcedMip);
  waterMaterial.colorNode = water.color;
  emissiveOf(waterMaterial).emissiveNode = water.glow;
  waterMaterial.opacityNode = waterOpacity;
  waterMaterial.needsUpdate = true;
}

/**
 * 🔴 **`emissiveNode` exists on every NodeMaterial at runtime and is declared on
 * almost none of them.**
 *
 * ✅ **Read in the installed three r180 source rather than assumed** — the same
 * check the leaf `alphaTest` note records. `NodeMaterial.setupLighting` does
 * `const { backdropNode, backdropAlphaNode, emissiveNode } = this` and then adds
 * it to the lit result. **It is `@types/three` that declares the field only on
 * Standard and Phong**, and Lambert inherits the behaviour without the type.
 *
 * ⚠️ **So this is a gap in the TYPES, not a misuse of the library**, and it is
 * narrowed to one property here rather than casting the material to `any` at the
 * call site.
 */
interface HasEmissiveNode {
  emissiveNode: Node | null;
}

function emissiveOf(m: MeshLambertNodeMaterial): HasEmissiveNode {
  return m as unknown as HasEmissiveNode;
}

function blockNodes(forcedMip: number | null): { color: Node; alpha: Node; glow: Node } {
  /* ⚠️ `.round()`, not `.toInt()`. A layer index is constant across a quad, but
     it still travels as an interpolated float, and perspective-correct
     interpolation of a constant is only exact to within a rounding error.
     Truncating 30 that arrived as 29.999998 draws tile 29 — a different block's
     texture, on scattered pixels, worse where triangles are small. */
  const written = attribute('layer', 'float').round().toInt();
  /**
   * 🌊 **Water draws from the moving bank instead of its still tile** — T207.
   *
   * 🔴 **Nothing about the world changed to make this happen.** The mesher
   * still writes water's still layer into every water vertex, exactly as it
   * always has, and every chunk already in memory stays correct. **This is the
   * only line that knows the water moves**, which is what makes the switch
   * above able to undo it completely.
   *
   * ⚠️ **The still layers are tested one by one rather than as a range.** They
   * are next to each other today; a range test would go on passing and start
   * animating a neighbouring block's tile the day somebody adds a face.
   */
  const stillWater = rippleStillLayers();
  const rippleBase = rippleLayerBase();
  const layer =
    rippleBase === null || stillWater.length === 0
      ? written
      : select(
          and(
            stillWater.map((l) => equal(written, int(l))).reduce((a, b) => or(a, b)),
            greaterThan(movingWaterStrength, float(0.5)),
          ),
          float(rippleBase).add(waterFrame).round().toInt(),
          written,
        );
  /* ⚠️ The mip level goes in when the sampler is BUILT. `.depth()` returns a
     plain node, so `.level()` is no longer reachable after it — and the two
     have to end up on the same `TextureNode` to produce one sample. */
  const sampler =
    forcedMip === null
      ? texture(blockTextures.texture, uv())
      : texture(blockTextures.texture, uv(), float(forcedMip));
  const tile = sampler.depth(layer);

  /* 1 up close, falling to 0 in the distance. */
  const near = smoothstep(DETAIL_FADE_START, DETAIL_FADE_END, positionView.length())
    .oneMinus()
    .mul(detailFadeStrength)
    .add(detailFadeStrength.oneMinus());

  const ao = mix(float(1), attribute('ao', 'float'), aoStrength.mul(near));
  const jitter = mix(float(1), attribute('jitter', 'float'), jitterStrength.mul(near));

  /**
   * 🔦 **How much fire falls on this corner**, 0–1, straight off the mesher
   * — and deliberately NOT faded out with distance the way `ao` and `jitter`
   * are. **A lit window across the valley is exactly the thing you want to still
   * see**, and in a landnám winter it is the only light there is.
   */
  const litness = attribute('light', 'float').mul(lightStrength);

  /* Textures off leaves a mid grey, so the world stays lit and readable and the
     only thing removed is the tile. */
  const color = mix(vec3(0.55, 0.55, 0.55), tile.rgb, textureStrength)
    .mul(ao)
    .mul(jitter);

  /**
   * 🔥 **The tile's own colour, warmed and scaled by how much fire reaches it.**
   *
   * ⚠️ **The surface's own colour, not a flat orange wash.** A lit turf wall
   * has to go on looking like turf; multiplying its tile by firelight is what
   * keeps the texture readable, where a plain glow laid over the top would
   * bleach it.
   *
   * 🔴 **The tint is the flame's own bottom end** (`flame.mjs`'s ramp), so a
   * torch, a hearth and the light they throw are one fire rather than three
   * near-misses.
   *
   * ⚠️ **`ao` is kept and `jitter` is not.** Firelight is blocked by the same
   * corners the sun is, so ambient occlusion still applies — but the value
   * jitter is per-block variety, and doubling it in here would make a lit block
   * disagree with its own unlit self.
   */
  const glow = color
    .mul(ao)
    .mul(vec3(1.25, 0.86, 0.52))
    .mul(litness);

  /* 🍃 ⚠️ The alpha deliberately IGNORES `textureStrength`. Switching textures
     off in Dev Tools is meant to remove the picture, not to fill the crowns back
     in — and a leaf that turns solid when you are debugging is a leaf you cannot
     debug. Shape stays; only colour goes. */
  return { color, alpha: tile.a, glow };
}

applyBlockNodes(null);

/**
 * The outline around the block you are looking at (T91).
 *
 * ⚠️ **It matters more here than it does in Minecraft.** Our terrain is a single
 * flat colour per habitat class, so a hillside of birkiskógur is one unbroken
 * field of the same green with 1 m terrace steps in it — telling one block from
 * its neighbour by eye is genuinely hard. Until T84 gives the ground texture and
 * variation, this outline is the only thing that says *which* block a click will
 * hit.
 *
 * Built once and moved, never rebuilt: it is drawn every frame, and allocating
 * geometry sixty times a second for twelve lines would be absurd.
 *
 * ⚠️ **Slightly larger than a block (1.004).** Coincident faces z-fight, which
 * looks like the outline flickering in and out — and that reads as a broken
 * feature rather than a depth-precision artefact. 2 mm out on each side is
 * invisible at 1 m per block and comfortably clear of the ground plane.
 *
 * ⚠️ **Depth-tested on purpose.** The edges on the far side of the block are
 * hidden by the block itself, so the outline wraps what you can see rather than
 * floating in front of it — which is what makes it read as *on* the world.
 *
 * ⚠️ Browsers ignore line width in both WebGPU and WebGL, so this is one pixel
 * whatever we ask for. That happens to match Minecraft; making it thicker means
 * a different technique entirely and is not worth it until somebody says it is
 * too faint to see.
 */
/**
 * ⛏️ The stopwatch behind hold-to-break. Rules and tests in `breaking.ts`.
 *
 * ⚠️ **Released on `mouseup` ANYWHERE, and on losing the pointer lock or the
 * window.** A button that is released while the browser is not looking would
 * otherwise leave the player breaking a block forever — and the symptom, ground
 * dissolving under a crosshair nobody is holding, reads as a server fault.
 */
const breaking = new Breaking();
document.addEventListener('mouseup', (e) => {
  if (e.button === 0) breaking.stop();
});
window.addEventListener('blur', () => breaking.stop());
document.addEventListener('pointerlockchange', () => {
  if (!document.pointerLockElement) breaking.stop();
});

const highlight = new LineSegments(
  new EdgesGeometry(new BoxGeometry(1.004, 1.004, 1.004)),
  /* Black at 45%, the Minecraft convention, and it reads well against the
     mid-green ground this world is mostly made of. ⚠️ Revisit when T84 gives
     the sky a colour — against today's black sky an outline on the horizon has
     nothing to contrast with. */
  new LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.45 }),
);
highlight.visible = false;
scene.add(highlight);

/**
 * ⛏️ **The block being broken, made impossible to miss (T110).**
 *
 * 🎯 **haugur, 2026-08-17, one minute after it went to dev:** *"i cannot break
 * blocks on dev."* **He could.** He clicked, as he always had, and breaking had
 * become a hold — and **the only feedback was the outline closing in, which is a
 * ONE PIXEL line on ground that was almost black at the time.** A working
 * mechanic with invisible feedback is a broken feature, and it took him under a
 * minute to find.
 *
 * ## Why a pale shape closing in, rather than the block actually sinking
 *
 * The design agent's proposal was that the block **sinks**, because at fifteen
 * pixels the only thing left of a block is its outline. **That is right, and it
 * cannot be done cheaply here:** the block is part of a chunk mesh, so making it
 * move means either re-meshing the chunk every frame, or writing air into the
 * client's own copy and re-meshing twice per attempt.
 *
 * 🔴 **The second is client-side PREDICTION, which this client deliberately does
 * not do** — `applyEdit` is only ever called from a server `block_update`. Ten
 * seconds of holding, a hidden block, and then a rejected edit would leave a
 * hole that is not there, and *that* fault looks like the server losing your
 * work. **Not worth it for an animation.**
 *
 * ✅ **So: a solid pale box that closes in on the block and firms up as it
 * goes.** It changes the outline, which was the whole of the argument, and it
 * reads as the block being crushed inward.
 *
 * ⚠️ **`depthTest: false` is load-bearing, not laziness.** Once the box is
 * smaller than the block it is *inside* it, and a depth-tested shape inside
 * solid geometry is invisible — which is precisely the fault being fixed.
 *
 * 🎨 **Pale, not dark**, and that is the design language's own finding (V34):
 * our ground runs from near-black undersides to glacier white, and a dark mark
 * disappears at night. A pale one keeps its contrast against everything the
 * world can be.
 */
/**
 * 🖐️ **Your own hand, in the bottom-right, swinging when you work (T150).**
 *
 * 🎯 **haugur:** *"they have a visible hand that moves when you chop"*, and
 * *"32 sleeve"* — undyed Icelandic wool rather than a bare summer forearm.
 *
 * ⚠️ **Added to the CAMERA, not to the scene.** It has to travel with the eye
 * exactly, and parenting is the only way that stays true through every movement
 * — including the ones nothing else here knows about, like the zoom-out.
 */
const hand = new Hand();
/**
 * 📷 **Decode the tool photographs so the hand can wear them** — 🎯 haugur,
 * 2026-09-04: *"the new images for axe and shovel etc etc, they have just been
 * put in as images on menus, not in the hud itself."*
 *
 * ⚠️ **Nothing waits on this.** Until a photograph has decoded the hand holds
 * the drawn triangles it has always held; when one lands the cached drawing for
 * that item is dropped so the next selection picks the photograph up.
 */
void preloadHeldPhotos().then(() => {
  /* 🔴 And ASK THE HAND AGAIN. Decoding the pictures is only half of it: the
     hand had already cached the drawing it built on the first frame. */
  hand.refresh();
});
camera.add(hand.object);
/* ⚠️ A camera is not in the scene graph by default in three, so a child of it
   is never drawn. Adding the camera itself is what makes the hand appear. */
scene.add(camera);

/**
 * ⛏️ **The cracks that spread across a block as you break it.**
 *
 * 🎯 **haugur, 2026-09-01:** *"a white box appears and gets smaller and smaller
 * until the block breaks… cracks appear on block and stay until block is broken,
 * i like that."*
 *
 * 🔴 **THIS REPLACED THE WHITE BOX**, which was T110's placeholder — a shrinking
 * pale cube, chosen because it was cheap and visible, and it never looked like
 * damage.
 *
 * 🔴 **Drawn triangles rather than a texture, and the reason is an outage.**
 * The obvious way to copy Minecraft is a textured overlay, and putting a texture
 * on a material is what turned the whole game black on 2026-09-01. This wears
 * the same `MeshBasicMaterial` setup the old white break-box used, so **nothing
 * new has to compile.** ⚠️ They were LINES until 2026-09-02: haugur asked for
 * them thicker, and `linewidth` is ignored by everything but a *new* material —
 * so the width went into the geometry instead. ⚠️ A real crack texture
 * would look better; **a worse picture that renders beats a better one that does
 * not**, and this can be revisited on a machine that can check a shader.
 *
 * ⚠️ **`depthTest: false`, like the highlight** — a crack on the far side of the
 * block should still show, because the damage is a property of the block rather
 * than of the face you can see.
 */
const cracks = new Mesh(
  new BufferGeometry().setAttribute('position', new BufferAttribute(crackGeometry(), 3)),
  /* 🔴 The EXACT material setup the old white break-box used and haugur saw
     working for weeks — transparent, no depth test, no map. Nothing here has to
     compile for the first time. */
  new MeshBasicMaterial({
    /* Dark, and a little softer than the highlight's outline so the two read as
       different things when both are on the same block. */
    color: 0x120e0a,
    transparent: true,
    opacity: 0.72,
    depthTest: false,
    depthWrite: false,
    /* ⚠️ A crack belongs to the BLOCK, not to one side of it, so a ribbon seen
       from behind must still draw. */
    side: DoubleSide,
  }),
);
cracks.visible = false;
/* Drawn after everything else, since it is deliberately not depth-tested. */
cracks.renderOrder = 999;
scene.add(cracks);

/* 👻 The storehouse's plot shadow — the cracks' material, a slab on the ground.
   D109: "place a grey shadow on plot, turns red if plot is not empty." */
const burShadow = new BurShadow();
scene.add(burShadow.mesh);

/**
 * 🧍 **Everybody else** — T173, protocol 26.
 *
 * 🎯 **haugur:** *"if not people would only see block appearing but no one
 * building them."* ⬜ **The body is a stand-in** until the photographed model
 * he chose has been drawn and looked at.
 */
const otherPlayers = new OtherPlayers();
/* 🙌 T261: where a sheep for somebody else's arms comes from. 🔴 **The flock's
   OWN template**, so a carried sheep is the animal you picked up rather than a
   second model that drifts away from it. ⚠️ It answers `null` until the model
   has loaded, and `players.ts` asks again every beat. */
otherPlayers.setSheepLender(() => flock.carriedBody());
scene.add(otherPlayers.group);

/**
 * 🐛 **Reporting a bug from inside the game** — T174.
 *
 * 🔴 **The errors are watched from the very first line**, because the error
 * worth reporting happened before the player decided to report it.
 */
watchErrors();
const bugBox = new BugBox(
  httpBase,
  () => ({
    at: controls ? { ...controls.state.position } : null,
    gameTime: gameTimeLabel(),
    protocol: PROTOCOL_VERSION,
  }),
  /**
   * ⏱️ **How the game was running** — T186. Read when the box OPENS, with the
   * picture, not when it sends. See `BugBox.perf` for why that distinction is
   * the whole value of the field.
   */
  () => {
    const f = frames.report();
    const w = chunkWaits.report();
    return {
      fps: f.fps,
      worstFrameMs: f.worstMs,
      hitches: f.hitches,
      /**
       * 🔴 **The half-minute, and it goes ONLY here — not to the HUD.**
       *
       * ⚠️ **The HUD wants NOW; a bug report wants WHAT JUST HAPPENED.** Putting
       * a thirty-second worst on screen would leave a stall you have already
       * walked away from sitting there, which is the exact argument `perf.ts`
       * makes for keeping its window at one second.
       */
      rememberedWorstMs: f.rememberedWorstMs,
      rememberedHitches: f.rememberedHitches,
      viewRadius,
      pending: pending.length + inFlight,
      /* ⚠️ Omitted rather than sent as zero when nothing has arrived yet — the
         report leaves the whole row out, which is honest. A *0 ms a chunk* row
         would say the world was instant in the one report filed because it
         was not. */
      ...(w.samples === 0 ? {} : { chunkWaitMs: w.medianMs, chunkWorstMs: w.worstMs }),
      /* 🔴 One mesh is one draw call, so this is the closest honest number the
         client has to *how much is the GPU being asked to do*. ⚠️ It is a
         COUNT, not a measurement — nothing here has timed a draw. */
      meshes: meshes.size,
    };
  },
  /* 🧍 Read at the moment of sending, not captured now: the credential arrives
     with the `welcome`, and this line runs before the socket is even open. */
  () => storedGameSession(),
  (open) => {
    /* 🖱️ The mouse comes back to the player, exactly as the panels do — you
       cannot type in a box while the pointer is locked to the world. */
    if (open) handMouseTo();
  },
);

/**
 * 🎁 **Whether this player is an admin**, from the welcome — protocol 47.
 * ⚠️ It only decides whether `T` opens the box; the server checks again.
 */
let iAmAdmin = false;

/** 🎁 The admin's give box — `givebox.ts`. */
const giveBox = new GiveBox(
  (item, count) => connection?.sendGive(item, count),
  (text) => say(text),
  {
    placeholder: t('give.placeholder'),
    unknown: (name) => t('give.notice.unknownItem', { name }),
    badCount: (max) => t('give.notice.badCount', { max }),
    asked: (count, item) => t('give.notice.asked', { count, item }),
  },
  (open) => {
    if (open) handMouseTo();
  },
);

/**
 * 📷 **Set when somebody asks to report a bug** — T174.
 *
 * 🔴 **The screen can only be grabbed IMMEDIATELY AFTER A FRAME IS DRAWN.**
 * A WebGPU canvas is not kept between frames, so `toDataURL` at any other
 * moment returns an empty picture — **which looks exactly like the reporter
 * being broken.** So the key raises this flag and the render loop takes the
 * picture at the one moment it exists.
 */
let wantBugShot = false;

/**
 * 🕰️ **What the world clock says, in words** — for a bug report.
 *
 * ⚠️ **Day and hour, not a timestamp.** *"Day 431, 02:55"* is what tells
 * somebody a screen was dark because it was NIGHT rather than because the sky
 * broke — and that mistake has been made here before.
 */
function gameTimeLabel(): string {
  const t = worldClock.time(performance.now());
  if (t === null) return 'unknown';
  const two = (n: number): string => String(n).padStart(2, '0');
  return `day ${String(t.absoluteDay)}, ${two(t.hour)}:${two(t.minute)}`;
}

const chunks = new Map<string, DecodedChunkFrame>();
const meshes = new Map<string, Mesh>();

/**
 * World coordinates are ISN93 metres — around 355,000 and 399,000. Float32,
 * which is what GPUs use, has about 7 significant digits, so geometry placed at
 * those coordinates would jitter visibly as the camera moves. Everything is
 * rendered relative to an origin near the player; only this carries the full
 * precision.
 */
let renderOrigin = new Vector3();

/** Block at a world coordinate, or 0 where nothing has loaded. */
/**
 * 🌊 **Has the chunk holding this block actually arrived?** — T61.
 *
 * 🔴 **{@link blockAt} answers AIR for a chunk that has not**, which is fine for
 * a readout and wrong for anything that must not flicker. **Air and *not yet
 * known* are different answers**, and this is the only way to tell them apart.
 */
function chunkLoadedAt(x: number, y: number, z: number): boolean {
  return chunks.has(chunkKey(worldToChunk(x, y, z)));
}

function blockAt(x: number, y: number, z: number): number {
  const coord = worldToChunk(x, y, z);
  const chunk = chunks.get(chunkKey(coord));
  if (!chunk) return 0;
  const origin = chunkOrigin(coord);
  const lx = x - origin.x;
  const ly = y - origin.y;
  const lz = z - origin.z;
  return chunk.blocks[lx + CHUNK_SIZE * (lz + CHUNK_SIZE * ly)] ?? 0;
}

/**
 * 🏠 Which rung of D64's shelter ladder the player is standing on (T132).
 *
 * ⚠️ **A READOUT, not the truth.** T131 charges exposure on the server, which
 * owns the world; this is the arrangement `stepVitals` already has, where the
 * client runs the shared function between the server's corrections. Two things
 * can make this row differ, both harmless for a diagnostic and both
 * disqualifying for a drain: {@link blockAt} answers **air for a chunk that has
 * not arrived yet**, so a roof still streaming in reads as open sky for a
 * moment — and in the flat debug view (T49) it describes terrain that does not
 * exist.
 *
 * ⚠️ **Recomputed when your head moves to a different block, when the wind
 * turns, or when a chunk is rebuilt.** The escape search inside is thousands of
 * lookups — running it every frame would be sixty times the work for the same
 * word on screen.
 *
 * 🔴 **The third of those was added with the hearth rung on 2026-08-31, and
 * without it this memo would have been WRONG rather than merely stale.** The
 * sentence above used to read *"the answer depends on nothing else"*, and once
 * a fire could turn the cold off that stopped being true: **a hearth burning
 * out changes the answer while your head and the wind both stand still**, so
 * the readout would have kept saying *Inni við eld* over a heap of cold ashes
 * until you walked to another block. ⚠️ **A memo is a claim about what the
 * answer depends on**, and every new input has to be added to it or removed
 * from the key.
 */
let shelterMemo: { key: string; rung: ShelterRung } | null = null;

/**
 * 🏠 Which rung you are on — memoised, and now asked by TWO things.
 *
 * ⚠️ **The memo holds the RUNG rather than the label**, because the rain wants
 * the answer and not the sentence. 🔴 **One computation, two readers** — writing
 * a second shelter check for the rain is exactly what `HANDOFF.md` warned
 * against when rain was being planned.
 */
function shelterRung(at: Vector3, windFrom: number | null): ShelterRung {
  const head = headBlock(at);
  const key = `${head.x},${head.y},${head.z},${windFrom ?? -1}`;
  if (shelterMemo?.key === key) return shelterMemo.rung;

  /* 🔥 The top rung, answered since 2026-08-31. The client's chunks are already
     in memory, so this is the same free lookup `shelters` is. */
  const rung = shelterAt({
    at,
    windFrom,
    shelters: (x, y, z) => sheltersFromWeather(blockAt(x, y, z)),
    fireAt: (x, y, z) => warmsFromWeather(blockAt(x, y, z)),
  });
  shelterMemo = { key, rung };
  return rung;
}

function shelterReading(at: Vector3, windFrom: number | null): string {
  return tAnyKey(`hud.shelter.${shelterRung(at, windFrom)}`);
}

/**
 * 🔴 **Take EVERYTHING a chunk put in the scene back out again.**
 *
 * ⚠️ **A chunk is six objects, not one.** The terrain mesh, the flames, the
 * torch posts, the driftwood heaps, the storehouse models and the smoke — all
 * under the same key, all with the same lifetime. 🔥 A flame left behind when
 * its chunk is rebuilt burns in mid-air; 🏠 **a storehouse left behind when the
 * world is thrown away FOLLOWS THE PLAYER.**
 *
 * 🔴 **THIS EXISTS BECAUSE THE LIST WAS WRITTEN OUT THREE TIMES AND TWO OF THEM
 * ONLY REMOVED THE TERRAIN.** Rebuilding a chunk removed all six;
 * `unloadFarChunks` and `discardWorld` removed one. 🎯 **haugur, the day the
 * world became Iceland:** *"whereever i went my skemma and stuff followd
 * sometimes in the sky sometimes underground."*
 *
 * ⚠️ **The teleport is what made it visible, not what broke it.** A left-behind
 * object keeps the position it was given, which is `origin − renderOrigin`;
 * re-anchoring `renderOrigin` on a jump silently re-reads that offset against
 * the new place, so the houses arrive beside you. Their **y is absolute** —
 * `renderOrigin.y` is always 0 — so they hang in the sky where the new ground
 * is lower and sink underground where it is higher. **Exactly the two symptoms
 * he described.**
 *
 * 🔴 **In a 1,400 m world nobody could walk far enough for `unloadFarChunks` to
 * show it.** The bug is older than the big world; the big world only made it
 * reachable. *A second way in that forgets what the first one does* is recorded
 * five times in this repo — this was the sixth and seventh.
 */
function forgetChunkObjects(key: string): void {
  const mesh = meshes.get(key);
  if (mesh) {
    scene.remove(mesh);
    mesh.geometry.dispose();
    meshes.delete(key);
  }

  /* 🌊 The water of this chunk (T158). ⚠️ Disposed with the ground and by the
     same key — a sea left behind when its chunk is rebuilt would hang in the
     air exactly as a forgotten torch post would, and it is transparent, so it
     would be the hardest one of these to ever notice. */
  const water = waterMeshes.get(key);
  if (water) {
    scene.remove(water);
    water.geometry.dispose();
    waterMeshes.delete(key);
  }

  const flame = flames.get(key);
  if (flame) {
    scene.remove(flame);
    flame.geometry.dispose();
    flames.delete(key);
  }

  const posts = torchPosts.get(key);
  if (posts) {
    scene.remove(posts);
    posts.geometry.dispose();
    torchPosts.delete(key);
  }

  const drift = driftMeshes.get(key);
  if (drift) {
    scene.remove(drift);
    drift.geometry.dispose();
    driftMeshes.delete(key);
  }

  /* 🏠 Nothing to dispose: the copies share the one template's geometry and
     material, and those live as long as the page. */
  const bur = burGroups.get(key);
  if (bur) {
    scene.remove(bur);
    burGroups.delete(key);
  }
  /* 🛏️ D133 — the beds, the same way: shared template, nothing to dispose. */
  const rum = rumGroups.get(key);
  if (rum) {
    scene.remove(rum);
    rumGroups.delete(key);
  }
  /* 🧵 D136 — the looms and the stones on the shore: shared templates; only
     the warp and cloth are our own, and those are cached per ell, never per loom. */
  const loom = loomGroups.get(key);
  if (loom) {
    scene.remove(loom);
    loomGroups.delete(key);
  }
  /* 🏠 D135 — the pit houses: their walls are this chunk's own geometry. */
  const pit = pitGroups.get(key);
  if (pit) {
    scene.remove(pit);
    disposePitHouseGroup(pit);
    pitGroups.delete(key);
  }

  const smoke = smokeMeshes.get(key);
  if (smoke) {
    scene.remove(smoke);
    smoke.geometry.dispose();
    smokeMeshes.delete(key);
  }
}

function buildMesh(frame: DecodedChunkFrame): void {
  const key = chunkKey(frame.coord);

  /* 🔥 The world changed, so the shelter readout may have too — a hearth going
     out, or a wall being knocked through. ⚠️ Cleared rather than recomputed:
     the next frame that wants it will pay for it, and chunks arrive in bursts
     while streaming. See `shelterMemo` for why this is a correctness fix and
     not a tidy-up. */
  shelterMemo = null;

  forgetChunkObjects(key);

  if (frame.empty) return;

  const origin = chunkOrigin(frame.coord);
  /* Consult neighbouring chunks so boundary faces are drawn only where truly
     exposed — otherwise every chunk gets a wall of faces around it. */
  const neighbourAt = (x: number, y: number, z: number): number =>
    blockAt(origin.x + x, origin.y + y, origin.z + z);

  const mesh = meshChunk(frame.blocks, neighbourAt, origin, nearbyEmitters(frame.coord));
  /**
   * 🌊 **BOTH buckets, T158 — and this line nearly shipped asking only one.**
   *
   * 🔴 **A chunk of open sea has NO ground faces in it.** Its bed is 32 m down
   * in the chunk below, and every one of its own cells is water; the ground
   * bucket is empty and the water bucket is full. Returning on the ground's
   * count alone would have drawn **no sea at all in deep water** — while
   * working perfectly everywhere a beach is in shot, which is everywhere this
   * would have been looked at.
   */
  if (mesh.faceCount === 0 && mesh.water.faceCount === 0) return;

  const chunkPosition = new Vector3(
    origin.x - renderOrigin.x,
    origin.y - renderOrigin.y,
    origin.z - renderOrigin.z,
  );

  /* ⚠️ Only where there is ground. A pure-water chunk gets no terrain object —
     see the note on the early return above. */
  if (mesh.faceCount > 0) {
    const object = new Mesh(geometryOf(mesh), material);
    object.position.copy(chunkPosition);
    scene.add(object);
    meshes.set(key, object);
  }

  /**
   * 🌊 **The water of this chunk, as a second object beside the ground** (T158).
   *
   * ⚠️ **Only where there IS water.** Most of Iceland is dry, and a mesh with no
   * faces in it is a draw call, an allocation and a disposal for nothing — on
   * every chunk in the country, while T218 is already about this path costing
   * too much.
   *
   * 🔴 **`renderOrder` is what puts it after the ground.** Three sorts the
   * renderer by default and would usually get this right on its own, but *"gets
   * it right on its own"* is not a thing to leave a transparent surface holding:
   * drawn first, the sea would blend against the sky and the seabed behind it
   * would never be drawn at all.
   */
  if (mesh.water.faceCount > 0) {
    const waterObject = new Mesh(geometryOf(mesh.water), waterMaterial);
    waterObject.position.copy(chunkPosition);
    waterObject.renderOrder = WATER_RENDER_ORDER;
    scene.add(waterObject);
    waterMeshes.set(key, waterObject);
  }

  /**
   * 🐟 **The fish of this chunk — T220.**
   *
   * 🔴 **NOT inside the water-mesh branch above, and that is the whole point.**
   * It was, and 🎯 **haugur reported the result three times running**: *"they
   * disapear when i get close"*, *"sometimes i find a few that stay around"*,
   * *"still looks like they get lost in the world build."*
   *
   * ⚠️ **Water touching water draws NOTHING.** A chunk under the surface has
   * every face hidden the moment its neighbours arrive — so its water mesh count
   * falls to zero, and the fish went with it. **The closer he walked, the more
   * neighbours loaded, and the more fish vanished.** The ones that *"stay
   * around"* were the chunks holding the surface itself, which always keep their
   * top faces.
   *
   * 👉 **Whether a fish exists has nothing to do with whether the water it is in
   * happens to draw a face.** Two different questions; one `if` was answering
   * both.
   *
   * ⚠️ **It costs a scan of every chunk, dry ones included** — one pass down
   * each of 1,024 columns, which stops at the first block on land. Measured
   * against the mesh build beside it, it does not show.
   */
  /* 🐟 **The chunk no longer looks for fish.** T228: the server keeps them and
     sends them (protocol 30), so meshing a chunk has nothing to say about what
     lives in its water. ⚠️ **One pass down all 1,024 columns of every chunk,
     dry ones included, went with it** — a saving on the thread T218 is open
     about, taken as a side effect rather than as the point. */

  /**
   * 🔥 **Find what is burning.**
   *
   * ⚠️ **Scanned here rather than inside the mesher**, deliberately. The mesher
   * is the hot loop and has its own tests; this is one pass over an array the
   * caller already holds, and it costs nothing next to meshing. **The smaller
   * change is the right one until it is measured otherwise.**
   */
  const lit = LIT_HEARTH;
  if (lit !== undefined) {
    const burning: number[] = [];
    for (let i = 0; i < frame.blocks.length; i++) {
      /* eslint-disable-next-line security/detect-object-injection --
         A loop counter reading a Uint16Array. Same reasoning as textures.ts. */
      if (frame.blocks[i] !== lit) continue;
      /* Chunk-local, in the same order the chunk array is laid out. */
      burning.push(
        i % CHUNK_SIZE,
        Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
        Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
      );
    }
    /**
     * 🔦 **The torches' fires go into the SAME mesh as the hearths'.**
     *
     * ✅ One object, one draw call, one material — and, more importantly, **one
     * flame**. 🎯 haugur asked for the torch to move *"as camfire does"*, and
     * the way to guarantee that is not to draw a second flame carefully to
     * match: it is to draw no second flame at all.
     */
    /**
     * 🔦 **The torches, each asked whether the wall it hangs on is a house.**
     *
     * 🔴 **Issue #532: a torch on a storehouse stood 46 cm off it and cut
     * through the door.** A torch is nailed to the **block grid**; a house is a
     * photographed **model drawn over that grid**, its gable 0.43 m inside the
     * box's own face. **The two surfaces were never in the same place.**
     *
     * ⚠️ **Asked here rather than inside `torchesIn`**, because it needs the
     * WORLD and `torchesIn` is handed one chunk's array. A house is 9 m long
     * and a chunk is 32, so **a torch and the door that explains it land in
     * different chunks often** — `blockAt` crosses that boundary and a chunk's
     * own block list cannot.
     *
     * ✅ **Cell coordinates go to world before asking**: `origin` is the
     * chunk's corner. ⚠️ **A chunk-local cell and a world cell are two numbers
     * that look identical**, which is the same trap the carved board fell into
     * between model units and metres.
     */
    const torches = torchesIn(frame.blocks).map((t) => ({
      ...t,
      wallInset: torchWallInset(
        { x: origin.x + t.x, y: origin.y + t.y, z: origin.z + t.z },
        t.facing,
        blockAt,
      ),
    }));
    const fires: FlameSpec[] = [
      ...Array.from({ length: burning.length / 3 }, (_unused, i) => ({
        cx: (burning[i * 3] ?? 0) + 0.5,
        cy: (burning[i * 3 + 1] ?? 0) + HEARTH_FLAME_BASE,
        cz: (burning[i * 3 + 2] ?? 0) + 0.5,
        height: HEARTH_FLAME_HEIGHT,
        width: HEARTH_FLAME_WIDTH,
      })),
      ...torchFlames(torches),
    ];

    const geometry = buildFlames(fires);
    if (geometry !== null) {
      const flame = new Mesh(geometry, flameMaterial);
      /* Drawn after the world, because it is transparent and adds to what is
         behind it. */
      flame.renderOrder = 1;
      flame.position.copy(chunkPosition);
      scene.add(flame);
      flames.set(key, flame);
    }

    /* 🪵 D128 — the tether pegs are drawn as posts too, and have no flame. */
    /* 🚪 D130 — and the pen gates, in the same mesh and the same wood. */
    const posts = buildTorchMesh(
      [...torches, ...pegsIn(frame.blocks)],
      torchMaterial,
      gatesIn(frame.blocks),
    );
    if (posts) {
      posts.position.copy(chunkPosition);
      scene.add(posts);
      torchPosts.set(key, posts);
    }

    /**
     * 🪵 **The driftwood, drawn beside the terrain like the torches.**
     *
     * 🔴 The mesher skips this block on purpose: a cell is a HEAP of 2–5 loose
     * pieces at their own angles, which a box scaled in Y cannot say. 🎯 haugur:
     * *"4 or 10 cubes in a row on a beach is just funny looking."*
     *
     * ⚠️ **The chunk's WORLD origin is passed in**, because a heap's shape is
     * hashed from where it is in the world. Hashing the chunk-local position
     * would make every chunk's heaps identical.
     */
    const drift = buildDriftMesh(driftwoodIn(frame.blocks, origin), driftMaterial);
    if (drift) {
      drift.position.copy(chunkPosition);
      scene.add(drift);
      driftMeshes.set(key, drift);
    }

    /* 🏠 The storehouse — one model per door in this chunk, D109. The door's
       cell is chunk-local and the footprint is worked out from it, so the group
       sits at the chunk's origin exactly as the driftwood does. */
    const bur = buildBurGroup(storehousesIn(frame.blocks), burModel, mesh.litAt);
    if (bur) {
      bur.position.copy(chunkPosition);
      scene.add(bur);
      burGroups.set(key, bur);
    }
    /* 🛏️ D133 — and every bed head in it. */
    const rum = buildRumGroup(bedsIn(frame.blocks), rumModel, mesh.litAt);
    if (rum) {
      rum.position.copy(chunkPosition);
      scene.add(rum);
      rumGroups.set(key, rum);
    }
    /* 🧵 D136 — every loom anchor in it, and every kljásteinn on its shore. */
    const loom = buildLoomGroup(
      loomsIn(frame.blocks),
      stonesIn(frame.blocks),
      loomModels,
      mesh.litAt,
    );
    if (loom) {
      loom.position.copy(chunkPosition);
      scene.add(loom);
      loomGroups.set(key, loom);
    }
    /* 🏠 D135 — and every pit house whose door is in it. */
    const pit = buildPitHouseGroup(pitHousesIn(frame.blocks), pitModels, mesh.litAt);
    if (pit) {
      pit.position.copy(chunkPosition);
      scene.add(pit);
      pitGroups.set(key, pit);
    }

    /**
     * 💨 **Smoke, from the same two lists the flames were built from.**
     *
     * 🔴 **In SCENE coordinates, and the mesh itself stays at the origin.**
     * `billboarding` writes each puff's own position into the model matrix, so
     * a mesh moved to the chunk would move the smoke twice. ⚠️ `chunkPosition`
     * is already `origin − renderOrigin`, which is exactly the space wanted.
     *
     * ✅ **No new scan.** The burning hearths and the torches are already known
     * by the time this runs — finding them again is the shape of mistake that
     * cost the frame rate on 2026-08-31.
     */
    const smoking: SmokeSource[] = [
      ...Array.from({ length: burning.length / 3 }, (_unused, i) => ({
        x: chunkPosition.x + (burning[i * 3] ?? 0),
        y: chunkPosition.y + (burning[i * 3 + 1] ?? 0),
        z: chunkPosition.z + (burning[i * 3 + 2] ?? 0),
        kind: 'hearth' as const,
      })),
      ...torches.map((t) => ({
        x: chunkPosition.x + t.x,
        y: chunkPosition.y + t.y,
        z: chunkPosition.z + t.z,
        kind: 'torch' as const,
      })),
    ];
    const smoke = buildSmokeMesh(smoking, smokeMaterial);
    if (smoke) {
      scene.add(smoke);
      smokeMeshes.set(key, smoke);
    }
  }
}

/**
 * 🔥 **The number a burning hearth holds in a chunk.**
 *
 * 🔴 **Resolved once, from the state registry, never written as a literal.**
 * `eldstaedi[lit=true]` is 1024 today and the whole point of the registry is
 * that no code should know that.
 */
const LIT_HEARTH: number | undefined = stateIdForKey('eldstaedi[lit=true]');

// ── building: placing and breaking blocks (T52) ─────────────────────────────

/**
 * What a right click puts down, as a block **key**.
 *
 * ⚠️ **A key, never a number** (T80). Móberg is the default because it is what
 * lies under everything here — dig anywhere in Hraunin and it is the first
 * thing you meet, so the starting material is one the world actually gives you.
 *
 * ⚠️ **This is a placeholder for a real building palette (T82).** Of the 29
 * blocks, about 20 are habitat classes and none of them is a *building*
 * material — Landnám has no turf, no driftwood, no thatch. Until that palette
 * exists, the way to choose a material is to find some and pick it up, which is
 * why middle-click exists below.
 */
/**
 * 🧱 **What the selected slot would place, or `null` for an empty hand.**
 *
 * 🔴 **Derived, never stored.** It used to be a variable that started at
 * `moberg` and changed on middle-click, and placing cost nothing. 🎯 haugur,
 * 2026-08-18: *"i also and the item wich slot i have selsected, to be
 * automatically the item im holdi, so that item is used for building."*
 *
 * ⚠️ **The server does not trust this**, and must not — it checks the selected
 * slot itself before moving a block. This exists so the screen can say what you
 * are holding without asking, and so a click with an empty hand can be answered
 * without a round trip.
 */
function heldBlockKey(): string | null {
  const stack = inventory.find((s) => s.slot === selectedSlot);
  if (stack === undefined) return null;
  return itemById(itemIdForKey(stack.itemKey) ?? -1)?.placesBlock ?? null;
}

/**
 * 🪓⛏️🥄 **The tool in the selected slot, or `null` for bare hands (D102).**
 *
 * ⚠️ **The server does not trust this and must not** — it reads the selected
 * slot from its own copy of the pack before it decides anything. This exists so
 * the crack you see grows at the speed the block will actually break at, rather
 * than at a bare-handed speed the server then contradicts.
 *
 * ⚠️ **A worn-out tool is bare hands.** It should never be in a slot — the
 * server deletes it the moment it finishes — but the two sides must agree about
 * what happens if one ever is.
 */
function toolInHand(): Held {
  const stack = inventory.find((s) => s.slot === selectedSlot);
  if (stack === undefined) return null;
  if (isWornOut(stack.itemKey, stack.used ?? 0)) return null;
  return toolOf(stack.itemKey) ?? null;
}

/**
 * 🖐️ **What should be drawn in front of the eye**, or `null` for your own hand.
 *
 * 🎯 **haugur, 2026-09-01:** *"if no tool then hand, if holding a pickaxe, then
 * pickaxe, when holding a block then the block etcetc."*
 *
 * 🔴 **`etcetc` is the whole design, and two earlier versions missed it.** They
 * asked *is it a tool? is it a block?* and fell back to the hand for everything
 * else — so a torch, a berry and a lump of charcoal all showed as a fist.
 *
 * ✅ **This asks the one question the PACK already asks** — `slotContentFor`,
 * the single resolver for *what does this item look like*. Every item is drawn
 * as itself, the hand and the pack cannot disagree, and an item added next year
 * is right without anybody coming back here.
 */
function pictureInHand(): SlotContent | null {
  /* 🙌 T261. **A sheep in both arms is the thing in front of your eye**, and it
     is checked FIRST — before the dev-tools override, because what you are
     carrying is the truth about your hands and the override is a viewing aid. */
  const carried = carriedContent();
  if (carried !== null) return carried;

  /**
   * 🛠️ **A Dev Tools override, so the held item can be LOOKED AT.**
   *
   * 🔴 **Without it there is no way to see one at all.** Dev has no accounts, an
   * anonymous pack lives in server memory, and `shoot.mjs` therefore photographs
   * an empty hand every time — **which is how a held tool that looked like a
   * mallet, and later one that did not render at all, both reached haugur before
   * anybody had seen them.**
   *
   * ⚠️ **It changes only what is DRAWN.** `toolInHand` above is untouched, so
   * break speed, the drop gate and durability all still read the real slot. **A
   * viewing aid must not change the world** — the rule the forced-rain switch
   * already follows.
   */
  const forced = lightStore.get().holdItem;
  if (forced !== null) return slotContentFor({ slot: selectedSlot, itemKey: forced, count: 1 });

  const stack = inventory.find((s) => s.slot === selectedSlot);
  return stack === undefined ? null : slotContentFor(stack);
}

/**
 * 🍇 What eating the thing in hand would do, or `null` if it is not food.
 *
 * ⚠️ **The server does not trust this either**, and must not — it looks at the
 * slot itself. It exists so that the right-click ladder can decide between
 * eating and using a block without a round trip.
 */
function edibleInHand(): EdibleDef | null {
  const stack = inventory.find((s) => s.slot === selectedSlot);
  if (stack === undefined) return null;
  return edibleFor(stack.itemKey) ?? null;
}

/**
 * Would eating it actually move the bar?
 *
 * 🔴 **`false` is a real answer, not a failure** — it is what happens at a full
 * bar, and at 60 % holding raw food that stops at 50. It is also what hands the
 * right-click back to the block underneath, so a fed player can still pick.
 *
 * ⚠️ **Answered from the carried-forward bars**, which are at most a frame old.
 * The server checks the same thing against its own number and wins if they ever
 * disagree; the worst this can be wrong by is one frame of drain.
 */
function wouldEatingHelp(food: EdibleDef): boolean {
  const vitals = clientVitals.now(performance.now());
  /* Before the server has said anything, assume it would help. A refusal
     invented out of a number we do not have yet is worse than a round trip. */
  if (vitals === null) return true;
  return vitals.foodPercent < food.ceilingPercent;
}

/**
 * 🫐 Does the block at this position do something when you right-click it?
 *
 * Today: **lyng bears berries**, and which block that is comes from
 * `tuning.json` rather than from a literal here — so the day kjarr or a berry
 * slab bears them too, this line does not change.
 *
 * ⚠️ **The server decides for real.** This only stops a click that is certainly
 * pointless from becoming a message — it is the last rung of the ladder, so a
 * `false` here is what produces the sentence saying nothing happened.
 */
/* 🗑️ `usableBlock` and `stationAt` were removed on 2026-08-27. They each
   answered half of "what does this click mean" and the ORDER they were called
   in carried the rest — which is why the hearth could be shipped unreachable
   with both of them behaving exactly as written. `interact.ts` now answers the
   whole question in one place, and has tests. */

/**
 * 🔨 **Is the thing under the crosshair a crafting station?** — T130 slice 3.
 *
 * ⚠️ **Derived from the recipes, not from a list of block keys.** The day a
 * second station exists — a stove, a loom — it appears here by existing in
 * `recipes.json`, and nobody has to remember to add it. 🔴 **The client
 * opening a screen is not permission to craft**: the server re-reads the block
 * at those coordinates and decides for itself (`craft` in `server/index.ts`).
 */
const STATION_KEYS: ReadonlySet<string> = new Set([
  ...RECIPES.map((r) => r.station).filter((s): s is string => s !== null),
  /* 🔨 **The workshop borrows the bench's list** — D110. No recipe names the
     smiðja, so it would never appear here on its own; `STATION_ALIAS` is the
     single place that says which blocks stand in for which. */
  ...STATION_ALIAS.keys(),
]);

/**
 * 🏘️ **Where a house will actually stand, and which way it will face** — D110.
 *
 * 🎯 **haugur, having placed three by hand and seen the gaps:** *"Yes i think
 * they should snap together, dont you think, it looks more authentic."*
 *
 * 🔴 **ONE answer, asked by the plot shadow AND by the click.** The shadow's
 * entire job is to promise where the building goes; working the two out
 * separately is how that promise gets broken, and it is the fault this repo
 * names oftener than any other.
 *
 * ⚠️ **A snap overrides the player's yaw.** Aim within {@link SNAP_REACH} of a
 * flush slot beside an existing house and you get that house's facing, because
 * **a row whose gables face different ways is not a row.** Further away, your
 * own aim wins exactly as before.
 */
function housePlacement(want: WorldPosition): {
  readonly cell: WorldPosition;
  readonly facing: BurFacing;
} {
  const snapped = snapToNeighbour(want, blockAt);
  if (snapped) return { cell: snapped.cell, facing: snapped.facing };
  return { cell: want, facing: burFacingToward(controls?.state.yaw ?? 0) };
}

/** The block's Icelandic name, or "empty-handed". Straight to the readout. */
/**
 * 🙌 **What a carried animal looks like in the pack and in the hand** — T261.
 *
 * 🎯 **haugur, having carried one:** *"there should be a sheep block in on hand
 * inventory to show you that you have one, also Holding: in Hud says im empty
 * hande even tho im carrying a sheep."*
 *
 * 🔴 **IT IS NOT AN ITEM AND IT NEVER ENTERS THE PACK.** A sheep is a thing the
 * server keeps (D117) with an id, a position and a mind; **an item stack has
 * none of those, and one that could be dragged into a chest would be a sheep
 * you could store, stack and duplicate.** ⚠️ **So this is a PICTURE of what is
 * in your arms, borrowing the slot the pack already knows how to draw.**
 *
 * ✅ **`kind` has a photograph in `item-icons.json`**, rendered from the game's
 * own model, so the on-hand row and the hand both draw it through the paths
 * they already use. **Nothing new renders and no new material compiles** — the
 * trap that blacked out dev and staging once.
 */
function carriedContent(): SlotContent | null {
  if (carryingKind === null) return null;
  return { item: carryingKind, photoUri: itemIconUri(carryingKind) };
}

function heldBlockName(): string {
  /* 🙌 T261. 🔴 **Your ARMS beat your HAND**, because they are literally full:
     you cannot swing or place while carrying a sheep, so reporting the slot
     would be reporting something you cannot use. 🎯 haugur: *"Holding: in HUD
     says im empty hande even tho im carrying a sheep."* */
  if (carryingKind !== null)
    return t(carryingLamb === undefined ? 'hud.holding.sheep' : 'hud.holding.lamb');
  const key = heldBlockKey();
  if (key === null) return t('hud.holding.nothing');
  return blockById(blockIdForKey(key) ?? -1)?.nameIs ?? key;
}

/** What the server last said is in the pack. A copy of its answer, never ours. */
let inventory: readonly Stack[] = [];

/**
 * 🎒 Which on-hand slot the server last said was in hand.
 *
 * ⚠️ **A copy of the server's answer, never our own decision.** It exists only
 * so `Tab` can work out which slot to ask for next; nothing draws from it.
 */
let selectedSlot = 0;

/**
 * Who the server says we are, or `null` for an unidentified connection (T52).
 *
 * ⚠️ Always the server's answer, never our own guess from having a token —
 * the same rule as the flat flag, for the same reason: a credential that
 * silently failed must not be displayed as if it had worked.
 */
let player: { displayName: string } | null = null;

/**
 * The play token from the website, taken from the URL **once, at startup**.
 *
 * ⚠️ Read here rather than inside `openConnection`, because it is single use
 * and the flat switch reconnects. Reading it per connection would spend it on
 * the first attempt and send a stale one on the second.
 */
const playToken = takePlayToken();

/** The server's last refusal, and when to stop showing it. */
let notice: string | null = null;
let noticeUntil = 0;
/** 🍂 T257 — whether the player has been told réttir is on; `null` before the first look. */
let rettirTold: boolean | null = null;

function say(text: string): void {
  notice = text;
  /* Long enough to read, short enough that a stale reason never sits on screen
     looking like it applies to what you just did — `notices.ts`. */
  noticeUntil = performance.now() + noticeMs(text);
}

/**
 * What the player is looking at, if anything solid is within reach.
 *
 * ⚠️ **Aimed from the eye, not the camera.** Zoomed out, the camera is up to
 * 400 m behind the player; aiming from there would target whatever the *camera*
 * can see, which is not what the crosshair is over.
 */
/**
 * 🔱 **The fish the server last told us about**, kept so a thrust can be aimed
 * at one — T221, protocol 31.
 */
let lastCreatures: CreaturesMessage['creatures'] = [];

/**
 * 🔱 **Try to take a fish with the spear. True if the click was a thrust.**
 *
 * 🎯 **haugur chose the verb:** *"Thrust — jab at a fish within reach."*
 *
 * 🔴 **THIS RUNS BEFORE THE BLOCK AIM, AND THE ORDER IS THE FEATURE.** The
 * click handler opens with `aim()` and says *"nothing in reach"* when no BLOCK
 * is found — and **a fish in open water has no block behind it within six
 * metres**, so a thrust placed after that check would be swallowed on exactly
 * the shot the spear exists for.
 *
 * ⚠️ **It refuses unless a spear is actually in hand**, so left-click still
 * breaks blocks with everything else, and a miss falls through to the ordinary
 * gesture rather than eating the click.
 *
 * 🔴 **The id it sends is a PROPOSAL.** The server runs the same
 * `spearTargetFrom` from where IT believes the player is, and refuses a name
 * that does not survive — *the browser never sends a name, it sends a
 * credential.*
 */
/**
 * 🙌 **What is in your own arms, as the SERVER last said** — T261, protocol 37.
 *
 * 🔴 **Never guessed from the key you pressed.** This client does not predict —
 * the same rule it keeps about its own block edits — and an optimistic value
 * here would leave the HUD saying *"put it down"* while the sheep stood in the
 * field, which is the one failure that would read as the sheep being lost.
 */
let carryingKind: string | null = null;
/** 🐣 Protocol 49 — how big the animal in your arms is, if it is a lamb. */
let carryingLamb: number | undefined = undefined;

/**
 * 🙌 **The sheep under the crosshair that you could pick up, or `undefined`.**
 *
 * ⚠️ **The same function the server will re-run** — `carryTargetFrom` lives in
 * `@lofi/shared` precisely so the two ends cannot disagree about what is in
 * reach. **The id this produces is a PROPOSAL.**
 */
function carriableUnderCrosshair(): { id: string; kind: string } | undefined {
  if (!controls) return undefined;
  return carryTargetFrom(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    /* 💀 D125 — the server refuses a dead one; so should the offer. */
    lastCreatures.filter((c) => c.doing !== 'Dead'),
  );
}

/**
 * 🙌 **Pick up, or put down** — T261, D123, protocol 37. The `C` key.
 *
 * 🔴 **ONE KEY FOR BOTH, because your arms can only be in two states.** ⚠️ A
 * second key to put down would be a key that does nothing almost all the time,
 * and this game already has a control list long enough to need its own guide
 * chapter.
 *
 * ⚠️ **Both paths send and wait.** Nothing here changes what is drawn — the
 * server answers with `carrying`, or with `edit_rejected` and a sentence.
 */
function tryCarry(): void {
  if (!connection) return;
  if (carryingKind !== null) {
    connection.sendRelease();
    return;
  }
  const target = carriableUnderCrosshair();
  if (target === undefined) {
    /* ⚠️ Said rather than swallowed. *"I pressed the key and nothing happened"*
       is the least diagnosable report there is — the HUD's own notice row
       exists for exactly this, and the person filing it cannot read the logs. */
    say(t('game.notice.nothingToCarry'));
    return;
  }
  connection.sendCarry(target.id);
}

function tryThrustSpear(): boolean {
  if (!controls || !connection) return false;
  /* ⚠️ The same two lines `heldBlockKey` uses — the client's own copy of the
     pack and the selected slot. **The server does not trust this**, and reads
     its own; this only decides whether the click is worth sending at all. */
  const inHand = inventory.find((stack) => stack.slot === selectedSlot);
  if (inHand?.itemKey !== SPEAR_ITEM) return false;

  const target = spearTargetFrom(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    lastCreatures,
  );
  if (target === undefined) return false;

  connection.sendSpear(target.id);
  return true;
}

/**
 * 🔪 **Strike the sheep under the crosshair, if a knife is in hand** — D125,
 * protocol 38.
 *
 * ⚠️ **Before the block aim, for the spear's reason**: a sheep on open grass
 * has ground behind it, and a click the block path took would dig the turf
 * instead. **A knife with no sheep under the crosshair falls through** to the
 * ordinary click, so nothing is eaten.
 *
 * 🔴 **It sends a proposal and draws nothing.** The server answers with the
 * sheep's `doing` turning to `Panic` or `Dead` and the pack arriving — this
 * client never guesses a wound.
 */
function tryStrike(): boolean {
  if (!controls || !connection) return false;
  const inHand = inventory.find((stack) => stack.slot === selectedSlot);
  if (inHand?.itemKey !== KNIFE_ITEM) return false;
  const target = knifeTargetFrom(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    /* 💀 A sheep already lying on its side is not a target. */
    lastCreatures.filter((c) => c.doing !== 'Dead'),
  );
  if (target === undefined) return false;
  connection.sendStrike(target.id);
  return true;
}

/**
 * ✂️ **Shear the sheep under the crosshair** — D126, protocol 40. The RIGHT
 * button; the left one strikes.
 *
 * 🔴 **A knife, OR AN EMPTY HAND** — plucking by hand is allowed in May and June,
 * and the SERVER knows the month. ⚠️ So an empty hand on a woolly sheep always
 * asks, and in winter the answer is a sentence saying why not — which is how a
 * player learns the fleece loosens in spring.
 *
 * ⚠️ **Only a WOOLLY sheep is taken.** Anything else falls through to the
 * ordinary right-click, so an empty hand next to a shorn sheep still picks up
 * driftwood and a knife still does whatever it did.
 */
/**
 * 🌱 **Feed turf to the sheep under the crosshair** — D130, protocol 44. The
 * RIGHT button with `gras` in hand.
 *
 * 🔴 **Only when a sheep IS under the crosshair**: otherwise turf in hand
 * places turf, as it always has. ⚠️ The server says no to a wild sheep, a
 * sheep outside a pen and any month but December, and says why.
 */
function tryFeed(): boolean {
  if (!controls || !connection) return false;
  const inHand = inventory.find((stack) => stack.slot === selectedSlot);
  if (inHand === undefined || feedGrade(inHand.itemKey) === undefined) return false;
  const target = knifeTargetFrom(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    lastCreatures.filter((c) => c.kind === 'kind' && c.doing !== 'Dead'),
  );
  if (target === undefined) return false;
  connection.sendFeed(target.id);
  return true;
}

function tryShear(): boolean {
  if (!controls || !connection) return false;
  const inHand = inventory.find((stack) => stack.slot === selectedSlot);
  if (inHand !== undefined && inHand.itemKey !== KNIFE_ITEM) return false;
  const target = knifeTargetFrom(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    lastCreatures.filter((c) => c.kind === 'kind' && c.doing !== 'Dead' && c.shorn !== true),
  );
  if (target === undefined) return false;
  connection.sendShear(target.id);
  return true;
}

/**
 * 🪢 **Put a lead on the sheep under the crosshair, or take one off** — D128,
 * protocol 42. The RIGHT button.
 *
 * 🔴 **Before shearing, and the order is the feature**: an empty hand on a sheep
 * you are LEADING lets it go; an empty hand on any other sheep is shearing's
 * (plucking in May and June). ⚠️ A taumur in hand on a sheep always asks, even
 * one tied to a peg — the server takes it off the peg onto your lead.
 */
/**
 * 🎣 **Cast, or pull** — D129, protocol 43. The RIGHT button with a rod in hand.
 *
 * 🔴 **First of the right-button verbs**: with a rod in your hand the button
 * means fishing, whatever is under the crosshair. With your float out it
 * pulls — landing the fish if the float is under, winding in if not. ⚠️ The
 * server aims the cast; this only asks.
 */
function tryRod(): boolean {
  if (!connection) return false;
  const inHand = inventory.find((stack) => stack.slot === selectedSlot);
  if (inHand?.itemKey !== ROD_ITEM) return false;
  const mine = myConnection !== null && floatsNow.some((f) => f.by === myConnection);
  if (mine) connection.sendReel();
  else connection.sendCast();
  return true;
}

function tryLead(): boolean {
  if (!controls || !connection) return false;
  const inHand = inventory.find((stack) => stack.slot === selectedSlot);
  const holdsLead = inHand?.itemKey === LEAD_ITEM;
  if (!holdsLead && inHand !== undefined) return false;
  const target = knifeTargetFrom(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    lastCreatures.filter((c) => c.doing !== 'Dead'),
    LEAD_REACH,
  );
  if (target === undefined) return false;
  const row = lastCreatures.find((c) => c.id === target.id);
  const mine = myConnection !== null && row?.ledBy === myConnection;
  if (holdsLead && !mine) {
    connection.sendLead(target.id);
    return true;
  }
  if (!holdsLead && mine) {
    connection.sendUnlead(target.id);
    return true;
  }
  return false;
}

/**
 * 🪢 **Pair every led or tied sheep with the other end of its rope** — D128.
 *
 * | Its rope is… | The other end |
 * |---|---|
 * | on YOUR lead | your right hand — a little below and in front of your eye |
 * | on somebody else's | their body's hand, from `players.ts` |
 * | tied to a peg | the top of the peg |
 *
 * ⚠️ **A lead whose holder is out of sight draws nothing**, rather than a rope
 * to nowhere.
 */
function drawRopes(): void {
  const ends: RopeEnds[] = [];
  const eye = controls?.eyeWorldPosition() ?? null;
  const look = controls?.lookDirection() ?? null;
  for (const sheep of flock.tethered()) {
    let other: { x: number; y: number; z: number } | null = null;
    if (sheep.ledBy !== null) {
      if (sheep.ledBy === myConnection && eye !== null && look !== null) {
        const flat = Math.hypot(look.x, look.z) || 1;
        other = {
          x: eye.x + (look.x / flat) * 0.35 - (look.z / flat) * 0.25,
          y: eye.y - 0.55,
          z: eye.z + (look.z / flat) * 0.35 + (look.x / flat) * 0.25,
        };
      } else {
        other = otherPlayers.handOf(sheep.ledBy);
      }
    } else if (sheep.tether !== null) {
      other = { x: sheep.tether.x + 0.5, y: sheep.tether.y + 0.45, z: sheep.tether.z + 0.5 };
    }
    if (other === null) continue;
    const span = Math.hypot(other.x - sheep.neck.x, other.z - sheep.neck.z);
    /* The rope is as long as the circle the sheep may roam, plus a little — a
       tied sheep's rope is 5 m, a lead 3 m of slack before it pulls. */
    const length = sheep.ledBy !== null ? 3.5 : 5.5;
    ends.push({
      key: sheep.id,
      a: other,
      b: sheep.neck,
      length: Math.max(length, span),
      /* 🟫 The sheep's own feet — the ground it stands on. */
      floor: sheep.feet,
    });
  }
  /* 🎣 D129 — the floats, and a line from each rod tip to its float. */
  const drawnFloats = floatMeshes.update(floatsNow, renderOrigin, performance.now() / 1000);
  for (const [by, float] of drawnFloats) {
    let tip: { x: number; y: number; z: number } | null = null;
    if (by === myConnection && eye !== null && look !== null) {
      /* Your rod reaches out in front of you and up. */
      tip = { x: eye.x + look.x * 1.3, y: eye.y + 0.35 + look.y * 0.6, z: eye.z + look.z * 1.3 };
    } else {
      const hand = otherPlayers.handOf(by);
      if (hand !== null) tip = { x: hand.x, y: hand.y + 1.2, z: hand.z };
    }
    if (tip === null) continue;
    const span = Math.hypot(float.x - tip.x, float.y - tip.y, float.z - tip.z);
    ends.push({
      key: `line:${by}`,
      a: tip,
      b: float,
      length: span * 1.06,
      floor: float.y,
      thin: true,
    });
  }
  ropes.update(ends, renderOrigin);
}

function aim(): VoxelHit | null {
  if (!controls) return null;
  return raycastVoxels(
    controls.eyeWorldPosition(),
    controls.lookDirection(),
    MAX_REACH,
    /* ⚠️ **Anything that is not air and not a fluid**, which is not the same as
       "solid" — the rule and the reason now live in `isTargetable`, so the
       server can ask the same question when it starts validating how long a
       block took to break. ⚠️ **It was inline here until 2026-08-17**, which is
       why a note claiming `kjarr` could not be broken survived in three
       documents for a week: the code that disproved it was a lambda with no
       name to grep for. */
    (x, y, z) => isTargetable(blockAt(x, y, z)),
  );
}

/**
 * Write a block into a chunk we already hold.
 *
 * ⚠️ Only ever called from a server `block_update`. The client does not predict
 * its own edits — see `protocol.ts`.
 *
 * Returns false when the chunk is not loaded, which is the ordinary case for an
 * edit someone else made on the far side of the world: there is nothing to
 * update, and the block will be there when the chunk next arrives.
 */
function setLocalBlock(x: number, y: number, z: number, id: number): boolean {
  /* 🔦 The chunk's fires may have changed, so its cached list is thrown away
     and worked out again the next time somebody asks. ⚠️ Cleared even when the
     edit turns out to change nothing — a cache that is cleared too often is
     slow, and one that is cleared too rarely is WRONG. */
  emittersByChunk.delete(chunkKey(worldToChunk(x, y, z)));
  const coord = worldToChunk(x, y, z);
  const key = chunkKey(coord);
  const frame = chunks.get(key);
  if (!frame) return false;

  const origin = chunkOrigin(coord);
  const index = x - origin.x + CHUNK_SIZE * (z - origin.z + CHUNK_SIZE * (y - origin.y));
  /* `.set` rather than `blocks[index] = id`: the index is computed from
     coordinates that arrived over the network, which is exactly the pattern
     `security/detect-object-injection` exists to flag. Same reasoning as the
     Map in `blocks.ts` — answer the objection rather than suppress it. */
  frame.blocks.set([id], index);

  /* ⚠️ An empty chunk is skipped by the mesher and sent as a bare header, so a
     block placed into open sky would be stored and never drawn. Replace the
     entry rather than mutating it — `DecodedChunkFrame` is readonly, and the
     flag is what the mesher consults. */
  if (frame.empty && id !== BLOCK_AIR) {
    chunks.set(key, { ...frame, empty: false });
    emittersByChunk.delete(key);
  }

  return true;
}

/**
 * Rebuild the geometry a changed block can affect.
 *
 * Its own chunk always, plus any neighbour it shares a face with — a block on a
 * chunk boundary hides or exposes faces in the chunk next door, and skipping
 * that leaves a hole in the wall you just built.
 */
/** Build each of these chunks once, if it is loaded. */
function remeshChunks(keys: Iterable<string>): void {
  for (const key of keys) {
    const frame = chunks.get(key);
    if (frame) buildMesh(frame);
  }
}

/** Which chunks a change at this cell can alter — its own and, with light reaching MAX_LIGHT, the neighbours. */
function chunksAround(x: number, y: number, z: number): Set<string> {
  const affected = new Set<string>();
  /**
   * 🔦 **MAX_LIGHT blocks, not one — and this had to change when light arrived.**
   *
   * ⚠️ One block was right while a rebuild only ever needed its immediate
   * neighbours for FACE CULLING. **A torch lights up to MAX_LIGHT blocks away**,
   * so placing one three blocks from a boundary changes the chunk next door —
   * and with a one-block reach that chunk would have kept its old darkness until
   * something else happened to rebuild it. 🔴 **A light that stops dead at a
   * seam reads as a rendering bug**, not a lighting one.
   *
   * ⚠️ It still touches at most a handful of chunks: MAX_LIGHT is 15 and a
   * chunk is 32, so the reach crosses one boundary per axis at worst.
   */
  for (const d of [0, MAX_LIGHT, -MAX_LIGHT]) {
    for (const axis of [0, 1, 2]) {
      affected.add(
        chunkKey(
          worldToChunk(
            x + (axis === 0 ? d : 0),
            y + (axis === 1 ? d : 0),
            z + (axis === 2 ? d : 0),
          ),
        ),
      );
    }
  }
  return affected;
}

/**
 * 🔦 **Which fires each chunk holds, worked out once and remembered.**
 *
 * 🔴 **haugur found what this replaced:** *"framrate on dev has dropped to 8,
 * framrate on staging is fine."* The mesher used to go looking for nearby fires
 * itself, by filling a **62³ region — 238,328 cells** — on **every chunk
 * rebuild**, whether anything was burning or not. Dev was streaming 73 chunks;
 * staging was not, which is exactly why only one of them was slow.
 *
 * ✅ **Finding a chunk's fires is one pass over an array we already hold**, and
 * the answer only changes when that chunk does. ⚠️ **So it is cached and
 * invalidated on edit** rather than recomputed per rebuild — and a chunk is
 * rebuilt far more often than it changes, because its neighbours changing is
 * enough.
 *
 * Positions are WORLD coordinates; the mesher wants them chunk-local, so
 * {@link nearbyEmitters} shifts them.
 */
const emittersByChunk = new Map<string, Emitter[]>();

function emittersIn(key: string): readonly Emitter[] {
  const cached = emittersByChunk.get(key);
  if (cached !== undefined) return cached;

  const frame = chunks.get(key);
  if (frame === undefined || frame.empty) {
    emittersByChunk.set(key, []);
    return [];
  }
  const origin = chunkOrigin(frame.coord);
  const found = findEmitters(frame.blocks, CHUNK_SIZE).map((e) => ({
    x: origin.x + e.x,
    y: origin.y + e.y,
    z: origin.z + e.z,
    level: e.level,
  }));
  emittersByChunk.set(key, found);
  return found;
}

/**
 * Every fire in the chunks TOUCHING this one, in this chunk's own coordinates.
 *
 * ⚠️ **The 26 neighbours and no further.** {@link LIGHT_REACH} is 15 and a chunk
 * is 32, so nothing two chunks away can reach in — and the mesher finds this
 * chunk's own fires for itself.
 */
function nearbyEmitters(coord: ChunkCoord): Emitter[] {
  const origin = chunkOrigin(coord);
  const out: Emitter[] = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dy === 0 && dz === 0) continue;
        const key = chunkKey({ cx: coord.cx + dx, cy: coord.cy + dy, cz: coord.cz + dz });
        for (const e of emittersIn(key)) {
          out.push({
            x: e.x - origin.x,
            y: e.y - origin.y,
            z: e.z - origin.z,
            level: e.level,
          });
        }
      }
    }
  }
  return out;
}

/** Apply what the server says has changed. */
/**
 * 🏠 **Which block a hit actually lands on.**
 *
 * The storehouse's doorway is two metres tall and its door block is the
 * bottom one; the cell above it is a *part*, which refuses. 🎯 haugur, taking
 * the first one down: *"cant seem to brean it … yes hitting the bottom of the
 * door breaks it."* So a hit on the part directly above a door counts as a hit
 * on the door — the whole doorway takes it down.
 */
/**
 * 🏠 **Which block a hit actually lands on.**
 *
 * 🎯 **haugur, 2026-09-04:** *"make sure i can focus anywhere on the houses to
 * press o or break it."*
 *
 * 🔴 **The WHOLE building answers now, not just the doorway.** It used to
 * redirect only the cell directly above the door, so a hit anywhere else on a
 * 225-cell house said *take it down at the door* — which reads as a building
 * that cannot be broken, and is the same complaint that opening it had.
 *
 * ⚠️ **This is the same resolution the menu uses** ({@link menuTargetFor}), and
 * they are now the same rule rather than two that differ. **The difference used
 * to be deliberate and he has overruled it**, which is the right way round.
 */
function breakTargetFor(at: WorldPosition): { x: number; y: number; z: number; block: number } {
  /* 🏠 D135 — a hit anywhere on a pit house goes to its lower door. */
  const pit = pitBreakNear(at);
  if (pit) return { ...pit, block: blockAt(pit.x, pit.y, pit.z) };
  const door = houseDoorNear(at);
  if (door) return { ...door, block: blockAt(door.x, door.y, door.z) };
  return { x: at.x, y: at.y, z: at.z, block: blockAt(at.x, at.y, at.z) };
}

/**
 * 🚪 **The door for the cell under the crosshair, remembered between frames.**
 *
 * 🔴 **`burDoorFor` runs the footprint derivation backwards and can cost up to
 * 1200 block lookups.** That was fine on a keypress and is NOT fine every frame
 * while you aim at a wall — which is exactly what breaking does.
 *
 * ✅ **Aim barely moves between frames**, so one remembered answer serves nearly
 * all of them. ⚠️ **And the memory is CHECKED, not trusted**: if the building
 * has been taken down since, the door cell is no longer a door and the search
 * runs again. One lookup to be sure beats a stale answer.
 */
let lastDoorFor: { at: string; door: WorldPosition | undefined } | undefined;

/** 🏠 The pit-house door a hit on this cell means — remembered, and checked (`pitaim.ts`). */
const pitBreakNearCached = pitBreakCache();
function pitBreakNear(at: WorldPosition): WorldPosition | undefined {
  return pitBreakNearCached(at, blockAt);
}
function houseDoorNear(at: WorldPosition): WorldPosition | undefined {
  /* 🛏️ D133 — a bed's foot answers for its head: a hit takes the bed down and
     a right-click lies down, from either end. Four lookups, so no memory. */
  if (isRumPart(blockAt(at.x, at.y, at.z))) return rumHeadFor(at, blockAt);
  /* 🧵 D136 — and any of a loom's cells for its anchor, the same way. */
  if (isLoomPart(blockAt(at.x, at.y, at.z))) return loomAnchorFor(at, blockAt);
  const key = `${String(at.x)},${String(at.y)},${String(at.z)}`;
  if (lastDoorFor?.at === key) {
    const had = lastDoorFor.door;
    if (had === undefined) return undefined;
    if (isBur(blockAt(had.x, had.y, had.z))) return had;
  }
  const door = burDoorFor(at, blockAt);
  lastDoorFor = { at: key, door };
  return door;
}

/**
 * 🅾️ **Which cell's MENU you are looking at** — a different question from
 * {@link breakTargetFor}, and the difference is deliberate.
 *
 * 🎯 **haugur, 2026-09-04:** *"lets fix that i can look anywhere on Búr and
 * press o for the menu, now i need to look at the very bottom of the door for
 * o to work."* A storehouse is 225 cells and **one** of them was the menu.
 *
 * | | |
 * |---|---|
 * | **The menu** | the WHOLE building answers — `burDoorFor`, tested in `shared` |
 * | **Breaking** | only the doorway, so a hit on the wall still says *take it down at the door* |
 * | **Right-click** | 🆕 2026-09-28 — **the WHOLE building too**, like `O`: an empty hand opens the menu from any wall. A block in hand still PLACES against a búr or skemma wall, which is how you build against a storehouse. ⚠️ **The smiðja is a station, and a station always opens** (T130) — so to build against a smiðja, aim at the ground beside it |
 *
 * ⚠️ **The search is 1200 lookups at worst and runs on a KEYPRESS, never per
 * frame** — that is why it is here and not in the aiming loop.
 */
function menuTargetFor(at: WorldPosition): { x: number; y: number; z: number } {
  /* 🏠 D135 — a pit house opens only what you clicked: the door, the oven, the
     bench. A bare wall is just a wall, so a torch in hand still hangs on it. */
  if (isPitBlock(blockAt(at.x, at.y, at.z)))
    return pitMenuTarget(at, blockAt) ?? { x: at.x, y: at.y, z: at.z };
  return houseDoorNear(at) ?? { x: at.x, y: at.y, z: at.z };
}

function applyBlockUpdate(edits: readonly BlockChange[]): void {
  const dirty = new Set<string>();
  for (const edit of edits) {
    /* 🔥 A plain block key first, then a STATE key — `eldstaedi[lit=true]`,
       protocol 15. Two lookups rather than one because they are two different
       name spaces, and a block key must never accidentally resolve to a state. */
    const id =
      edit.block === 'air' ? BLOCK_AIR : (blockIdForKey(edit.block) ?? stateIdForKey(edit.block));
    if (id === undefined) {
      /* The server knows a block this build does not. Loud, because it means
         the client is older than the world it is looking at. */
      console.warn(`[world] server sent unknown block "${edit.block}" — is this client stale?`);
      continue;
    }
    if (setLocalBlock(edit.x, edit.y, edit.z, id))
      for (const key of chunksAround(edit.x, edit.y, edit.z)) dirty.add(key);
  }
  /* 🏠 ONE rebuild per chunk per message. 🎯 haugur, placing the first
     storehouse: *"screen frose for some time."* Its 300 cells arrive in one
     message, and rebuilding the chunk after every one of them was up to 300 ×
     six chunk meshes in a single frame. Now each touched chunk is built once. */
  remeshChunks(dirty);
}

/**
 * 🔦 **Which key to send — a plain block, or a torch with a facing.**
 *
 * 🔴 **Only the client knows which FACE you clicked**, and that is the whole
 * reason this exists. `raycastVoxels` gives back the block it hit and the empty
 * cell in front of it, and **the difference between those two IS the face** —
 * `Minecraft/02` §1.2's trick, which is why nothing has to ask the player which
 * way round they want it.
 *
 * ⚠️ **The server does not take this on trust.** It derives the block the
 * torch would hang off and refuses if that face will not hold one. This is a
 * proposal; the world has the last word.
 *
 * ⚠️ **Two ways to arrive at a cell, and they need different answers.**
 * Ordinarily the torch goes into `hit.place` and leans off `hit.block`. But a
 * non-solid block is REPLACED where it stands (the `kjarr` rule), so a torch put
 * where a bush was is standing on whatever the bush was standing on — which is
 * `up`, and never a wall.
 */
function keyToPlace(held: string, hit: VoxelHit, into: WorldPosition): string {
  /* 🏘️ **Any of the three houses** — D110. `HOUSES` is the only list of them,
     so a fourth is a row there and nothing here. */
  if ((HOUSES as readonly string[]).includes(held))
    return stateKey(held, { facing: housePlacement(into).facing });
  /* 🏠 D135 — the pit house: its door faces you, shut and the lower half; the
     server builds every other cell from it. */
  if (held === PIT_DOOR)
    return stateKey(PIT_DOOR, {
      facing: burFacingToward(controls?.state.yaw ?? 0),
      open: false,
      half: 'lower',
    });
  /* 🚪 The pen gate — D130. It faces the way a house door would, toward you,
     so it spans ACROSS your view and you walk straight through it. Placed shut:
     a gate you have just set in a wall holds a sheep. */
  if (held === GATE_BLOCK)
    return stateKey(GATE_BLOCK, { facing: burFacingToward(controls?.state.yaw ?? 0), open: false });
  /* 🥣 The jata — T286. Its board faces you, the way the gate does, so the side
     you placed it from is the side the sheep eat from. Placed empty. */
  if (held === 'jata')
    return stateKey('jata', { facing: burFacingToward(controls?.state.yaw ?? 0), fill: 0 });
  /* 🛏️ D133 — the rúm's head faces you, so the foot runs away from you. */
  if (held === RUM_BLOCK)
    return stateKey(RUM_BLOCK, { facing: burFacingToward(controls?.state.yaw ?? 0) });
  if (held !== TORCH_KEY) return held;
  const facing =
    into.x === hit.place.x && into.y === hit.place.y && into.z === hit.place.z
      ? (torchFacingFrom(hit.block, hit.place) ?? 'up')
      : 'up';
  /* ⚠️ `stateKey`, not a template string. The property order is what decides
     the name, and it is `block-states.json`'s order rather than ours. */
  return stateKey(TORCH_KEY, { facing });
}

/**
 * Ask the server to change a block.
 *
 * ⚠️ **Position is sent first, in the same tick.** The server checks reach
 * against the last position it was told about, and the ordinary update runs at
 * 5 Hz — so without this, building while walking would be refused at random and
 * the refusal would be blamed on the reach limit.
 */
function requestEdit(x: number, y: number, z: number, block: string): void {
  const state = controls?.state;
  if (state) {
    connection.sendPlayerState({
      x: state.position.x,
      y: state.position.y,
      z: state.position.z,
      yaw: state.yaw,
      pitch: state.pitch,
      flying: state.flying,
    });
  }
  connection.sendBlockEdit(x, y, z, block);
}

/**
 * Left click breaks, right click places, middle click picks up the material.
 *
 * ⚠️ Only once the pointer is locked. The first click on the canvas is what
 * requests the lock (`controls.ts`), and demolishing whatever happened to be
 * under the cursor at that moment is not what anybody means by clicking on a
 * window to focus it.
 */
/* ── 🛏️ SLEEP — D133, protocol 50 ───────────────────────────────────────── */

/** The bed you are asleep in, as the SERVER last said — or `null`, awake. */
let asleep: { x: number; y: number; z: number } | null = null;

/**
 * 🛏️ **The screen while you sleep** — dimmed, with one line that says what is
 * standing still and how to get up. 🎯 Not a HUD note: the whole screen, so
 * nobody mistakes sleeping for the game having frozen.
 */
const sleepVeil = document.createElement('div');
sleepVeil.style.cssText =
  'position:fixed;inset:0;display:none;align-items:center;justify-content:center;' +
  'background:rgba(12,10,8,0.78);color:#f2e9da;font:600 20px/1.5 sans-serif;' +
  'text-align:center;padding:0 12%;z-index:40;pointer-events:none';
document.body.appendChild(sleepVeil);

/**
 * 🛏️ **Any key wakes you** — in the CAPTURE phase, so it runs before every other
 * key handler and a sleeping player's key does nothing else. ⚠️ **F8 is let
 * through**: a bug report is the one thing worth doing in your sleep.
 */
window.addEventListener(
  'keydown',
  (e) => {
    if (asleep === null || e.repeat || e.code === 'F8' || isTypingTarget(e.target)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    connection?.sendWake();
  },
  { capture: true },
);
/* 🛏️ A click while asleep does nothing — no breaking or placing in your sleep. */
window.addEventListener(
  'mousedown',
  (e) => {
    if (asleep === null) return;
    e.stopImmediatePropagation();
  },
  { capture: true },
);

document.addEventListener('mousedown', (e) => {
  if (!controls?.pointerLocked) return;
  if (flatWorld) {
    say(t('game.notice.flatView'));
    return;
  }

  /**
   * 🙌 **BOTH ARMS ARE FULL — T261.**
   *
   * 🔴 **This is what makes the picture honest.** The on-hand row shows a sheep
   * over the slot you chose and the HUD says *A sheep — both arms*; **if a click
   * still placed the block underneath, the game would be drawing one thing and
   * doing another.**
   *
   * ⚠️ **It is a JUDGEMENT rather than something haugur asked for**, and it is
   * one line to undo. 👉 **The alternative was a sheep in the slot that you can
   * build straight through**, which is the sort of quiet inconsistency this
   * project keeps paying for.
   *
   * ✅ **It SAYS so** — *"I clicked and nothing happened"* is the least
   * diagnosable report there is.
   */
  if (carryingKind !== null) {
    say(t('game.notice.armsFull'));
    return;
  }

  /* 🔱 **Before the block aim, and it has to be** — see `tryThrustSpear`. A
     fish over open water has nothing solid behind it, and the early return
     below would swallow the very click the spear is for. */
  if (e.button === 0 && tryThrustSpear()) return;
  /* 🔪 The knife, for the same reason — D125. */
  if (e.button === 0 && tryStrike()) return;
  /* ✂️ And shearing on the other button, before the block aim — D126. */
  /* 🪢 D128 — the lead, BEFORE shearing: an empty hand on your own led sheep lets it go. */
  /* 🎣 D129 — the rod, before every other right-button verb. */
  if (e.button === 2 && tryRod()) return;
  if (e.button === 2 && tryLead()) return;
  if (e.button === 2 && tryFeed()) return;
  if (e.button === 2 && tryShear()) return;

  const hit = aim();
  if (!hit) {
    say(t('game.notice.nothingInReach'));
    return;
  }

  if (e.button === 0) {
    /**
     * 🪵 **A block you PICK UP answers the break gesture by being picked up.**
     *
     * 🎯 **haugur, 2026-09-02:** *"yes make left click work too, it works
     * beautifully."* He reached for left-click first, before right-click, and
     * he was right to: **you do not chip driftwood off a beach, you bend down
     * and take it.**
     *
     * 🔴 **Without this, left-click on driftwood does NOTHING FOR EVER** — the
     * block is `unbreakable`, so `breaking` aims at it and never damages it,
     * which is exactly what *"no breaking"* asked for and exactly what made the
     * gesture feel dead. 🎯 *"i left click it with empty hand nothing happens."*
     *
     * ⚠️ **This does NOT care what you are holding, and right-click does.**
     * That asymmetry is deliberate rather than an oversight: right-click has to
     * choose between placing and picking, so an empty hand is what separates
     * them. **Left-click has nothing to choose between** — a block that cannot
     * be broken has no other answer to give — so demanding an empty hand would
     * only mean "nothing happens" again, with an axe in your hand this time.
     */
    const aimedId = blockAt(hit.block.x, hit.block.y, hit.block.z);
    if (isPickedByHand(aimedId)) {
      connection?.sendUseBlock(hit.block.x, hit.block.y, hit.block.z);
      return;
    }

    /* ⛏️ **Breaking is a HOLD, not a click (T110).** 🎯 haugur, 2026-08-17.
       Nothing is sent here — the frame loop counts the time and asks the server
       only when the block has actually given way. */
    breaking.start({
      x: hit.block.x,
      y: hit.block.y,
      z: hit.block.z,
      block: blockAt(hit.block.x, hit.block.y, hit.block.z),
    });
    return;
  }

  if (e.button === 2) {
    /**
     * 🍇 **Right-click is a short ladder now, and the ORDER is the design.**
     *
     * | | |
     * |---|---|
     * | Holding food, and eating would do something | **Eat it** |
     * | Holding a block | **Place it** |
     * | The block in front of you does something | **Use it** — pick berries |
     * | Otherwise | Say why nothing happened |
     *
     * 🔴 **Food first, which is NOT Minecraft's order** — there a chest opens
     * even with bread in hand. Ours cannot afford that: **lyng is 23 % of the
     * ground**, so letting the block win would mean *"I am starving and it will
     * not let me eat"* every time you looked downhill. ⚠️ **Unless eating would
     * do nothing** — a full bar, or raw food below its ceiling — and then it
     * falls through, which is what lets a well-fed player carry berries and
     * still pick more.
     *
     * 🔴 **And BUILDING beats using, which was a real bug caught before it
     * shipped.** The first version had using above placing, and since lyng is a
     * quarter of the ground that would have meant **you cannot build on the
     * heath** — every attempt would have picked a berry instead. Right-click
     * with a block in hand means *place this*, everywhere, and it has to keep
     * meaning that. ✅ **The cost is small and it is the familiar one:** to pick
     * berries, do not be holding a block. An empty hand picks; so does a hand
     * holding berries you have no room for.
     */
    const eating = edibleInHand();
    if (eating !== null && wouldEatingHelp(eating)) {
      connection.sendEatItem(selectedSlot);
      return;
    }

    /* ⚠️ **Minecraft's "replaceable" rule**, and it arrives with `kjarr`.
       Now that the crosshair stops at scrub, placing a block *against* a bush
       would stack it on top of the bush instead of putting it where you are
       plainly pointing. A non-solid block is overwritten in place instead —
       which is what happens to grass and ferns in Minecraft, and is listed as
       load-bearing in `Development/Minecraft/02_Building_Mechanics.md`. */
    /* 🔨 **A station is ALWAYS opened, whatever is in your hand** — T130 slice 3.
       🎯 **haugur, 2026-08-27, on the first version:** *"when i right click
       smíðabekkur with something in my hand it is placed on top, to get the
       crafting menu my hands need to be empty, right clicking it should alwas
       open up crafting."*

       🔴 **This is checked FIRST, before placing, and the order IS the
       feature.** The first version put placing first, so a bench could only be
       opened empty-handed — and carrying something is the normal state of a
       player who wants to build a bench and then use it.

       ⚠️ **The cost, stated because it is real: you cannot build a block
       against a bench by aiming at the bench.** Aim at the ground beside it
       instead. 🎯 He was offered Minecraft's answer — sneak to place — and
       chose this: **`Shift` here is already descend-while-flying and
       run-on-the-ground**, and a third meaning on one key is worse than a
       small fiddle when building. */
    /* 🔴 **ONE decision, made in `interact.ts` where it is tested.** The order
       of these branches IS the feature, and while it lived inline here — in the
       one file the suite does not touch — **the hearth shipped unreachable**:
       holding a log and clicking a fire placed the log, and an empty hand sent
       nothing at all, because `usableBlock` knew only about berries. */
    const held = heldBlockKey();
    /**
     * 🏘️ **Anywhere on a house means its DOOR** — the cell that owns the menu.
     *
     * 🎯 **haugur, 2026-09-28:** *"i can right click heart with a empty hand and
     * the menu opens, but in skemma búr and smiðja i have to press O."* 🔴 **`O`
     * already went through `menuTargetFor`; right-click did not**, so it asked
     * about whichever wall cell was under the crosshair, and a wall has no menu.
     * *A second way in forgets what the first one does* — now they ask the same
     * cell. ⚠️ **Placing still uses the aimed cell** (`hit`, below), so a block
     * in hand still builds against the wall you are pointing at.
     */
    const menuAt = menuTargetFor(hit.block);
    const aimedKey = blockById(blockAt(menuAt.x, menuAt.y, menuAt.z))?.key;
    const action = interactionFor({
      blockKey: aimedKey,
      stations: STATION_KEYS,
      operated: OPERATED_BLOCKS,
      holding: held,
      harvestable: HARVESTABLE,
      /* 🌾 D131 — the ljár cuts the fields; any other hand cuts nothing. */
      cuts:
        inventory.find((stack) => stack.slot === selectedSlot)?.itemKey === LJAR_ITEM
          ? CUTTABLE_FIELDS
          : NOTHING_CUT,
    });

    if (action === 'station' && aimedKey !== undefined) {
      /* 🔨 The smiðja opens the BENCH's list — one alias, shared with the
         server's craft check, so a screen you can open is a craft that works. */
      pack.openAtStation(stationFor(aimedKey), menuAt);
      return;
    }

    if (action === 'place' && held !== null) {
      const aimed = blockAt(hit.block.x, hit.block.y, hit.block.z);
      const want = isSolid(aimed) ? hit.place : hit.block;
      /* 🧵 D136 — a loom goes where `loomPlotFor` says, the SAME rule the
         server checks: its anchor on a vefstofa's back row, facing the house's
         way. Anywhere else is sent as aimed, and the server's refusal says why. */
      if (held === LOOM_BLOCK) {
        const plot = loomPlotFor(want, blockAt);
        const at = plot.verdict === 'free' ? plot.anchor : want;
        const facing =
          plot.verdict === 'free' ? plot.facing : burFacingToward(controls?.state.yaw ?? 0);
        requestEdit(at.x, at.y, at.z, stateKey(LOOM_BLOCK, { facing, weave: 'bare' }));
        return;
      }
      /**
       * 🏘️ **A snapped house MOVES, and the cell has to move with it** — D110.
       *
       * ⚠️ The first version snapped only the FACING, because that is the field
       * `keyToPlace` returns. **The building would have turned to match its
       * neighbour and then stood in the wrong place**, which is worse than not
       * snapping at all: the shadow would have promised a row and the click
       * would have delivered a gap.
       */
      const into = (HOUSES as readonly string[]).includes(held) ? housePlacement(want).cell : want;
      requestEdit(into.x, into.y, into.z, keyToPlace(held, hit, into));
      return;
    }

    /* 🟫 Using the block. ⚠️ `hit.block`, never `hit.place`: you are pointing
       at the hearth, not at the air in front of it. */
    if (action === 'use') {
      /* 🏘️ The door, when it is a house — see `menuAt` above. */
      connection.sendUseBlock(menuAt.x, menuAt.y, menuAt.z);
      return;
    }

    /* Nothing in hand that places, nothing in front that does anything.
       ⚠️ **Answered here rather than by the server** — there is no block key to
       send at all, and a refusal the client can work out for itself should not
       cost a round trip. "I ate and nothing happened" is a bug report. */
    if (eating !== null) {
      say(
        eating.ceilingPercent < 100
          ? t('game.notice.ateNothingRaw', { ceiling: eating.ceilingPercent })
          : t('game.notice.ateNothing'),
      );
      return;
    }
    say(t('game.notice.emptyHand'));
    return;
  }

  if (e.button === 1) {
    /**
     * Pick, the Minecraft convention — but it now **selects the slot** holding
     * that material rather than conjuring it.
     *
     * 🔴 **It used to hand you the block for nothing**, which was fine while
     * building was free and is an infinite-materials cheat now that it is not.
     * Minecraft's own pick works this way in survival: it finds the stack, and
     * does nothing if you have none.
     */
    const key = blockById(blockAt(hit.block.x, hit.block.y, hit.block.z))?.key;
    if (key !== undefined && key !== 'air') {
      const holding = inventory.find(
        (stack) => itemById(itemIdForKey(stack.itemKey) ?? -1)?.placesBlock === key,
      );
      if (holding === undefined) {
        say(
          t('game.notice.notCarrying', {
            block: blockById(blockAt(hit.block.x, hit.block.y, hit.block.z))?.nameIs ?? key,
          }),
        );
        return;
      }
      connection?.sendSelectSlot(holding.slot);
      /* ⚠️ One key with a hole in it, not a sentence built with `+`. In
         Icelandic the block's name would change shape inside this sentence, so
         the *file* has to be able to choose a different shape — a label, say —
         which it cannot do if the English is welded together here (T124). */
      say(
        t('game.notice.holding', {
          block: blockById(blockAt(hit.block.x, hit.block.y, hit.block.z))?.nameIs ?? key,
        }),
      );
    }
  }
});

/* Right click places a block; without this it opens the browser's menu over
   the world and the click that dismisses it places a second one. */
document.addEventListener('contextmenu', (e) => e.preventDefault());

let controls: Controls | null = null;
let nearbyRoads: NearbyRoad[] = [];
const requested = new Set<string>();

/** Wanted, nearest first, not yet asked for. Rebuilt whenever the ring moves. */
let pending: ChunkCoord[] = [];
let inFlight = 0;
/**
 * ⏱️ **How long the world is taking to arrive** — T186.
 *
 * 🔴 **The number T185 and T214 are judged on.** Measured ask-to-arrival, so it
 * includes the server generating the chunk — which on 2026-09-15 turned out to
 * be 42 ms of the wait, sequentially, while the containers sat near 0% CPU.
 */
const chunkWaits = new ChunkWaits();
let lastChunkAt = performance.now();
let centreChunk: ChunkCoord | null = null;

function refreshWanted(centre: ChunkCoord): void {
  /* 🔴 Centred on the player's own chunk, then clamped to the world's real
     floor and ceiling — asking for layers that cannot exist wastes requests at
     sea level and above Hvannadalshnjúkur alike. */
  const { cyMin, cyMax } = verticalSpan(centre.cy, CY_BELOW, CY_ABOVE, CY_WORLD_MIN, CY_WORLD_MAX);
  pending = chunksWanted(centre, viewRadius, cyMin, cyMax, inWorldArea).filter(
    (coord) => !requested.has(chunkKey(coord)),
  );
}

/**
 * Feed the queue to the server a little at a time.
 *
 * The server generates chunks sequentially per connection, each one a range
 * read against the DEM. Asking for a whole 800-chunk ring at once would put
 * minutes of work in front of the ground under your feet, so requests go out
 * only as earlier ones come back — and always nearest first.
 */
function pumpRequests(): void {
  while (inFlight < MAX_IN_FLIGHT && pending.length > 0) {
    const batch = pending.splice(0, REQUEST_BATCH);
    const askedAt = performance.now();
    for (const coord of batch) {
      requested.add(chunkKey(coord));
      /* ⏱️ T186. Timed from here rather than from the socket, because this is
         the moment the player's world stopped being complete. */
      chunkWaits.request(chunkKey(coord), askedAt);
    }
    inFlight += batch.length;
    connection.requestChunks(batch);
  }
}

/**
 * ⚠️ Let go of what is behind you. This is the whole point of T42.
 *
 * The geometry must be explicitly disposed: dropping a `Mesh` from the scene
 * graph does not free the GPU buffers behind it, and on 4 GB of VRAM that
 * distinction is the difference between walking for hours and walking for
 * minutes.
 */
function unloadFarChunks(centre: ChunkCoord): number {
  let dropped = 0;

  for (const [key, frame] of chunks) {
    if (!shouldUnload(frame.coord, centre, viewRadius)) continue;

    chunks.delete(key);
    /* Also forget that it was requested, or walking back will find a hole. */
    requested.delete(key);

    /* 🔴 ALL SIX, not just the terrain — see forgetChunkObjects. This removed
       the mesh alone until 2026-09-11, so every storehouse, torch and heap in a
       chunk you walked away from stayed in the scene for the rest of the
       session. */
    forgetChunkObjects(key);
    dropped++;
  }

  return dropped;
}

/**
 * Ask for a different render distance — the `+`/`-` keys and the Graphics tab
 * both come through here (T188).
 *
 * 🔴 **It only writes the store.** {@link applyViewRadius} does the work, from
 * the subscription, so the two ways in cannot drift.
 */
function setViewRadius(radius: number): void {
  viewRadiusStore.set(clampViewRadius(radius));
}

/** Rebuild the fog and the streaming ring for a new distance. */
function applyViewRadius(radius: number): void {
  const next = clampViewRadius(radius);
  if (next === viewRadius) return;
  viewRadius = next;

  scene.fog = new Fog(skyColour, CHUNK_SIZE * 3, CHUNK_SIZE * viewRadius * 2.2);
  /* ⚠️ A new `Fog` starts at full visibility, so changing the render distance
     inside a fog bank would clear the fog until the next frame reapplied it.
     Restore it in the same breath rather than relying on that. */
  setFogDistance(lastViewFactor);
  /* ⚠️ No save here since T188 — `settings.ts` does it, for every caller. */

  if (centreChunk) {
    unloadFarChunks(centreChunk);
    refreshWanted(centreChunk);
  }
}

/**
 * ⚠️ Matched on `e.key` as well as `e.code`, and PageUp/PageDown given as the
 * escape hatch.
 *
 * `e.code` names the **physical key position using US layout names**. On
 * haugur's Icelandic keyboard the key printed `=` is not in the position called
 * `Equal`, so binding on `code` alone meant the control silently did nothing —
 * it looked like a broken feature rather than a broken assumption.
 *
 * `e.key` gives the character actually produced, which is what a player is
 * reading off their own keyboard. PageUp and PageDown are the same everywhere
 * and need no layout at all.
 */
document.addEventListener('keydown', (e) => {
  if (e.repeat) return;
  /* ⚠️ Not while typing in the Dev Tools window — "g" is a letter in
     Urðarstígur, and flattening the world mid-search is a memorable way to
     lose your place. See `keys.ts`. */
  if (isTypingTarget(e.target)) return;

  const out =
    e.key === '+' ||
    e.key === '=' ||
    e.code === 'Equal' ||
    e.code === 'NumpadAdd' ||
    e.code === 'PageUp';
  const inward =
    e.key === '-' ||
    e.key === '_' ||
    e.code === 'Minus' ||
    e.code === 'NumpadSubtract' ||
    e.code === 'PageDown';

  if (out) setViewRadius(viewRadius + 1);
  if (inward) setViewRadius(viewRadius - 1);

  /* G for ground, so the view can be flipped without releasing the mouse — the
     switch in the HUD needs a visible cursor, and the interesting moment is
     usually while you are standing on the thing you are trying to identify.
     Matched on `e.key` too, for the same reason as `+` and `-` above. */
  if (e.key === 'g' || e.key === 'G' || e.code === 'KeyG') setFlat(!flatWorld);

  /**
   * 🎒 The number keys choose which of the ten on-hand slots is in hand.
   *
   * `Development/Minecraft/09` §3 marks the quick-select hotbar as **load-bearing
   * as a pattern** and the count as flavour; ours is ten because haugur chose
   * ten. **1–9 then 0**, which is the order they sit on the keyboard and the
   * order the slots are numbered on screen.
   *
   * ⚠️ **`e.code`, not `e.key`.** The digit row is in the same place on every
   * layout, and on an Icelandic keyboard `e.key` for those positions is not
   * always the digit — the same trap `+` and `-` above were fixed for.
   *
   * 🔴 **The server decides.** Nothing here changes the HUD; it asks, and the
   * on-hand row moves when the `inventory` message comes back. One source of
   * truth, so the highlight cannot end up on a slot the server disagrees about.
   */
  const digit = /^Digit([0-9])$/.exec(e.code);
  if (digit) {
    /* "1" is slot 0 and "0" is slot 9 — the row reads 1…9, 0. */
    const typed = Number(digit[1]);
    connection?.sendSelectSlot(typed === 0 ? 9 : typed - 1);
  }

  /**
   * 🎒 **Tab steps along the on-hand row**, Shift+Tab back. 🎯 haugur,
   * 2026-08-18: *"i would like to be able to use TAB to go between slots."*
   *
   * 🔴 **Only while the mouse is captured, and that guard is the whole of the
   * care needed.** Tab is the browser's own "move focus" key: swallowing it
   * unconditionally would trap anybody using the keyboard to reach the Dev Tools
   * window, with no way out and nothing on screen saying why. Playing is exactly
   * when focus navigation is not wanted, and exactly when the pointer is locked.
   *
   * It wraps, because ten slots in a row is a ring and stopping at the end would
   * be a rule to remember for no benefit.
   */
  if (e.code === 'Tab' && controls?.pointerLocked === true) {
    e.preventDefault();
    const step = e.shiftKey ? -1 : 1;
    connection?.sendSelectSlot((selectedSlot + step + 10) % 10);
  }

  /**
   * 🎒 **E opens the pack, E or Esc closes it** (T152).
   *
   * The panel's own title row has said so since the design sheets were drawn,
   * which is why it is these two keys and not a choice made here.
   *
   * ⚠️ **`e.code`, so it is the key in that position on any layout** — the same
   * trap the digits and the `+` and `-` keys were both fixed for. On an
   * Icelandic keyboard `e.key` is not always the letter the cap shows.
   *
   * 🔴 **Escape is a special case worth knowing about.** The browser fires it at
   * `document` *and* releases the pointer lock itself, so pressing it while
   * playing does two things at once. That is why the close path here does not
   * assume it still has the lock — it asks for it back rather than restoring it.
   */
  /* 🔥 The hearth answers to `E`, and takes it FIRST — with it open, `E` means
     close this rather than open the pack behind it.
     ⚠️ **Escape left this branch in T187.** It is handled once, for every
     screen, by the dedicated listener beside `gameMenu` — see there. */
  if (hearthPanel.isOpen && e.code === 'KeyE') {
    setHearthOpen(false);
    return;
  }
  /* 📦 And the chest, for the same reason. */
  if (chestPanel.isOpen && e.code === 'KeyE') {
    setChestOpen(false);
    return;
  }
  /* 🐛 F8 reports a bug — T174. ⚠️ Checked BEFORE the panel keys, so it works
     with the pack open, which is exactly when a pack fault is on screen. */
  if (e.code === 'F8' && !bugBox.isOpen) {
    e.preventDefault();
    wantBugShot = true;
    return;
  }
  /* 🙌 T261. ⚠️ **Below the panel checks above**, so `C` typed with the pack
     open does not reach past the screen you are looking at. */
  /* 🎁 T for the give box — an admin only, protocol 47. ⚠️ `preventDefault`, or
     the T that opened the box is typed into it. */
  if (
    e.code === 'KeyT' &&
    iAmAdmin &&
    !giveBox.isOpen &&
    !bugBox.isOpen &&
    !pack.isOpen &&
    !hearthPanel.isOpen &&
    !chestPanel.isOpen
  ) {
    e.preventDefault();
    giveBox.open();
    return;
  }
  if (e.code === 'KeyC' && !pack.isOpen && !hearthPanel.isOpen && !chestPanel.isOpen) {
    tryCarry();
    return;
  }

  if (e.code === 'KeyE') setPackOpen(!pack.isOpen);
  /**
   * 🅾️ **O opens whatever you are looking at that HAS a menu** — the bench,
   * the hearth, a chest, a storehouse door. 🎯 haugur, 2026-09-03: *"when
   * looking at a item that has a menu, like búr hearth etc etc, pressing o
   * opens the menu."* The same message a right-click sends; `isOperated` is
   * the same list `interact.ts` reads, so a fifth menu block cannot be left
   * out. ⚠️ Only while nothing is open: with a panel up the key is the panel's.
   */
  if (e.code === 'KeyO' && !pack.isOpen && !hearthPanel.isOpen && !chestPanel.isOpen) {
    const look = aim();
    if (look) {
      const at = menuTargetFor(look.block);
      const key = blockById(blockAt(at.x, at.y, at.z))?.key;
      /**
       * 🔨 **A STATION and an OPERATED block open in different ways**, and this
       * used to know only the second.
       *
       * 🎯 **haugur, 2026-09-04:** *"make sure i can focus anywhere on the
       * houses to press o."* ⚠️ **The smiðja is a station**: its door shows the
       * craft list, which the client opens itself. Sending `use_block` for it
       * asked the server for a container it does not have, and **nothing
       * happened at all** — a key that silently does nothing on one of the three
       * houses.
       *
       * 🔴 **The order is the same one `interact.ts` uses for a right-click**,
       * because they are the same question asked by two keys.
       */
      if (key !== undefined && STATION_KEYS.has(key)) pack.openAtStation(stationFor(key), at);
      else if (isOperated(blockAt(at.x, at.y, at.z))) connection.sendUseBlock(at.x, at.y, at.z);
    }
  }
});

/**
 * The HTTP origin of the game server, derived from the WebSocket URL.
 *
 * One definition, because three things now use it — roads, search and the
 * terrain probe the teleport lands on.
 */
function httpBase(): string {
  return serverUrl().replace(/^ws/, 'http').replace(/\/ws$/, '');
}

/**
 * 🔐 **Go and sign in, and come back** — T88, D39.
 *
 * 🎯 **haugur, 2026-09-10:** *"if player has a browser open for long he
 * automatically signs out but can keep playing ad not signed in."*
 *
 * 🔴 **The decision is NOT made here.** `signInAction` in `@lofi/shared` is
 * arithmetic over plain values with a test on every branch, because **a
 * redirect is the one client behaviour that cannot be tried twice** — a loop
 * between two sites is worse than being stranded, since a stranded player can
 * still press F8 and a looping one cannot read the screen. **This function does
 * the two things a test cannot: it navigates, and it remembers that it did.**
 *
 * ⚠️ **The marker is set BEFORE the navigation**, not after. A page that has
 * already begun leaving does not reliably run another line.
 */
function handleSignIn(
  who: { readonly displayName: string } | null,
  signIn: string | undefined,
): void {
  const action = signInAction({
    player: who,
    signIn,
    pageHost: window.location.host,
    /* ⚠️ The host we are actually talking to, which `?server=` can make
       different from the page's. See the test that pins it. */
    serverHost: hostOf(serverUrl()),
    alreadyTried: signInAttempted(),
  });

  if (action === 'stay') {
    /* ✅ Signed in, or nobody needs to be. Forget any earlier attempt, so that
       being signed out again later gets a fresh one rather than being told it
       has already tried. */
    if (who !== null) forgetSignInAttempt();
    return;
  }

  if (action === 'strand') {
    /* 🔴 We went, we came back, and we are still nobody. Say so where the
       player can see it — *"I clicked and nothing happened"* is the least
       diagnosable report there is, and this is the state that produces it. */
    console.warn('[net] signed out, and the hand-over did not work');
    say(t('game.notice.signInFailed'));
    return;
  }

  rememberSignInAttempt();
  console.info('[net] not signed in — going to sign in and coming back');
  /* `assign`, not `replace`: Back should return to the game the player was
     looking at rather than skipping over it to whatever came before. */
  window.location.assign(signIn ?? '');
}

/** The `host` of a URL, or the empty string if it will not parse. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

/** Ask the server which streets are nearby, for the HUD and the labels. */
async function refreshRoads(x: number, z: number): Promise<void> {
  try {
    const base = httpBase();
    /* The endpoint speaks ISN93; x and z here are world coordinates. */
    const northing = Math.round(zToNorthing(z));
    const response = await fetch(`${base}/api/roads?x=${Math.round(x)}&z=${northing}&r=300`);
    const data = (await response.json()) as { roads: NearbyRoad[] };
    /* …and answers in ISN93, so convert back on the way in. */
    nearbyRoads = (data.roads ?? []).map((r) => ({ ...r, z: northingToZ(r.z) }));
  } catch {
    /* No streets is a worse HUD, not a broken game. */
    nearbyRoads = [];
  }
}

/** Search every address, landeign, örnefni and building in Iceland (T73). */
async function searchIceland(query: string, kind: SearchKind | null): Promise<SearchHit[]> {
  /* ⚠️ `kind` goes to the server, not applied to the answer. The row limit is
     applied after the filter there, so "addresses only" returns twenty
     addresses rather than the three that survived a mixed cut. */
  const url =
    `${httpBase()}/api/search?q=${encodeURIComponent(query)}` +
    (kind === null ? '' : `&kind=${encodeURIComponent(kind)}`);
  const response = await fetch(url);
  /* ⚠️ Thrown, not swallowed into an empty list. "Nothing found" and "the
     search index was never built" are different answers and the window says
     so — the second one is a migration nobody ran, not a missing street. */
  if (!response.ok) throw new Error(`search failed: ${response.status}`);
  const data = (await response.json()) as { results?: SearchHit[] };
  return data.results ?? [];
}

/**
 * How high above the ground a teleport arrives, in metres.
 *
 * Small, because the destination height is asked for rather than guessed. The
 * player still lands via `settle()`, so this only has to clear the surface.
 */
const TELEPORT_CLEARANCE = 2;

/**
 * Go somewhere else in Iceland (T73).
 *
 * ⚠️ **No protocol message and no reconnect.** Position is not server state —
 * the server answers chunk requests and has no idea where anyone is — so a
 * teleport is: throw the world away, move the player, re-centre the streaming
 * ring, and let the ordinary machinery fetch what is now nearby. That is also
 * why this needs no `worldgenVersion` bump: nothing about generation changes.
 *
 * ⚠️ **The destination height is asked for, not assumed.** `/api/terrain`
 * reads the national DEM, so it answers anywhere in the country. Landing at a
 * guessed altitude works fine in Hafnarfjörður, which is near sea level, and
 * would bury you inside a mountain the first time you went to the highlands.
 */
async function teleportTo(isn93X: number, northing: number, label: string): Promise<void> {
  const worldZ = northingToZ(northing);
  const destinationExists = insideWorld(isn93X, northing);

  let altitude = REFLOAT_ALTITUDE;
  /**
   * ✅ **The height is now asked for ANYWHERE IN ICELAND (T191, T193).**
   *
   * ⚠️ **It used to be asked only inside `TEST_AREA`**, and for a real reason:
   * outside it `/api/terrain` answered **−8 m everywhere**, confidently, and
   * Reykjavík came back at −8 m. The coastline was fetched **clipped to the
   * test area**, so beyond that edge there was no land polygon at all, every
   * column read as sea, and the number returned was the modelled seabed.
   *
   * 🔴 **T191 removed the clip** — the water layer now reads prepared national
   * geometry per chunk — so the land polygon exists everywhere and the height
   * is a real measurement wherever you land. **That is what makes teleporting
   * across the country land you on the ground rather than 25 m under it**, and
   * it is the half of **T47** that is now answered.
   */
  if (destinationExists) {
    try {
      const response = await fetch(
        `${httpBase()}/api/terrain?x=${Math.round(isn93X)}&z=${Math.round(northing)}`,
      );
      const data = (await response.json()) as { height?: number };
      if (typeof data.height === 'number' && Number.isFinite(data.height)) {
        altitude = data.height + TELEPORT_CLEARANCE;
      }
    } catch {
      /* Fall back to the refloat altitude and let `settle()` find the ground.
         A teleport that lands you high is recoverable; one that fails silently
         because the probe 500'd is not. */
    }
  }

  /* Keep walking if you were walking, keep flying if you were flying — the
     same rule the flat-view switch uses, and for the same reason: being
     landed by a debug control is not what anyone means by pressing it. */
  const wasFlying = controls?.state.flying ?? true;
  const from = controls?.state.position;

  /**
   * ⚠️ **Only throw the world away when you have actually left it.**
   *
   * The first version discarded unconditionally, and haugur caught it: a
   * teleport of **three metres** — pasting his own coordinates while standing
   * on them — emptied the screen and put **596 chunks** back on the queue at
   * 11 fps. Everything discarded was about to be re-requested identically.
   *
   * `renderOrigin` only exists to keep geometry near the float32 origin, so it
   * does not need to track the player closely — a few kilometres of drift
   * costs nothing. Re-anchor it only on a jump big enough to matter, and let
   * `unloadFarChunks` do the ordinary work for everything smaller.
   */
  const jumped =
    from === undefined ||
    Math.hypot(isn93X - from.x, worldZ - from.z) > CHUNK_SIZE * viewRadius * 2;

  if (jumped) {
    discardWorld();
    renderOrigin = new Vector3(isn93X, 0, worldZ);
  }

  controls?.teleport(isn93X, worldZ, altitude);
  settled = wasFlying;

  centreChunk = worldToChunk(isn93X, altitude, worldZ);
  if (!jumped) unloadFarChunks(centreChunk);
  refreshWanted(centreChunk);
  void refreshRoads(isn93X, worldZ);

  /* ⚠️ Reset the streaming loop's memory of which chunk you were in. It
     compares against `lastChunk` to decide whether to re-centre the ring, and
     a stale value there means the ring is never rebuilt at the far end. */
  lastChunk = '';

  console.info(
    `[devtools] teleported to ${label} — ISN93 ${isn93X.toFixed(0)}, ${northing.toFixed(0)}` +
      (destinationExists ? '' : ' (outside Iceland — no terrain there, height not asked for)') +
      (jumped ? ' · world discarded' : ' · kept the loaded chunks'),
  );
}

/**
 * Whether the world being rendered is the flat debug view (T49).
 *
 * ⚠️ Set from the server's welcome, never from `flatRequested()`. The HUD has
 * to report what the world *is*, not what was asked for — a debug view mistaken
 * for real terrain wastes a day, and so does a request that quietly failed to
 * arrive.
 */
let flatWorld = false;

/** True from the moment the switch is pressed until the new world arrives. */
let switchingFlat = false;

/** Bumped on every connection, so a superseded one cannot touch the world. */
let connectionGeneration = 0;

/** Set false whenever the player needs dropping onto ground that has moved. */
let settled = false;

/**
 * 🏠 **Where this player wakes up** — protocol 29, T201.
 *
 * ⚠️ **The server's answer, kept as it arrived.** It is sent on connect and
 * again whenever a hearth is lit, so this is never worked out here — the client
 * is not told where home is so that it can decide, only so that it can offer to
 * take you there. 🔴 `null` until a hearth has been lit, which on development
 * is the whole session.
 */
let home: { x: number; y: number; z: number } | null = null;

/**
 * ⚠️ How high the player is lifted when the ground is replaced.
 *
 * `settle()` scans **64 m down** for something solid, so this cannot simply be
 * "very high": from 200 m it would find nothing and the player would fall
 * through the world. 60 m clears everything in the test area — the ground runs
 * from the seabed to about 30 m — and still leaves the ground inside the scan.
 */
const REFLOAT_ALTITUDE = 60;

/**
 * Switch the flat debug view on or off, in place (T49).
 *
 * ⚠️ **The connection has to be remade.** `flat` is a property of the
 * connection, fixed by `hello`, so there is nothing to send that would change
 * it mid-session. Reconnecting is also the honest option: everything already
 * streamed was generated the old way and every one of those chunks is now
 * wrong, so they are thrown away rather than left to disagree with their
 * neighbours.
 *
 * Your position is kept. That is the entire point of a switch rather than a
 * URL: stand on a lump, flip it, and see whether the lump was a building.
 */
function setFlat(next: boolean): void {
  if (switchingFlat || next === flatWorld) return;
  switchingFlat = true;

  const standing = controls?.state.position;

  connection.close();
  discardWorld();

  /* Keep the address bar honest, so a reload or a shared link comes back to the
     same view — and without adding a history entry per press. */
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${withFlatParam(window.location.search, next)}`,
  );

  connection = openConnection(next, standing ? { x: standing.x, z: standing.z } : null);
}

/** Throw away every chunk and its geometry. The GPU buffers must go too. */
function discardWorld(): void {
  /* 🔴 Over the KEYS of every map, not just the meshes. Throwing the world away
     while leaving its storehouses in the scene is what made them follow the
     player across Iceland — see forgetChunkObjects. */
  const keys = new Set([
    ...meshes.keys(),
    /* 🌊 T158. ⚠️ **A chunk can have water and no ground at all** — open sea,
       where the bed is in the chunk below — so its key is in THIS map and in no
       other. Leaving it out is how the sea would have followed haugur across
       Iceland the way his skemma did. */
    ...waterMeshes.keys(),
    ...flames.keys(),
    ...torchPosts.keys(),
    ...driftMeshes.keys(),
    ...burGroups.keys(),
    ...rumGroups.keys(),
    ...loomGroups.keys(),
    ...pitGroups.keys(),
    ...smokeMeshes.keys(),
  ]);
  for (const key of keys) forgetChunkObjects(key);
  chunks.clear();
  requested.clear();
  pending = [];
  inFlight = 0;
  /* ⏱️ T186. ⚠️ Chunks asked for before this point will never arrive, so
     without this they sit outstanding for ever and the readout climbs until it
     means nothing. The measured waits are kept — only the unanswered go. */
  chunkWaits.reset();
  lastChunkAt = performance.now();
  centreChunk = null;
}

/**
 * Open a connection, optionally keeping the player where they are standing.
 *
 * @param keep where the player is, when this is a view switch rather than a
 *   first connection. The server's spawn is used for a fresh session only.
 */
function openConnection(wantFlat: boolean, keep: { x: number; z: number } | null): Connection {
  /**
   * ⚠️ Which connection this is. Everything below ignores events that arrive
   * from a connection that is no longer the current one.
   *
   * Two of those events are certain, not hypothetical: closing the old socket
   * fires its `close` — which would clear `switchingFlat` while the new world
   * is still arriving — and chunks already on the wire can be delivered after
   * `close()` is called, which would seed the new world with terrain generated
   * the old way. Both look like worldgen bugs and neither is one.
   */
  const generation = ++connectionGeneration;
  const current = (): boolean => generation === connectionGeneration;

  return connect(
    serverUrl(),
    {
      onFloats(floats) {
        if (!current()) return;
        floatsNow = floats;
      },

      onWelcome(spawn, worldgenVersion, flat, who, signIn, you) {
        /* 🐑⚠️ T286 — every arrival may open the starving-flock warning once. */
        flockWarning.armLogin();
        if (!current()) return;
        myConnection = you;
        /* 🎁 Protocol 47 — only an admin is offered the give box. */
        iAmAdmin = who?.admin === true;
        /* 🔨 **A new world knows nothing about the last one's cracks (D104).**
           Damage is keyed by position, and the flat debug view and the real
           terrain share every coordinate — so without this, toggling to flat and
           back would show a rock half broken that nobody had ever hit. ⚠️ This
           is also where the *reload repairs everything* limit actually lives:
           when the damage moves to the server, this line is what goes. */
        breaking.forgetDamage();
        flatWorld = flat;
        player = who;
        if (!who) {
          console.info('[net] not signed in — you can walk and look, but not build');
        }
        handleSignIn(who, signIn);
        switchingFlat = false;
        if (flatRequested() && !flat) {
          console.warn('[net] flat view was requested but the server is sending real terrain');
        }

        const at = keep ?? { x: spawn.x, z: spawn.z };
        renderOrigin = new Vector3(at.x, 0, at.z);

        if (keep && controls) {
          /* ⚠️ Rebuilding `Controls` here would re-register its listeners and
             lose the look direction, so the existing one is lifted instead and
             the ordinary spawn path drops it onto the new ground.

             Never *downwards*: someone flying above the town keeps their view,
             which is the position the comparison is usually made from. */
          const wasFlying = controls.state.flying;
          controls.refloat(Math.max(controls.state.position.y, REFLOAT_ALTITUDE));
          /* A walker is dropped onto whatever ground arrives; a flyer is left
             in the air, because being landed by a debug switch is not what
             anyone means by pressing it. */
          settled = wasFlying;
        } else {
          controls = new Controls(
            camera,
            /* 🔴 How much of the cell is solid, not whether it is — T160. A
               non-solid block answers 0 and you walk through it exactly as
               before; a quarter-height hearth answers 0.25. */
            (x, y, z) => {
              const id = blockAt(x, y, z);
              return isSolid(id) ? blockHeight(id) : 0;
            },
            new Vector3(spawn.x, spawn.y, spawn.z),
          );
          /**
           * 🎡 **The wheel steps along the on-hand row** — 🎯 haugur, 2026-09-02:
           * *"scrolling with mouse should move between inventory slots on on
           * hand menu, ctl scroll should do the Zoom."*
           *
           * 🔴 **The server decides, exactly as it does for the number keys and
           * for Tab.** Nothing here moves the highlight; it asks, and the row
           * moves when the `inventory` message comes back. One source of truth,
           * so the highlight cannot land on a slot the server disagrees about.
           *
           * It wraps, because ten slots in a row is a ring.
           */
          controls.onSlotStep = (step) => {
            connection?.sendSelectSlot((selectedSlot + step + 10) % 10);
          };
          /* 🐑 A sheep stops you — T274. As drawn, so you stop at the one you see. */
          controls.bodies = () => [...flock.bodies(), ...foxes.bodies()];
        }
        settled = false;

        centreChunk = worldToChunk(at.x, REFLOAT_ALTITUDE, at.z);
        refreshWanted(centreChunk);
        void refreshRoads(at.x, at.z);
        document.title = t(flat ? 'game.title.flat' : 'game.title', {
          version: String(worldgenVersion),
        });
      },

      onChunk(frame) {
        if (!current()) return;
        inFlight = Math.max(0, inFlight - 1);
        lastChunkAt = performance.now();
        /* ⏱️ T186. How long this one took, ask to arrival. */
        chunkWaits.arrive(chunkKey(frame.coord), lastChunkAt);

        chunks.set(chunkKey(frame.coord), frame);
        emittersByChunk.delete(chunkKey(frame.coord));
        buildMesh(frame);

        /* Rebuild horizontal neighbours: their boundary faces may now be hidden. */
        for (const [dx, dz] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const neighbour = chunks.get(
            chunkKey({ cx: frame.coord.cx + dx, cy: frame.coord.cy, cz: frame.coord.cz + dz }),
          );
          if (neighbour) buildMesh(neighbour);
        }
      },

      onBlockUpdate(edits) {
        if (!current()) return;
        applyBlockUpdate(edits);
      },

      onClock(clock) {
        /* ⚠️ Deliberately NOT gated on `current()`, unlike everything else
           here. The others are gated because a stale connection could seed the
           new world with the wrong terrain; the clock is one number for the
           whole world, and the newest answer is right whichever socket carried
           it. */
        worldClock.sync(clock.playedMs, performance.now());
      },

      onWeather(weather) {
        /* ⚠️ Ungated for the same reason as the clock — and `null` is left
           alone deliberately. A server that has not drawn a year must leave the
           sky on the measured overcast default (V17), never reset it to one. */
        if (weather) worldWeather.sync(weather, performance.now());
      },

      onVitals(vitals, reasonKey, exposure, biting) {
        /* ⚠️ Ungated, like the clock, the weather and the inventory. A body
           belongs to the PLAYER rather than to a particular world, so the newest
           answer is right whichever socket carried it — and gating it would
           leave the bars frozen after a reconnect until the next correction. */
        clientVitals.sync(vitals, reasonKey, performance.now(), exposure, biting);
      },

      onInventory(stacks, selected) {
        /* Kept so Tab can ask for "the next one" and so the readout can say what
           is in hand. The server still decides; the row only moves when it
           answers. */
        selectedSlot = selected;
        inventory = stacks;
        /* ⚠️ Ungated, like the clock and the weather. An inventory belongs to
           the PLAYER rather than to a particular world, so the newest answer is
           right whichever socket carried it — and gating it would leave the
           on-hand row empty after a reconnect until the next pickup. */
        hud.setInventory(stacks, selected);
        /* 🎒 And the open pack, which shows all thirty-four slots rather than
           the ten on hand. ⚠️ It also **cancels anything being carried** — see
           `pack.ts`: a stack riding the cursor is a picture of what a slot USED
           to hold, and putting it down against an inventory that has moved on
           would be a move the server refuses for a reason nobody can see. */
        pack.setInventory(stacks, selected);
        /* 🔥 And the open hearth, for the same reason — it draws the pack too. */
        hearthPanel.setInventory(stacks, selected);
        /* 📦 And the open chest, which draws the pack too. */
        chestPanel.setInventory(stacks, selected);
        /**
         * 🔨 **A craft is the one thing that certainly emptied a chest** —
         * T172. The inventory message is how this client learns a craft
         * succeeded, and the craft may have taken stone out of a storehouse
         * forty metres away. **Ask again, or the next row is greyed against
         * materials that were spent a second ago.**
         *
         * ⚠️ **Only while the craft page is showing**, so an ordinary pickup
         * while walking about costs nothing.
         */
        if (pack.isShowingCraft) connection?.sendAskNearbyMaterials();
      },

      /**
       * 🔨 **What the chests and storehouses in reach hold** — T172, D111,
       * protocol 25.
       *
       * 🔴 **For the screen only.** The server reads the containers again when
       * the craft arrives, so this can go stale and cause a refusal, and can
       * never cause a craft that spends what is not there.
       */
      /**
       * 🧍 **Where everybody else is** — T173, protocol 26.
       *
       * ⚠️ **Ungated on the world, like the clock and the weather.** A body is
       * a fact about a person rather than about a particular chunk, and gating
       * it would leave people frozen after a reconnect until they moved.
       */
      /* 🐟 T228: the fish arrive on the wire now. ⚠️ **The clock is the
         SERVER's beat, not the game clock** — an animal's legs move at the rate
         eyes see them, and `performance.now()` is what measures how fast one
         actually moved between messages. */
      onCreatures(message) {
        fishSchool.setFrom(message, performance.now() / 1000);
        flock.setFrom(message, performance.now() / 1000);
        foxes.setFrom(message);
        /* 🔱 Kept so a thrust can be aimed at one — T221. ⚠️ **The server's own
           word, unchanged**, rather than anything read back out of the drawn
           school: the school smooths a body's movement between beats, and
           aiming at a smoothed position would mean the client and the server
           disagreed about where the fish was by exactly the amount that makes
           it look like lag. */
        lastCreatures = message.creatures;
      },
      onPlayers(players) {
        otherPlayers.setPlayers(players);
      },

      /* 🙌 T261, protocol 37. 🔴 The server's word on what is in our own arms —
         `players` never includes us, on purpose. */
      onFlockWarning(flocks) {
        if (!current()) return;
        flockWarning.set(flocks);
      },

      /**
       * 🛏️ **Asleep, or awake again** — D133, protocol 50. The server decides
       * both ends: which bed, and where you stand up.
       */
      onAsleep(message) {
        if (!current()) return;
        const now = performance.now();
        if (message.bed !== null) {
          asleep = message.bed;
          const facing = message.facing ?? 'north';
          const { into } = burFrame(facing);
          const mid = rumCentre(message.bed, facing);
          /* Onto the mattress, half a metre up, facing the foot of the bed. */
          controls?.teleport(mid.x + 0.5, mid.z + 0.5, message.bed.y + 0.5);
          settled = false;
          controls?.setLying(true, Math.atan2(-into.x, -into.z));
          clientVitals.setFrozen(true, now);
          sleepVeil.textContent = t('hud.asleep');
          sleepVeil.style.display = 'flex';
          return;
        }
        asleep = null;
        controls?.setLying(false);
        clientVitals.setFrozen(false, now);
        sleepVeil.style.display = 'none';
        if (message.wakeAt) {
          controls?.teleport(message.wakeAt.x, message.wakeAt.z, message.wakeAt.y);
          settled = false;
        }
      },

      onCarrying(kind, lamb) {
        const had = carryingKind;
        carryingKind = kind;
        /* 🐣 Protocol 49 — how big, if it is a lamb. */
        carryingLamb = kind === null ? undefined : lamb;
        /* 🙌 T261: the on-hand row shows what is in your arms over the slot you
           had chosen, because your hands are full. */
        hud.setCarrying(carriedContent());
        if (had === null && kind !== null) say(t('game.notice.pickedUp'));
        if (had !== null && kind === null) say(t('game.notice.setDown'));
      },

      onNearbyMaterials(stacks, containers) {
        if (!current()) return;
        pack.setNearbyMaterials(stacks, containers);
      },

      /**
       * 🔥 What a hearth holds — T128, protocol 16.
       *
       * 🔴 **This is what OPENS the screen.** The right-click only asks; the
       * server checks the block really is a hearth and answers with its
       * contents, and the screen appears when they arrive. So it can never show
       * an empty box while the real one is still being read, and never appears
       * at all for a click the server refused.
       */
      onHearthContents(at, slots, lit, hoursLeft, slotItems, home, oven) {
        if (!current()) return;
        hearthPanel.setContents(at, slots, lit, hoursLeft, slotItems, home, oven);
        if (!hearthPanel.isOpen) {
          /* ⚠️ The pack and the hearth are both full-screen overlays in the
             same place. Two open at once is two things taking the same click. */
          if (pack.isOpen) setPackOpen(false);
          setHearthOpen(true);
        }
      },

      /**
       * 📦 What a chest holds — T135, protocol 20.
       *
       * 🔴 **This is what OPENS the screen**, exactly as `hearth_contents` is.
       * The right-click only asks; the server checks the block really is a chest
       * and answers with its contents.
       */
      onChestContents(at, stacks, capacity, title, fire, spin, loom) {
        if (!current()) return;
        chestPanel.setContents(at, stacks, capacity, title, fire, spin, loom);
        if (!chestPanel.isOpen) {
          /* ⚠️ These panels are all full-screen overlays in the same place. Two
             open at once is two things taking the same click. */
          if (pack.isOpen) setPackOpen(false);
          if (hearthPanel.isOpen) setHearthOpen(false);
          setChestOpen(true);
        }
      },

      /**
       * 💀 **You died** — protocol 21, D21, D82.
       *
       * 🔴 **The server has already decided everything.** Where you woke, what
       * you kept and what your bars read all arrived from it; this moves the
       * camera to match and says what happened.
       *
       * ⚠️ **`teleport` and then `settle`, exactly as the debug jump does** —
       * the ground at the far end has not streamed in yet, so arriving flying
       * and dropping once terrain exists is the only way not to fall through an
       * empty world.
       *
       * ⚠️ **The emptied pack is NOT handled here.** The server sends the
       * ordinary `inventory` message straight after, and the pack redraws from
       * that — one way a pack changes, not two.
       */
      onDied(x, y, z, atHome) {
        if (!current()) return;
        controls?.teleport(x, z, y);
        /* 🔴 The same flag the spawn uses, not a second one: the render loop
           calls `settle()` until it finds ground, and clearing it here is how a
           teleport lands. */
        settled = false;
        say(t(atHome ? 'death.wokeAtHearth' : 'death.wokeAtStart'));
      },

      /**
       * 🏠 **Where this player wakes** — protocol 29, T201.
       *
       * ⚠️ **Kept, not acted on.** Nothing moves because of this message; it
       * is what the World map's *Back to home* button reads. 🔴 `null` is the
       * ordinary answer for anybody who has never lit a hearth, and on
       * development that is everybody until they light one.
       */
      onHome(at) {
        if (!current()) return;
        home = at;
      },

      onEditRejected(reasonKey, values) {
        if (!current()) return;
        /* ✅ The capital letter and the full stop used to be added here, to a
           fragment the server sent. They now live in the sentence itself, in the
           language file — because "capitalise the first letter" is an English
           rule, and gluing punctuation onto somebody else's words at runtime is
           precisely what T124 exists to stop. */
        /* 🥣 Protocol 48: a notice may carry numbers — "142 of 300". */
        say(tAnyKey(reasonKey, values));
      },

      onStatus(text) {
        if (!current()) return;
        console.warn('[net]', text);
        /* ⚠️ Otherwise a failed reconnect leaves the switch disabled forever,
           with no way back and nothing on screen saying why. Releasing it means
           a second press can retry. */
        switchingFlat = false;
      },
    },
    {
      flat: wantFlat,
      /* ⚠️ The token only on the FIRST connection. After that the game session
         stored by `net.ts` carries the identity — a play token is single use,
         and re-sending a spent one would arrive as an anonymous connection. */
      token: connectionGeneration === 1 ? playToken : undefined,
      session: storedGameSession(),
    },
  );
}

let connection = openConnection(flatRequested(), null);

const renderer = new WebGPURenderer({ antialias: true });
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setSize(window.innerWidth, viewHeight());
document.body.appendChild(renderer.domElement);

/** The tone curves the Dev Tools panel offers, by name. */
const TONE_CURVES = {
  none: NoToneMapping,
  neutral: NeutralToneMapping,
  aces: ACESFilmicToneMapping,
  agx: AgXToneMapping,
} as const;

/**
 * Apply the Dev Tools light settings (T84).
 *
 * ⚠️ **Nothing here is baked in, on purpose.** Every one of these is a
 * judgement call that can only be made by looking, this host has no GPU, and
 * the browser preview crashes on the game — so haugur is the only person who
 * can see the result. The panel is the source of truth; `Copy settings` is how
 * a good result gets out of his browser and into the repo.
 *
 * ⚠️ `toneMapping` defaults to `NoToneMapping` in three (verified in
 * `three.webgpu.js` for 0.180.0), which means anything over 1.0 clips channel
 * by channel — and per-channel clipping is what walks a colour to white rather
 * than merely brightening it. `neutral` is the Khronos PBR Neutral curve, whose
 * defining property is that it does **not** shift hue, which is the thing that
 * matters when two genuinely different greens have to stay different.
 */
/**
 * The sun and the sky, from the world clock (T103 piece ②).
 *
 * ⚠️ **Called every frame while the clock is driving**, unlike `applyLight`,
 * which runs when a slider moves. The sun has to actually cross the sky: a game
 * day is 20 real minutes, so it travels 18° of arc every real minute, and
 * anything updated only on a settings change would be a sun that jumped when
 * somebody touched the panel.
 *
 * ⚠️ **`light.sun` and `light.ambient` are still haugur's numbers.** They are
 * multiplied, never replaced — see `TUNED_ELEVATION` in `sky.ts`, which is what
 * makes his 2026-08-07 tuning survive contact with a moving sun.
 */
/**
 * ⚠️ **Named `look` and `sky`, never `scene`.** The module-level `scene` is the
 * three.js `Scene`, and a parameter called `scene` shadows it — which compiles,
 * and then quietly stops the fog ever being updated because `scene.fog` means
 * the wrong thing. The type checker caught it here; it would not have if the
 * shapes had happened to match.
 */
function applySceneLight(light: LightSettings, sky: SkyScene): SkyLook {
  const { sunElevation: elevation, sunDirection: direction } = sky;
  const look = skyLook(elevation, sky.weather, sky.moon, light.nightFloor);

  sun.intensity = light.sun * look.sunFactor;
  /* ☀️ The storehouse's daylight lift follows the sun — 2.2 is the sun's
     ordinary daytime strength, so anything brighter counts as full day. */
  burDaylight.value = Math.min(1, Math.max(0, sun.intensity / 2.2));
  /* 🐟 The fish read the SAME sun as the houses — one value, two places that
     must not drift apart. T220. */
  fishDaylight.value = burDaylight.value;
  sheepDaylight.value = burDaylight.value;
  skyLight.intensity = light.ambient * look.skyFactor;

  /* 🌕 The moon, from its own direction (D60). Scaled off the tuned SUN value so
     the two stay in proportion when haugur moves that slider. */
  moonLight.intensity = light.sun * look.moonFactor;

  /**
   * 🖐️ **The hand takes the light of the place you are standing** (T150 ④).
   *
   * 🔴 **A level, not a direction.** The hand is drawn with a flat material and
   * hangs off the camera, so lighting it properly would shade it by where the
   * sun is — and it would then brighten and darken as you turned on the spot,
   * which reads as flickering rather than as light. Minecraft lights the held
   * item by the light level at the player, and that is the model here.
   *
   * ⚠️ **Self-normalising**, so it cannot fight haugur's sliders: the numerator
   * is what the world is lit by *now* and the denominator the same sum with
   * every factor at 1. Move the tuning and the ratio is unchanged.
   *
   * ⚠️ **The three weights are judged by eye and are meant to be argued with.**
   * The hand is roughly upright and close to the player, so most of what falls
   * on it is sky rather than sun.
   */
  const HAND_SUN = 0.4;
  const HAND_MOON = 0.6;
  const fullDay = light.ambient + HAND_SUN * light.sun;
  const rightNow = skyLight.intensity + HAND_SUN * sun.intensity + HAND_MOON * moonLight.intensity;
  hand.setLight(fullDay > 0 ? rightNow / fullDay : 1);
  moonLight.position.set(sky.moon.direction.x, sky.moon.direction.y, sky.moon.direction.z);

  /* ⚠️ The direction comes out of `sun.ts` already in world axes, north as −z.
     Doing the conversion there rather than here is deliberate: a sun placed in
     the north looks entirely plausible until every shadow points the wrong way. */
  sun.position.set(direction.x, direction.y, direction.z);

  /* Two stops for the sky; the HORIZON one for the fog and for the light the
     sky casts. Design §12.6, and it is not a style choice — terrain must fade
     into the colour the sky is where the terrain meets it, and lighting the
     ground from the zenith would tint every shadow blue. */
  setSky(look.zenith, look.colour);
  skyLight.color.set(look.colour);

  /* 🌊 T61. 🔴 **The AIR values are remembered and never written straight to the
     scene**, because being under water changes both the fog and how bright the
     world is, and something has to hold what it would have been. **Exactly one
     function writes `scene.fog` and the light intensities** — `applySubmersion`
     below — so the two cannot disagree. ⚠️ *A second way in that forgets what
     the first one does* is recorded six times in `06_Traps.md`, and adding the
     sea as a third writer of the fog is how it would be a seventh. */
  airFogColour = look.colour;
  airSun = sun.intensity;
  airSky = skyLight.intensity;
  airMoon = moonLight.intensity;

  /* 🔴 Fog is the one weather effect that is a DISTANCE and not a colour, and it
     is the only one that still applies after dark. */
  setFogDistance(look.viewFactor);

  /* Handed back so the sky objects use the SAME reading the light did, rather
     than recomputing it and risking the two drifting apart. */
  return look;
}

/**
 * How far you can see, given the weather (T103 piece ④).
 *
 * ⚠️ **This moves the fog planes, never the streaming ring.** A fog bank must
 * not unload chunks: the fog lifts in a couple of game hours and the world would
 * then have to arrive all over again, which reads as a stall rather than as
 * weather. Fog therefore *hides* terrain that is still there — which is also why
 * it costs nothing on the GPU.
 */
function setFogDistance(viewFactor: number): void {
  lastViewFactor = viewFactor;
  /* 🌊 T61: the numbers themselves are applied by `applySubmersion`, which is
     the only writer of `scene.fog` — under water the weather's visibility is
     not what decides how far you can see. */
  applySubmersion();
}

/**
 * The weather as one line of the HUD (T103 piece ④).
 *
 * ⚠️ **Built from the last reading the server sent, NOT from the eased value
 * driving the sky.** The two are deliberately different: the sky is mid-way
 * between two observations because a sky that jumps looks broken, but a HUD that
 * reported 5.7 oktas would be reporting a number no instrument ever recorded.
 * **The row shows what Veðurstofa wrote down.**
 *
 * ⚠️ **Every field can be absent** — three of the five stations are unstaffed
 * overnight and about 30% of their rows are filled in rather than observed. A
 * missing field is left out of the line rather than printed as a zero.
 */
function describeWeather(reading: WeatherSnapshot | null): string | null {
  const w = reading?.weather;
  if (!w) return null;

  const parts: string[] = [];
  if (w.temperature !== null) parts.push(`${w.temperature.toFixed(1)} °C`);
  if (w.wind !== null) {
    /* The gust only when it is worth knowing about. In Iceland it usually is. */
    const gusting = w.gust !== null && w.gust > w.wind + 1 ? ` (${w.gust.toFixed(1)})` : '';
    parts.push(`${w.wind.toFixed(1)}${gusting} m/s`);
  }
  parts.push(describeSky(w));
  /* 🔴 Shown even though NOTHING LIES ON THE GROUND YET. The archive carries the
     depth and the world does not draw it — T109's collision fix gates that — and
     a number nobody can see is exactly the sort of thing that gets forgotten. */
  if (w.snowDepth !== null && w.snowDepth > 0) parts.push(`snjór ${w.snowDepth.toFixed(0)} cm`);

  return parts.join(' · ');
}

/**
 * The last weather and moon the frame loop saw.
 *
 * ⚠️ **`applyLight` is called from a slider handler**, which has no frame to read
 * them from — but with the sun switch off the panel still has to show a *sky*,
 * and a sky needs to know whether it is foggy and where the moon is.
 */
let lastSkyWeather: SkyWeather = DEFAULT_SKY_WEATHER;
let lastMoon: MoonPosition | null = null;

/**
 * Hand-driven light: the sliders place the sun, and the sky follows them.
 *
 * 🔴 **With the switch off, "Sun height" became a time-of-day dial** — and that
 * is what makes the night reviewable at all. A game day is 20 real minutes, so
 * the calendar advances three days an hour: reaching a dark winter night from a
 * June morning is **thirty-eight real hours of play.** Dragging the slider to
 * −20° gets there instantly, with the stars, the moon and the real dark.
 *
 * ⚠️ **This does NOT fight haugur's tuning.** `skyLook` is normalised to return
 * exactly 1 at 31°, the elevation he tuned at, so `sun 5.2 · ambient 1.9` at 31°
 * is bit-for-bit what it always was. Everywhere else it now behaves like the
 * clock's sun, which is the point.
 */
function applyLight(light: LightSettings): void {
  const look = skyLook(light.elevation, lastSkyWeather, lastMoon, light.nightFloor);

  sun.intensity = light.sun * look.sunFactor;
  /* ☀️ The storehouse's daylight lift follows the sun — 2.2 is the sun's
     ordinary daytime strength, so anything brighter counts as full day. */
  burDaylight.value = Math.min(1, Math.max(0, sun.intensity / 2.2));
  /* 🐟 The fish read the SAME sun as the houses — one value, two places that
     must not drift apart. T220. */
  fishDaylight.value = burDaylight.value;
  sheepDaylight.value = burDaylight.value;
  skyLight.intensity = light.ambient * look.skyFactor;
  moonLight.intensity = light.sun * look.moonFactor;
  if (lastMoon) {
    moonLight.position.set(lastMoon.direction.x, lastMoon.direction.y, lastMoon.direction.z);
  }
  /* The sky lights the world as well as being drawn behind it. */
  skyLight.color.set(light.sky);

  /* Bearing is clockwise from north, and north is −z (`northingToZ` is
     `-northing`), so a bearing `a` points along `(sin a, −cos a)`. */
  const elevation = (light.elevation * Math.PI) / 180;
  const azimuth = (light.azimuth * Math.PI) / 180;
  const horizontal = Math.cos(elevation);
  sun.position
    .set(horizontal * Math.sin(azimuth), Math.sin(elevation), -horizontal * Math.cos(azimuth))
    .normalize();

  /* One colour, two places — the background and the fog must agree or the
     horizon is a hard line between them.

     ⚠️ **Both stops take it, so the gradient collapses.** This is the hand-tuned
     path (Dev Tools, sun switch off): the panel offers one colour, so a gradient
     here would be inventing a zenith haugur did not pick. §12.6's own words —
     *"set its two stops nearly equal for the default"* — with equal being the
     honest reading of a single swatch. */
  setSky(light.sky, light.sky);

  /* 🌊 T61. 🔴 **THIS WAS A THIRD WRITER OF THE FOG AND IT IS WHY THE SEA DID
     NOTHING ON THE HAND-TUNED PATH.** `applySubmersion` was written to be the
     only one, two writers were routed through it — **and this one was missed,
     because the grep that found the others was reading the function they lived
     in.** ⚠️ *A second way in that forgets what the first one does*, found
     forty minutes after a comment was written promising it had been designed
     out. 👉 **The air value is REMEMBERED here and applied there**, exactly as
     the weather path does it. */
  airFogColour = light.sky;
  airSun = sun.intensity;
  airSky = skyLight.intensity;
  airMoon = moonLight.intensity;

  /* Manual light means manual visibility: no weather, so the full view. */
  setFogDistance(1);

  renderer.toneMapping = TONE_CURVES[light.toneMapping];
  renderer.toneMappingExposure = light.exposure;
}

/**
 * ⚠️ **The most pixels we will ever ask a GPU to draw.**
 *
 * The first version of this multiplied `devicePixelRatio` by the slider with no
 * ceiling. On haugur's laptop — Windows scaling 1.5, slider at 2 — that asked
 * for **nine times** the pixels, and the world went black and stopped
 * responding. A drawing buffer is quadratic in this number, so the difference
 * between "smoother" and "hung" is one notch of a slider.
 *
 * 2.0 is the same effective resolution a Retina MacBook renders at, which is
 * the thing this exists to reproduce. There is nothing above it worth having.
 */
const MAX_PIXEL_RATIO = 2;

/** What is currently on the GPU, so nothing is rebuilt that has not changed. */
let appliedGraphics: GraphicsSettings | null = null;

/**
 * Texture filtering and render scale (T84).
 *
 * ⚠️ **Anisotropy is why `crisp` costs something.** three only enables it when
 * *every* filter mode is linear — its own comment, in the sampler it builds:
 * "anisotropy can only be used when all filter modes are set to linear". So
 * nearest-neighbour magnification, which is what makes a 16x16 tile look like
 * Minecraft rather than a blur, switches off the one thing that stops distant
 * ground crawling as the camera moves. In WebGPU that is one sampler and one
 * choice.
 *
 * ⚠️ **And render scale is what an M4 MacBook was doing for free.** Its Retina
 * screen reports `devicePixelRatio` 2, so it renders at double resolution and
 * scales down — free antialiasing, which hid the problem completely. haugur's
 * RTX 3050 laptop, at a lower ratio, had nothing hiding it and looked far worse
 * on better hardware.
 */
function applyGraphics(graphics: GraphicsSettings): void {
  /* ⚠️ Only rebuild the texture when the FILTER changed. The first version
     rebuilt it on every call, so dragging the render-scale slider destroyed and
     recreated the texture once per step — which is what turned the world
     black. Two settings in one panel are not two reasons to touch the GPU. */
  const filterChanged = appliedGraphics === null || appliedGraphics.crisp !== graphics.crisp;
  let previous: DataArrayTexture | null = null;
  if (filterChanged) {
    previous = blockTextures.texture;
    /* ⚠️ Build a NEW texture rather than mutating and disposing this one. A
       sampler is created when a texture is first uploaded, so changing a filter
       mode in place is either ignored or leaves the renderer holding something
       it has already freed. Swapping the object is the one version with no
       half-state. */
    blockTextures = buildBlockTextures();
    const tex = blockTextures.texture;
    tex.magFilter = graphics.crisp ? NearestFilter : LinearFilter;
    tex.anisotropy = graphics.crisp ? 1 : 16;
    tex.needsUpdate = true;
  }

  /* The four T94 switches are uniforms, so they need no shader rebuild and take
     effect on the next frame. Forcing a mip level does need one, because it is
     a different sampling instruction. */
  textureStrength.value = graphics.textures ? 1 : 0;
  aoStrength.value = graphics.ambientOcclusion ? 1 : 0;
  jitterStrength.value = graphics.jitter ? 1 : 0;
  detailFadeStrength.value = graphics.detailFade ? 1 : 0;
  /* 🌊 T207. A uniform too, so the water stops and starts moving on the next
     frame rather than on a reload. */
  movingWaterStrength.value = graphics.movingWater ? 1 : 0;
  /* 🌊 T158, D115. A uniform for the same reason: haugur finds the number by
     dragging and looking, and a rebuild between each step would make that
     impossible. ⚠️ Clamped HERE as well as in the store — this is the value
     that reaches the shader, and it is the only clamp that cannot be bypassed
     by a hand-written setting. */
  waterOpacity.value = Math.min(
    WATER_OPACITY_MAX,
    Math.max(WATER_OPACITY_MIN, graphics.waterOpacity),
  );

  if (filterChanged || appliedGraphics === null || appliedGraphics.mipLevel !== graphics.mipLevel) {
    applyBlockNodes(graphics.mipLevel);
  }

  /* ⚠️ Only after the material points at the new texture, never before. */
  previous?.dispose();

  const ratio = Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio * graphics.renderScale);
  if (appliedGraphics === null || appliedGraphics.renderScale !== graphics.renderScale) {
    renderer.setPixelRatio(ratio);
    renderer.setSize(window.innerWidth, viewHeight());
  }

  appliedGraphics = graphics;
}

/**
 * 🌞 **The light reaches the renderer from the STORE, however it was changed**
 * (T187).
 *
 * 🔴 **This one subscription replaced a callback Dev Tools owned.** A slider in
 * Dev Tools, a *Look at* button in the game menu and the value restored from
 * `localStorage` now all arrive here by the same path — and the menu could not
 * have changed the light at all without becoming a second owner of it.
 *
 * ⚠️ **It fires immediately on subscribe**, which is how the stored light gets
 * applied on the first frame. **There is no separate startup call any more and
 * there must not be one** — two ways to apply the same value is the shape of
 * bug this whole change exists to avoid.
 */
lightStore.subscribe((light) => applyLight(light));

/**
 * 👁️ **The field of view** (T187).
 *
 * ⚠️ **`updateProjectionMatrix` or nothing happens.** three caches the
 * projection matrix, so setting `camera.fov` alone changes a number nobody
 * reads — which on screen looks exactly like the slider being disconnected.
 */
menuStore.subscribe((menu) => {
  camera.fov = menu.fov;
  camera.updateProjectionMatrix();
});

/**
 * 🎛️ **The menu is the front door — it is open when you arrive** (T187).
 *
 * 🎯 **haugur, 2026-09-11:** *"same menu should be used when you press play on
 * the website."* Pressing Play on the website hands you to the game exactly as
 * it did before (`/play` mints a token and redirects); **this is what greets
 * you at the other end**, and it is why there is a *Play game* button at all —
 * a button that starts the game only makes sense if the menu comes first.
 *
 * ✅ **It also covers the loading.** The world streams in behind the scrim, so
 * the seconds spent reading the menu are seconds the chunks were arriving
 * anyway — which on a slow connection is the difference between a blank world
 * and a menu.
 *
 * ⚠️ **Placed here, at the very end of setup, and not beside the constructor.**
 * Opening it calls `handMouseTo`, which reads `pack`, `hearthPanel` and
 * `chestPanel` — all of them declared further up but none of them constructed
 * until this module has run. Opening it early is a temporal-dead-zone crash on
 * the first frame, which looks like the game failing to start.
 */
gameMenu.setOpen(true);

/**
 * 🖼️ **The graphics settings reach the renderer from the STORE** (T188).
 *
 * Same shape as the light above, and for the same reason: the controls left
 * Dev Tools for the game menu's Graphics tab, so no window owns the value.
 */
graphicsStore.subscribe((graphics) => applyGraphics(graphics));

/**
 * 👀 **The render distance** (T188).
 *
 * 🔴 **The `+`/`-` keys and the Graphics tab's slider now write the same
 * store**, so the HUD and the slider cannot disagree about how far you can see
 * — which they would have, being two ways in.
 *
 * ⚠️ **Guarded on an unchanged value**, because the work is not free: a new
 * `Fog`, a re-scan of every loaded chunk and a fresh want-list. The store
 * notifies on every `set`, including one that changed nothing.
 */
viewRadiusStore.subscribe((radius) => applyViewRadius(radius));

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / viewHeight();
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, viewHeight());
});

await renderer.init();

/* ⚠️ Ask the browser what it actually picked, and put it where haugur can read
   it out. Brief §8: "have the game log the detected GPU on startup so we can
   see which one it picked. Check this before diagnosing any performance
   problem." Not awaited — a slow adapter query must not delay the first frame. */
void rendererInfo(
  (renderer.backend as unknown as { isWebGPUBackend?: boolean }).isWebGPUBackend === true,
).then((info) => devTools.showRenderer(`${info.backend} · ${info.gpu}`));

let last = performance.now();
/**
 * ⏱️ **Frame times, and the worst one** — T186.
 *
 * 🔴 **This replaced a single smoothed line**, `fps = fps * 0.9 + (1/dt) * 0.1`,
 * which could not see a stutter at all: one 200 ms freeze moved it from 60 to
 * 54.5 and it sprang straight back. 🎯 **haugur was feeling exactly that while
 * the HUD said 60** — see `perf.ts` for the whole argument.
 */
const frames = new FrameTimes();
let lastChunk = '';
let roadsTimer = 0;
let playerStateTimer = 0;

void renderer.setAnimationLoop(() => {
  const now = performance.now();
  const elapsedMs = now - last;
  const dt = elapsedMs / 1000;
  last = now;
  frames.record(elapsedMs, now);

  /* 🔥 Which frame of the fire is showing. ⚠️ REAL time, not game time: a
     flame flickers at the speed a flame flickers, and does not run 72x faster
     because the world clock does. The only thing on the game clock is how long
     the log lasts. */
  flameFrame.value = flameFrameAt(now);
  /* 🌊 Which frame of the water is showing. ⚠️ REAL time, like the flame: the
     sea does not run 72x faster because the world clock does. */
  waterFrame.value = rippleFrameAt(now);
  /* 💨 Smoke runs on a plain wall clock in SECONDS. ⚠️ Every puff's motion is
     a function of this and nothing else, so it must never jump backwards —
     `now` is monotonic where a game clock is not. */
  smokeTime.value = now / 1000;
  rainTime.value = now / 1000;

  if (!controls) return;

  /* Spawn flying, and drop to the ground as soon as terrain arrives underneath.
     Spawning at a guessed height would mean falling through an unloaded world. */
  if (!settled) settled = controls.settle();

  controls.update(dt);
  /* 🧍 Everybody else, easing towards where the server last said they were —
     T173. ⚠️ Nothing the server judges depends on where these are drawn. */
  /* 🔴 The RENDER origin, not the world one. Bodies are kept in world metres
     and placed relative to this — without it they are drawn 347 km away, which
     is what "dont see nobody" was. */
  otherPlayers.update(dt, renderOrigin);
  /* 🐑 After the origin is known, like every other body in the world. */
  flock.setOrigin(renderOrigin);
  foxes.setOrigin(renderOrigin);
  const { position, flying, zoom, effectiveZoom, pitch, lookScale, flightBoost } = controls.state;

  /**
   * 🐟 **The fish** — T220, D115.
   *
   * 🔴 **Driven by the WORLD clock, not by this browser's.** Every player is
   * meant to see the same fish in the same place, and the spear (T221) will ask
   * the server to agree about one — **a client-local clock would make each
   * screen's fish its own.**
   *
   * ⚠️ **So fish stand still on DEV**, where the world clock is stopped. That is
   * the clock doing what it says, not the fish being broken.
   */
  const playedMs = worldClock.playedMilliseconds(now);
  if (playedMs !== null) {
    fishSchool.update(playedMs / 1000, position, renderOrigin, dt);
  }

  /* 🐑 After the origin AND the player position are both known — a sheep turns
     its head toward whoever it is watching, so it needs to know where you are. */
  flock.update(dt);
  /* 🍂 T257 — réttir opens and closes on the calendar, and the player is told.
     ⚠️ Only while the game menu is closed: a note under the menu is unread. */
  const today = worldClock.time(now);
  if (today !== null && !gameMenu.isOpen) {
    const step = rettirStep(rettirTold, today.month, today.dayOfMonth);
    rettirTold = step.told;
    if (step.note !== null)
      say(t(step.note === 'open' ? 'game.notice.rettirOpens' : 'game.notice.rettirCloses'));
  }
  /* 🦊 The month decides a white fox's coat — summer brown, winter white. */
  const foxMonth = worldClock.time(now)?.month;
  if (foxMonth !== undefined) foxes.setMonth(foxMonth);
  foxes.update(dt);

  /* 🪢 D128 — the ropes, AFTER the flock has posed its necks. */
  drawRopes();

  /* Stream as you walk: the ring follows you, and what falls behind is let go. */
  const here = worldToChunk(position.x, position.y, position.z);
  const hereKey = `${here.cx},${here.cz}`;
  if (hereKey !== lastChunk) {
    lastChunk = hereKey;
    centreChunk = here;
    unloadFarChunks(here);
    refreshWanted(here);
  }

  /* ⚠️ Watchdog. `inFlight` is decremented when a chunk arrives, so a chunk the
     server fails to generate never returns its slot and streaming stalls
     silently — the worst kind of failure, because the world simply stops
     growing with no error anywhere. If nothing has arrived for three seconds
     while work is queued, assume the slots are lost and take them back. */
  if (pending.length > 0 && now - lastChunkAt > 3000) {
    inFlight = 0;
    lastChunkAt = now;
  }

  pumpRequests();

  /* Roads change slowly; asking every frame would hammer the database. */
  roadsTimer -= dt;
  if (roadsTimer <= 0) {
    roadsTimer = 2;
    void refreshRoads(position.x, position.z);
  }

  /**
   * ⚠️ Tell the server where we are — it cannot check reach otherwise (T52).
   *
   * 5 Hz, not every frame: at 60 fps this would be 60 packets a second per
   * player carrying information that changes by centimetres. `REACH_TOLERANCE`
   * exists to cover the staleness that buys.
   */
  playerStateTimer -= dt;
  if (playerStateTimer <= 0) {
    playerStateTimer = 0.2;
    connection.sendPlayerState({
      x: position.x,
      y: position.y,
      z: position.z,
      yaw: controls.state.yaw,
      pitch,
      flying,
    });
  }

  if (notice !== null && now > noticeUntil) notice = null;

  const standing = blockAt(
    Math.floor(position.x),
    Math.floor(position.y) - 1,
    Math.floor(position.z),
  );
  const looking = aim();

  /**
   * Move the outline onto whatever is under the crosshair (T91).
   *
   * ⚠️ Positioned in **render-origin space**, like every chunk mesh — world
   * coordinates are ISN93 metres around 347,000, and float32 cannot hold those
   * without the geometry jittering as you move. `+ 0.5` because a block's
   * coordinate is its minimum corner and the box is centred on its origin.
   *
   * ⚠️ Shown even when you cannot build — not signed in, or in the flat view.
   * It answers "what am I pointing at", which is worth knowing regardless, and
   * hiding it would make an unrelated restriction look like a rendering bug.
   */
  /**
   * 📏 **HOW TALL THE BLOCK UNDER THE CROSSHAIR ACTUALLY IS** — T251.
   *
   * 🎯 **haugur, 2026-09-22, issue #730:** *"see how the breakige is abouve the
   * kjarr like its a whole block."*
   *
   * 🔴 **It was a whole block, because both the outline and the cracks are unit
   * cubes.** ⚠️ **`kjarr` is `height: 0.5` and `söl` is `0.25`**, so the marks
   * stood half a block and three quarters of a block **in the air above the
   * plant** — and a player reasonably reads that as *the game thinks there is a
   * block up there.*
   *
   * ⚠️ **Nothing was wrong with the breaking itself.** The right block took the
   * damage and broke; only the drawing lied about where it was.
   */
  const lookingHeight = looking
    ? blockHeight(blockAt(looking.block.x, looking.block.y, looking.block.z))
    : 1;

  if (looking) {
    highlight.visible = true;
    /* 🔴 Centred on the block's OWN middle, not the cell's. A half-height block
       sits on the cell floor, so its middle is at `y + h/2`. */
    highlight.position.set(
      looking.block.x + 0.5 - renderOrigin.x,
      looking.block.y + lookingHeight / 2 - renderOrigin.y,
      looking.block.z + 0.5 - renderOrigin.z,
    );
  } else {
    highlight.visible = false;
  }

  /**
   * 👻 **The plot shadow** — while a storehouse is in hand and something is
   * under the crosshair, the footprint it would take, grey if free and red if
   * not. The door cell is the one a placement would use (`hit.place` against a
   * solid block, the block itself against a bush), and the facing is the one
   * `keyToPlace` will send — so the shadow shows exactly what the click does.
   */
  if (looking && (HOUSES as readonly string[]).includes(heldBlockKey() ?? '')) {
    const aimed = blockAt(looking.block.x, looking.block.y, looking.block.z);
    const want = isSolid(aimed) ? looking.place : looking.block;
    /* 🔴 The SAME answer the click will use. A shadow worked out separately
       from the placement is a shadow that lies, and this is the one screen
       element whose whole job is to promise where the building goes. */
    const put = housePlacement(want);
    burShadow.show(put.cell, put.facing, blockAt, isSolid, renderOrigin);
  } else if (looking && heldBlockKey() === PIT_DOOR) {
    /* 🏠 D135 — the pit house's 7 × 6, grey if free and solid, red if not:
       `pitPlotVerdict`, the server's own check. */
    const aimed = blockAt(looking.block.x, looking.block.y, looking.block.z);
    const want = isSolid(aimed) ? looking.place : looking.block;
    burShadow.showPit(
      want,
      burFacingToward(controls?.state.yaw ?? 0),
      blockAt,
      isSolid,
      isFluid,
      renderOrigin,
    );
  } else if (looking && heldBlockKey() === RUM_BLOCK) {
    /* 🛏️ D133 — the bed's two cells, the same way and by the same rule. */
    const aimed = blockAt(looking.block.x, looking.block.y, looking.block.z);
    const want = isSolid(aimed) ? looking.place : looking.block;
    burShadow.showBed(
      want,
      burFacingToward(controls?.state.yaw ?? 0),
      blockAt,
      isSolid,
      renderOrigin,
    );
  } else {
    burShadow.hide();
  }

  /**
   * ⛏️ **Advance the break, and show it by CLOSING THE OUTLINE IN.**
   *
   * 🎨 The design agent's finding, and it is why this and not a crack texture:
   * at fifteen pixels the only thing left of a block is its **outline**, so the
   * effect that survives distance is one that changes the outline. The block
   * reads as being pressed in as it gives way.
   *
   * ⚠️ **Cheap on purpose** — one scale on geometry that is already drawn every
   * frame. A stage-by-stage crack overlay is a texture, an extra material and a
   * second draw call, and none of it is visible at the range most mining
   * happens at.
   */
  const progress = flatWorld
    ? null
    : breaking.update(looking ? breakTargetFor(looking.block) : null, dt, toolInHand());

  /**
   * 🔨 **How damaged the block under the crosshair is, whether or not you are
   * hitting it right now — D104.**
   *
   * 🔴 **This is what makes the persistence VISIBLE.** `progress` is null the
   * moment the button comes up, and without this line a block you had nearly
   * broken would look untouched again — the damage would be kept and nobody
   * could tell. ⚠️ **Until the cracks are drawn it is still the pale box**, and
   * the box is a poor way to show it: it is the same shape whether you are
   * swinging or standing still.
   */
  const shownDamage =
    progress?.fraction ??
    /* 🔴 `flatWorld` FIRST. Breaking is switched off in the flat debug view, so
       `progress` is always null there — and without this guard the line below
       would happily draw damage recorded in the REAL world onto a flat-world
       block that merely shares its coordinates. The block-id check inside
       `damageAt` does not save us: the flat world is made of real blocks. */
    (!flatWorld && looking
      ? breaking.damageAt(
          looking.block.x,
          looking.block.y,
          looking.block.z,
          blockAt(looking.block.x, looking.block.y, looking.block.z),
        )
      : 0);

  /* 1.004 → 0.55: clear at arm's length, and still a visible change in outline
     size at the far end of the six-metre reach. */
  const squeeze = progress ? 1.004 - 0.45 * progress.fraction : 1.004;
  /* 📏 …and as tall as the block is — T251. ⚠️ `setScalar` scaled all three,
     which is right for a cube and wrong for everything else in the palette. */
  highlight.scale.set(squeeze, squeeze * lookingHeight, squeeze);

  /**
   * ⛏️ **The cracks spreading — the part haugur can actually see.**
   *
   * 🔴 **Drawn from `shownDamage`, NOT from `progress`**, and that is what makes
   * D104 visible: `progress` is null the moment the button comes up, so a block
   * you had nearly broken would look untouched again — the damage would be kept
   * and nobody could tell.
   *
   * ⚠️ **`setDrawRange` rather than rebuilding the geometry.** The full pattern
   * is uploaded once; growing the damage only changes how much of it is drawn,
   * which costs nothing per frame. Rebuilding a buffer sixty times a second to
   * add two points would be the expensive way to do the same thing.
   */
  if (shownDamage > 0) {
    cracks.visible = true;
    cracks.position.copy(highlight.position);
    /* 📏 T251 — the cracks are a unit cube too, and haugur photographed them
       standing above a half-height kjarr. ⚠️ **Only Y**: a short block is still
       a full metre across, so squashing x and z would pull the cracks off its
       sides. */
    cracks.scale.set(1, lookingHeight, 1);
    cracks.geometry.setDrawRange(0, crackVertexCount(shownDamage));
  } else {
    cracks.visible = false;
  }

  /* 🖐️ The hand swings while the button is down, whether or not anything is
     actually being broken — you swing at the air too, and a hand that only moved
     when it connected would feel like the game deciding for you. */
  hand.setSwinging(breaking.active);
  /* 🖐️ **What is in view is what you are holding** — a tool, a block, or your
     own hand when it is empty. 🎯 haugur: *"if no tool then hand, if holding a
     pickaxe, then pickaxe, when holding a block then the block."* ⚠️ `setHeld`
     returns immediately when nothing changed, so this costs a comparison per
     frame rather than a material write. */
  {
    /**
     * 🙌 **The POSE follows the PICTURE, not the message** — T261.
     *
     * 🔴 **So the dev-tools hold override poses the hand too**, which is the
     * only way this workstation can ever LOOK at a carried animal: dev has no
     * accounts, and walking a headless client up to a wandering sheep is not
     * something a screenshot tool can do.
     *
     * ⚠️ **It is also the honest coupling.** An armful is posed as an armful
     * because an armful is being DRAWN — asking a second source whether we are
     * carrying would be two answers to one question, and they would disagree
     * the first time one of them changed.
     */
    const shown = pictureInHand();
    hand.setCarrying(
      shown !== null && shown.item !== undefined && isCarriable(shown.item),
      /* 🐣 Protocol 49 — a lamb is drawn at a lamb's size. */
      carryingKind === null ? 1 : (carryingLamb ?? 1),
    );
    hand.setHeld(shown);
  }
  hand.update(dt);

  if (progress?.done) {
    requestEdit(progress.target.x, progress.target.y, progress.target.z, 'air');
    cracks.visible = false;
  }

  /* 🍖 The two bars (T129). ⚠️ Per frame, and the cost is a comparison: the
     cluster rounds to whole percent and redraws only when a whole percent has
     actually moved. `null` before the server's first word leaves the bars at
     full, which is what a new player IS. */
  const vitalsNow = clientVitals.now(now);
  if (vitalsNow) {
    const reasonKey = clientVitals.reason(now);
    /* 🥶 What the cold is costing, in words (T131, D64 §7).

       🔴 **Exposure outranks hunger and is outranked by starving.** All three
       can be true at once and the player needs the one they can ACT on: an
       empty stomach is the emergency, a storm is the thing to walk out of, and
       being merely peckish can wait. */
    const biting = clientVitals.biting;
    const line =
      biting.length > 0 && reasonKey !== 'starving'
        ? t('hud.reason.exposed', {
            factors: biting.map((f) => t(`hud.exposure.${f}`)).join(' · '),
          })
        : reasonKey === null
          ? null
          : /* 🔴 The wire carries a key and the sentence is chosen HERE, because
               the server has no idea what language this player reads (T124). */
            t(`hud.reason.${reasonKey}`);

    /* 🔴 The gauge is DERIVED from the multiplier with the shared snapping
       function, not sent as a fourth number. Two ends computing one value from
       one function cannot drift; two numbers on a wire can. And D84 is why it
       snaps at all — a needle that glides is a temperature field in disguise. */
    const gauge = snapGauge(clientVitals.exposure - 1, worstSurcharge(TUNING.exposure));

    hud.setVitals(vitalsNow.healthPercent, vitalsNow.foodPercent, line, gauge);
  }

  /* The sun crosses the sky at 18° a real minute, so this is a per-frame job
     (T103 piece ②). ⚠️ Skipped entirely when the panel has the sun FIXED, so
     tuning by hand is not fought by the clock a sixtieth of a second later. */
  const clockTime = worldClock.time(now);
  const measured = worldWeather.known ? worldWeather.skyWeather(now) : DEFAULT_SKY_WEATHER;
  /* 🌟 The Dev Tools viewing aid (T116): believe the archive, or pretend it is
     clear so the sky can actually be looked at. ⚠️ Client-side only — it changes
     nothing on the server and nothing for anybody else. */
  /**
   * 🌟 The Dev Tools viewing aids, in the order they have to be asked.
   *
   * 🔴 **`clearSky` wins over `forceRain`**, and that is a decision rather than
   * an accident: pretending the sky is clear *and* forcing a downpour is a
   * contradiction, and the honest answer is the clear sky — that switch exists
   * to look AT the sky, and rain in front of it defeats the point.
   *
   * ⚠️ **Forcing rain brings the cloud with it.** Rain out of a clear blue sky
   * is the one way this could look sillier than not raining at all.
   */
  const skyWeather: SkyWeather = lightStore.get().clearSky
    ? { cloud: 0, fog: 0, rain: 0 }
    : lightStore.get().forceRain
      ? { ...measured, cloud: Math.max(measured.cloud, 0.9), rain: 1 }
      : measured;
  const moon = clockTime ? moonPosition(clockTime) : null;
  lastSkyWeather = skyWeather;

  /**
   * 🌧️ **How hard it is raining on YOU** — the sky's own rain, and nothing at
   * all once there is something over your head.
   *
   * 🔴 **Both halves are answers that already existed.** `skyWeather.rain` is
   * what darkens the sky (`RAIN_DARKEN`), so the rain you see and the rain that
   * dulls the daylight can never disagree; and `underARoof` is `shelter.ts`'s
   * one answer to *is there something above me*, which the cold already asks.
   *
   * ⚠️ **The lee of a wall still gets rained on.** You are out of the wind and
   * still wet, which is exactly what standing behind a wall in Iceland is like.
   *
   * ⚠️ **It follows the Dev Tools' clear-sky switch**, because that is the
   * `skyWeather` above rather than the archive — so pretending the sky is clear
   * stops the rain too, which is what anybody pressing it means.
   */
  {
    const at = controls?.state.position;
    const roofed =
      at !== undefined &&
      underARoof(shelterRung(at, worldWeather.reading?.weather?.direction ?? null));
    rainStrength.value = roofed ? 0 : skyWeather.rain;
  }
  lastMoon = moon;

  /* 🔴 ONE scene decides the light AND what is drawn in the sky. They used to
     decide separately, and the panel's switch moved the light while the sun
     stayed where the clock had put it. */
  const skyScene =
    clockTime && moon
      ? resolveScene(lightStore.get(), clockTime, moon, skyWeather, worldWeather.kp())
      : null;
  const skyLookNow = skyScene ? applySceneLight(lightStore.get(), skyScene) : null;

  const nearest = nearbyRoads[0];

  const reading = worldWeather.reading;

  /* ⏱️ T186. Read ONCE per frame and shared by the HUD and by what F8 sends —
     two calls would be two different windows, and a bug report that disagreed
     with the screen it was filed from is worse than no numbers at all. */
  const frameReport = frames.report();
  const waitReport = chunkWaits.report();

  /* The bar: what is true of the world right now. The card below: what is true
     of the thing in front of you. */
  topBar.render({
    time: clockTime ? `${formatWorldDate(clockTime)} · ${formatWorldClock(clockTime)}` : null,
    weather: describeWeather(reading),
    elevation: position.y,
    flat: flatWorld,
    x: position.x,
    /* A real national-grid coordinate, not the world's z. */
    northing: zToNorthing(position.z),
    player: player?.displayName ?? null,
  });

  hud.render({
    worldTime: clockTime,
    weatherSource: reading ? `${reading.zone} · ${reading.realYear}` : null,
    street: nearest?.name ?? null,
    streetDistance: nearest?.distance ?? null,
    standingOn: blockById(standing)?.nameIs ?? null,
    /* 🏠 T132. The wind direction comes straight off the reading the server
       already sends — which is what makes the lee of a wall the CORRECT side
       of the wall, and makes it change when the weather does. */
    shelter: shelterReading(position, reading?.weather?.direction ?? null),
    /**
     * 🌊 **What the game thinks the sea is doing to you** — T61.
     *
     * 🔴 **This row exists because the sea shipped green and invisible.** A
     * photograph of the seabed could not say whether the effect was off, on
     * with the wrong numbers, or on and drawn over — three faults, three fixes,
     * one identical picture.
     *
     * ⚠️ **The depth is to ONE decimal**, because the interesting cases are all
     * shallow: standing in a metre of water and standing on a wet beach are a
     * few centimetres apart and must not round to the same line.
     */
    water: submersion.submerged
      ? t(submersion.stale ? 'hud.water.waiting' : 'hud.water.under', {
          depth: submersion.depth.toFixed(1),
        })
      : t('hud.water.dry'),
    /**
     * 🌫️ **What the fog actually IS, this frame** — T61.
     *
     * 🔴 **Read off `scene.fog`, never off the values we meant to write.** The
     * row above proved the game knew it was 6.3 m under water; the picture was
     * unchanged anyway. **That leaves two faults that look identical** — the
     * fog is set and something downstream ignores it, or something overwrites
     * it before the frame draws — **and this line separates them.**
     *
     * ⚠️ **`instanceof Fog` rather than a truthiness check**, because a
     * `FogExp2` has no `near` or `far` and would read as `undefined–undefined`,
     * which looks like a bug in this row rather than a different kind of fog.
     */
    fog:
      scene.fog instanceof Fog
        ? t('hud.fog.reading', {
            near: scene.fog.near.toFixed(0),
            far: scene.fog.far.toFixed(0),
            colour: `#${scene.fog.color.getHexString()}`,
          })
        : t('hud.fog.none'),
    chunks: meshes.size,
    viewRadius,
    pending: pending.length + inFlight,
    /* ⏱️ T186. One report per frame, read by both the HUD and F8. */
    fps: frameReport.fps,
    worstFrameMs: frameReport.worstMs,
    hitches: frameReport.hitches,
    /* ⚠️ `null`, not 0, until a chunk has actually landed. Nothing measured is
       not the same as no wait, and a zero here reads as *instant*. */
    chunkWaitMs: waitReport.samples === 0 ? null : waitReport.medianMs,
    flying,
    zoom,
    effectiveZoom,
    pitch,
    lookScale,
    flightBoost,
    locked: controls.pointerLocked,
    packOpen: pack.isOpen,
    flat: flatWorld,
    /* ⚠️ Computed from where you actually are, not from the last teleport.
       You can walk out of the world as easily as jump out of it, and the
       symptom — chunks simply stop arriving — is identical either way. */
    outsideWorld: !insideWorld(position.x, zToNorthing(position.z)),
    /* 🧱 What the SELECTED slot would place — the readout follows the row.
       🎯 haugur: *"the item wich slot i have selsected, to be automatically the
       item im holdi."* An empty slot says so in words rather than showing the
       last thing you happened to hold. */
    holding: heldBlockName(),
    /**
     * 🙌 **A sheep under the crosshair beats the block behind it** — T261.
     *
     * 🔴 **Because the block behind it is what you would otherwise read**, and
     * *"Looking at: gras"* while a sheep fills the screen is the HUD telling you
     * the wrong thing about the thing you are aiming at.
     *
     * ⚠️ **It says which key to press**, because a verb nobody can discover is a
     * verb nobody has. **This is the only place the game teaches it.**
     */
    ...((): { target: string | null; targetTone: AimTone } => {
      /* 🦊 Any animal under the crosshair, out to the block reach — not only
         one you could pick up, which is how a fox went unnamed (2026-10-05).
         ⚠️ A block nearer than its near side wins, so a fox is never read
         through a wall — and a sheep standing in scrub still is (`slaughter.ts`). */
      const seen = controls
        ? lookTargetFrom(
            controls.eyeWorldPosition(),
            controls.lookDirection(),
            lastCreatures.filter((c) => c.doing !== 'Dead'),
            MAX_REACH,
          )
        : undefined;
      if (seen !== undefined && (!looking || seen.near < looking.distance)) {
        const carriable = carryingKind === null && carriableUnderCrosshair()?.id === seen.target.id;
        /* 🥣 T286 — a hungry or starving sheep says so first: that is the thing
           you most need to know about the animal in front of you. */
        /* 🐣 And a lamb says it is a lamb — `aimline.ts`. */
        const line = animalAimLine(
          lastCreatures.find((c) => c.id === seen.target.id),
          carriable,
        );
        if (line !== null) return line;
      }
      const name = looking
        ? blockById(blockAt(looking.block.x, looking.block.y, looking.block.z))?.nameIs
        : undefined;
      return {
        target: name === undefined ? null : t('hud.target.block', { name }),
        targetTone: 'plain',
      };
    })(),
    /* 🙌 T261. ⚠️ The SPECIES in words, not the id — see the row's own note. */
    carrying:
      carryingKind === null
        ? null
        : t(carryingLamb === undefined ? 'hud.carrying.sheep' : 'hud.carrying.lamb'),
    notice,
  });
  hud.updateLabels(nearbyRoads, camera, renderOrigin);
  /* 🧍 T176 — names above heads. ⚠️ **After `otherPlayers.update`**, which ran
     earlier this frame, so the plates use the positions the bodies were
     actually drawn at rather than the ones they were drawn at last frame. */
  /* 🔴 `position` — the PLAYER in world metres — and NOT `camera.position`,
     which is rebased into render space twenty lines below this and is
     therefore still last frame's when read here. That mistake hid every name
     on screen and looked exactly like nobody being nearby. */
  hud.updateNameplates(otherPlayers.nameplates(), camera, renderOrigin, position);
  /* 🐑⚠️ T286 — the arrow to a starving flock, every frame. */
  flockWarning.update(camera, renderOrigin, position);
  devTools.render(flatWorld, switchingFlat);

  /* The camera lives in render-origin space, not ISN93 space. `controls`
     computes its world position including the zoom pull-back; this just
     rebases it. */
  const eye = controls.cameraWorldPosition();
  camera.position.set(eye.x - renderOrigin.x, eye.y - renderOrigin.y, eye.z - renderOrigin.z);

  /**
   * 🌊 **Is the eye under water?** — T61.
   *
   * 🔴 **Measured from `eye`, the WORLD position, and never from
   * `camera.position`** — the line above rebases that into render-origin space,
   * and measuring from the rebased one is the fault that hid every nameplate
   * for a day.
   *
   * ⚠️ **Applied while submerged AND on the frame you surface.** The depth
   * changes as you swim down, so this cannot be a one-shot on entering; and
   * without the `wasSubmerged` half, climbing out would leave the sea's fog on
   * the beach.
   */
  const wasSubmerged = submersion.submerged;
  submersion = submersionAt(eye, blockAt, chunkLoadedAt, submersion);
  if (submersion.submerged || wasSubmerged) applySubmersion();

  /* 🌟 The sky's contents (T116). ⚠️ **After the camera has been placed, not
     before** — everything up there is drawn around the viewer, so using last
     frame's position would make the sun lag a frame behind the world and swim
     as you turn. */
  if (clockTime && skyScene && skyLookNow) {
    skyObjects.update(
      {
        sunDirection: skyScene.sunDirection,
        sunElevation: skyScene.sunElevation,
        moonDirection: skyScene.moon.direction,
        moonElevation: skyScene.moon.elevation,
        moonPhase: skyScene.moon.phase,
        /* 1 once the sun is properly down, 0 in daylight — the same band the
           sky colour fades across, so the stars arrive as the blue leaves. */
        night: 1 - smoothstepValue(-12, 0, skyScene.sunElevation),
        cloud: skyScene.weather.cloud,
        fog: skyScene.weather.fog,
        sunsetGlow: skyLookNow.sunsetGlow,
        glowColour: skyLookNow.glowColour,
        days: clockTime.absoluteDay + clockTime.dayFraction,
        /* 🌌 T114. The scene decides this, not the server directly — so the Dev
           Tools preset can pose a good night without the world having one. */
        kp: skyScene.kp,
        /* 🔴 REAL seconds, not game seconds. A game hour is 50 real seconds, so
           a curtain drifting on game time would strobe. */
        realSeconds: performance.now() / 1000,
      },
      camera.position,
    );
  }

  /**
   * 🐛 **The widest a bug report's picture is sent, in pixels** (T182).
   *
   * 🔴 **This exists because the report was refused TWICE for being too big**, and
   * the second time the fix had been applied to the wrong hop: on 2026-09-07 only
   * the game server's own body limit was raised, and **the proxy in front of it
   * was never found** — so the player got nginx's HTML error page and a JSON
   * parser message.
   *
   * 👉 **So this fixes it at the only place that cannot be misconfigured: the
   * picture itself.** A full-screen PNG of a 3D scene runs to several megabytes,
   * and a `data:` URL inflates it by a further third in base64.
   *
   * ⚠️ **A bug report wants a picture somebody can LOOK at, not a master copy.**
   * 1,600 px wide as JPEG is a few hundred kilobytes and shows everything a
   * screenshot of a game needs to show.
   */
  const BUG_SHOT_MAX_WIDTH = 1600;

  /** JPEG quality for a bug report's picture. Detail enough to read the HUD. */
  const BUG_SHOT_QUALITY = 0.85;

  /**
   * Photograph the canvas small enough to send.
   *
   * ⚠️ **Must run in the same frame callback as the render.** The canvas is not
   * preserved between frames, so reading it at any other moment gives a blank
   * picture — the same reason `wantBugShot` exists at all.
   *
   * 🔴 **The format comes from `BUG_SHOT_TYPE` in `@lofi/shared`, which the
   * SERVER also reads.** ⚠️ It was a literal here for three days while the
   * server took only PNG, and **every screenshot was discarded in silence** —
   * see that constant for the whole story. Neither side restates it now.
   */
  /**
   * 🖥️ **Every HUD layer that is on screen right now, in drawing order** —
   * T252.
   *
   * 🔴 **The HUD is SVG sitting OVER the canvas, not in it**, so a photograph of
   * the canvas alone is a picture of an empty world. ⚠️ **That is exactly what
   * bug #725 was** — haugur reported a fault in a MENU and the attached picture
   * showed grass.
   *
   * ⚠️ **Asked of the document rather than listed**, so a layer added later is
   * photographed without anybody remembering to add it here. 👉 *A second list
   * beside the real one is the fault this repo names oftener than any other.*
   *
   * 🔴 **The bug box's own preview is excluded**, or each report would carry a
   * picture of the previous one.
   */
  function hudLayers(): readonly SVGSVGElement[] {
    return [...document.querySelectorAll('svg')].filter((el) => {
      if (el.closest('.bugbox') !== null) return false;
      if (el.style.display === 'none') return false;
      const box = el.getBoundingClientRect();
      return box.width > 0 && box.height > 0;
    });
  }

  /** One HUD layer as an image, or `null` if it could not be turned into one. */
  async function layerImage(el: SVGSVGElement): Promise<HTMLImageElement | null> {
    const box = el.getBoundingClientRect();
    /* 🔴 A CLONE, sized in pixels. The live element is sized by CSS, and an
       `<img>` has no stylesheet to read — without explicit width and height it
       renders at the SVG default of 300 × 150. */
    const copy = el.cloneNode(true) as SVGSVGElement;
    copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    copy.setAttribute('width', String(Math.round(box.width)));
    copy.setAttribute('height', String(Math.round(box.height)));
    const markup = new XMLSerializer().serializeToString(copy);
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

    return new Promise((resolve) => {
      const img = new Image();
      /* ⚠️ A layer that will not load must not stop the report. A picture of the
         world alone is what we had yesterday and is still worth sending. */
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  /**
   * 📷 **Photograph the screen — the world AND the HUD over it** — T174, T252.
   *
   * 🔴 **THE CANVAS IS READ SYNCHRONOUSLY, BEFORE ANY `await`.** A WebGPU canvas
   * is not preserved between frames, so the copy has to be taken in this frame's
   * callback. ⚠️ **Everything after the first `await` is a later tick** — which
   * is fine for the HUD, because an SVG element is still there next tick, and
   * fatal for the canvas, which is not.
   *
   * ⚠️ **The fonts will not be the game's.** `'EB Garamond'` and
   * `'Alegreya Sans'` are loaded by the page's stylesheet, and **an `<img>` has
   * no stylesheet** — so the text falls back to Palatino and Segoe UI. **The
   * layout, the numbers and every slot are right**; the letters are shaped
   * differently. 👉 **That is a real limit and it is written down rather than
   * discovered**: do not read a bug report's picture as evidence about
   * TYPEFACE.
   *
   * 🔴 **The format comes from `BUG_SHOT_TYPE` in `@lofi/shared`, which the
   * SERVER also reads.** ⚠️ It was a literal here for three days while the
   * server took only PNG, and **every screenshot was discarded in silence**.
   */
  async function photographForReport(source: HTMLCanvasElement): Promise<string> {
    const scale = Math.min(1, BUG_SHOT_MAX_WIDTH / Math.max(1, source.width));
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(source.width * scale));
    out.height = Math.max(1, Math.round(source.height * scale));
    const ctx = out.getContext('2d');
    /* No 2D context is not a reason to send nothing — the world alone is what
       every report carried until today. */
    if (!ctx) return source.toDataURL(BUG_SHOT_TYPE, BUG_SHOT_QUALITY);

    /* 🔴 THIS LINE IS THE FRAME. Nothing may be awaited before it. */
    ctx.drawImage(source, 0, 0, out.width, out.height);

    /**
     * 🖥️ Then the HUD over it.
     *
     * ⚠️ **Scaled by the CANVAS's ratio, not the window's.** On a screen whose
     * device pixel ratio is not 1 the canvas is bigger than the CSS box it fills
     * — and using the window's size here would have drawn the HUD at the wrong
     * scale on exactly the machine haugur plays on.
     */
    const perCssPixel = out.width / Math.max(1, source.getBoundingClientRect().width);
    for (const layer of hudLayers()) {
      const img = await layerImage(layer);
      if (img === null) continue;
      const box = layer.getBoundingClientRect();
      ctx.drawImage(
        img,
        box.left * perCssPixel,
        box.top * perCssPixel,
        box.width * perCssPixel,
        box.height * perCssPixel,
      );
    }
    return out.toDataURL(BUG_SHOT_TYPE, BUG_SHOT_QUALITY);
  }

  void renderer.renderAsync(scene, camera).then(() => {
    if (!wantBugShot) return;
    wantBugShot = false;
    /* 🐛 T174: the one moment the canvas has a picture in it. ⚠️ It can still
       fail — a tainted or zero-sized canvas throws — and a report with no
       picture is still worth sending. */
    /* 🐛 T174: the one moment the canvas has a picture in it. 🔴 The canvas is
       copied SYNCHRONOUSLY inside this call, before its first `await` — the HUD
       layers are drawn on a later tick, which they survive and the canvas would
       not. ⚠️ It can still fail, and a report with no picture is worth sending. */
    void photographForReport(renderer.domElement)
      .catch((err: unknown) => {
        console.error('[bug] the screen could not be photographed', err);
        return null;
      })
      .then((shot) => {
        bugBox.open(shot);
      });
  });
});
